/* eslint-disable no-empty */
import { Children, cloneElement, createContext, isValidElement, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { adminDisplayMembershipAmount } from "../lib/admin-display-amount";
import BrandName from "../components/site/BrandName";
import {
  PRIMARY_CIRCLE_OPTIONS, PRIMARY_CIRCLE_NONE,
  SECONDARY_CIRCLE_OPTIONS, SECONDARY_CIRCLE_NA,
} from "../lib/apply-schema";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, ResponsiveContainer,
} from "recharts";

/**
 * High Five Access — Admin Portal (Phase 1).
 *
 * URL: /admin
 * Auth: password → bearer token in localStorage. Every request sends the
 *       token via Authorization: Bearer <token>.
 *
 * Layout: left sidebar + main content. Fully responsive — sidebar
 * collapses to a top drawer on tablet/mobile; the members table gets
 * horizontal scroll and the row detail becomes a full-screen sheet.
 *
 * Modular: every "page" (Dashboard, Members, Payments, Exceptions,
 * Settings) is its own component below so Phase 2 features (column
 * resize, saved views, custom columns, RBAC) layer in cleanly.
 */

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api/admin";
const TOKEN_KEY = "hfa_admin_token";

/* =====================================================================
 * Palette + typography — HFA orange / white / black
 * ===================================================================== */
const T = {
  bg: "#FDF8F1",           // very light warm ivory — brand-tinted workspace
  bgSubtle: "#FAF6EE",     // slightly deeper warm tone for subtle depth
  card: "#FFFFFF",
  ink: "#0B0B0F",
  muted: "#6B7280",
  line: "rgba(0,0,0,0.08)",
  lineSoft: "rgba(0,0,0,0.05)",
  brand: "#EA580C",
  brandSoft: "#FFF3EA",
  brandTint: "#FFF7EF",
  brandTintHover: "#FFF3E4", // slightly warmer for row hover
  danger: "#B91C1C",
  success: "#166534",
  info: "#1E40AF",
  slate: "#374151",
};
/* Hairline outlines for the Members control rows — delicate, not heavy. */
const HAIR_INK   = "0.5px solid rgba(11,11,15,0.55)";
const HAIR_BRAND = "0.5px solid rgba(234,88,12,0.7)";


/* Injected once so table rows can react to :hover without every <td>
 * needing a JS mouseenter listener. Kept scoped by attribute so it
 * never leaks to public-site tables. */
if (typeof document !== "undefined" && !document.getElementById("hfa-admin-inline-css")) {
  const st = document.createElement("style");
  st.id = "hfa-admin-inline-css";
  st.textContent = `
    tr[data-hfa-row]:hover > td { background-color: #FFF9F0 !important; transition: background-color 160ms ease; }
    tr[data-hfa-row][data-selected="true"]:hover > td { background-color: #FFF3EA !important; }
    input[data-hfa-focus]:focus { outline: none; border-color: #EA580C !important; box-shadow: 0 0 0 3px rgba(234,88,12,0.18) !important; }
    button[data-hfa-btn]:hover:not(:disabled) { transform: translateY(-1px); transition: transform 120ms ease, box-shadow 120ms ease; box-shadow: 0 3px 10px rgba(0,0,0,0.08); }
    button[data-hfa-btn]:active:not(:disabled) { transform: translateY(0); box-shadow: 0 1px 3px rgba(0,0,0,0.08); }
    [data-hfa-outline]:not(:disabled):not([aria-disabled="true"]):not([data-active="true"]):hover { background: #FFF7ED !important; }
  `;
  document.head.appendChild(st);
}
const font =
  "'Manrope', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

/* =====================================================================
 * Shared utilities
 * ===================================================================== */
const getToken = () => { try { return localStorage.getItem(TOKEN_KEY) || ""; } catch (_) { return ""; } };
const setToken = (t) => { try { localStorage.setItem(TOKEN_KEY, t); } catch (_) {} };
const clearToken = () => { try { localStorage.removeItem(TOKEN_KEY); } catch (_) {} };

/* =====================================================================
 * Shared response cache — Stale-While-Revalidate
 * ---------------------------------------------------------------------
 * The Admin Portal's three heavy pages (Dashboard, Members, Payments)
 * all read from the same handful of endpoints (`/members`,
 * `/exceptions/unpaid`, `/payments`). Without a cache, every tab
 * switch triggers 3 parallel round-trips (~1s cold), even though the
 * data is usually identical to what we just fetched a second ago.
 *
 * `cachedGet(api, key)` implements a Stale-While-Revalidate contract:
 *   1. If we have a fresh (< STALE_MS) response for `key`, return it
 *      synchronously via the `onCached(data)` callback so the page
 *      paints in a single frame with no visible loading state.
 *   2. Immediately re-fetch in the background and call `onFresh(data)`
 *      with the authoritative response — components re-render only if
 *      the payload actually changed (React's shallow equality catches
 *      identical arrays via a hash trick).
 *   3. Every mutation calls `invalidateAdminCache()` (wired into
 *      `broadcastDataChanged`), so nothing stale is ever displayed
 *      after an edit — refunded payments, status transitions, and
 *      archives all bust the cache immediately.
 *
 * Deliberately in-memory only: no localStorage/sessionStorage so a
 * private-window audit never leaks admin data at rest.
 * =================================================================== */
const _ADMIN_CACHE = new Map();
const STALE_MS = 5 * 60_000;
const MEM_KEY = "/members?" + JSON.stringify({ limit: 2000 });
const PAY_KEY = "/payments?" + JSON.stringify({ limit: 2000 });
const STORE_EVENT = "hfa-admin-store-updated";
const _INFLIGHT = new Map();
let _sharedApi = null;

function invalidateAdminCache() {
  // Keep the data (pages keep painting it) but mark it stale so the next
  // read revalidates in the background.
  _ADMIN_CACHE.forEach((e) => { e.t = 0; });
}
function _storeSet(key, data) {
  _ADMIN_CACHE.set(key, { data, t: Date.now() });
  try { window.dispatchEvent(new CustomEvent(STORE_EVENT, { detail: { key } })); } catch (_) {}
}
const _storeData = (key) => _ADMIN_CACHE.get(key)?.data;
const _storeFetchedAt = (key) => _ADMIN_CACHE.get(key)?.t || 0;
const _storeFresh = (key) => { const e = _ADMIN_CACHE.get(key); return !!e && (Date.now() - e.t) < STALE_MS; };

/** Shared, de-duplicated GET: fresh cache → no request; otherwise one
 *  in-flight request per key is shared by every caller. */
function fetchShared(api, url, params, { force } = {}) {
  const key = url + "?" + JSON.stringify(params);
  if (!force && _storeFresh(key)) return Promise.resolve(_storeData(key));
  if (_INFLIGHT.has(key)) return _INFLIGHT.get(key);
  const p = api.get(url, { params })
    .then((r) => { _storeSet(key, r.data); return r.data; })
    .finally(() => _INFLIGHT.delete(key));
  _INFLIGHT.set(key, p);
  return p;
}
/** Both Members + Payments feeds (the pair every admin page needs). */
async function loadAdminData(api, opts) {
  const [m, p] = await Promise.all([
    fetchShared(api, "/members", { limit: 2000 }, opts),
    fetchShared(api, "/payments", { limit: 2000 }, opts),
  ]);
  return { m, p };
}
const cachedPair = () => {
  const m = _storeData(MEM_KEY), p = _storeData(PAY_KEY);
  return m && p ? { m, p } : null;
};
/** After a mutation: replace exactly one row in the shared Members feed
 *  with the server's authoritative version (or drop it if it left the feed). */
async function refreshMemberRow(api, uuid) {
  const r = await api.get(`/members/${uuid}/row`);
  const entry = _ADMIN_CACHE.get(MEM_KEY);
  if (!entry) return;
  const rows = entry.data.rows || [];
  const row = r.data.row;
  const appId = r.data.application_id;
  const idx = rows.findIndex((x) => x.id === uuid || (appId && x.application_id === appId));
  let next;
  if (!row) next = idx >= 0 ? rows.filter((_, i) => i !== idx) : rows;
  else if (idx >= 0) next = rows.map((x, i) => (i === idx ? row : x));
  else next = [row, ...rows];
  _ADMIN_CACHE.set(MEM_KEY, { data: { ...entry.data, rows: next }, t: entry.t });
  try { window.dispatchEvent(new CustomEvent(STORE_EVENT, { detail: { key: MEM_KEY } })); } catch (_) {}
}

async function cachedGet(api, url, { params, onCached } = {}) {
  const key = url + (params ? "?" + JSON.stringify(params) : "");
  const entry = _ADMIN_CACHE.get(key);
  if (entry && typeof onCached === "function") {
    try { onCached(entry.data); } catch (_) {}
  }
  const data = await fetchShared(api, url, params || {}, { force: !!entry && !_storeFresh(key) });
  return { data };
}

/** Single Indian-grouping number formatter used across the Dashboard
 *  (cards, chart axis, tooltip, totals): 1,000 · 1,00,000 · 1,00,00,000. */
const fmtIN = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "0";
  return v.toLocaleString("en-IN", { maximumFractionDigits: 2 });
};
const rupees = (paise) => {
  if (!paise) return "—";
  return "₹" + fmtIN(Number(paise) / 100);
};
/** Same as `rupees` but treats 0 as a real amount (₹0) instead of "—".
 *  Used by the Dashboard financial cards where a zero total is a
 *  meaningful, presentable value — not "no data". */
const rupeesOrZero = (paise) => {
  const n = Number(paise);
  if (!Number.isFinite(n)) return "₹0";
  return "₹" + fmtIN(n / 100);
};


/** Global-search matcher shared by Members + Payments.
 *
 *  Requirements met (spec §3.1 – §3.4):
 *    • Case-insensitive substring match on every listed field.
 *    • Phone-like queries are compared BOTH against the raw stored value
 *      AND a digit-only normalised version of the field. Handles common
 *      formatting differences: "+91 98765 43210" / "98765 43210" /
 *      "9876543210" all locate the same record whose DB value is
 *      "9876543210".
 *    • Whitespace-tokenised — every token must match at least one
 *      field (AND across tokens, OR across fields), so
 *      "rahul 9876" narrows to rows matching both parts. Prevents the
 *      over-fuzzy behaviour called out in the spec.
 *    • No-token / empty query = pass-through (returns everything).
 *
 *  Purely a client-side helper — it never modifies row data. */
const _digitsOnly = (v) => String(v ?? "").replace(/\D+/g, "");

/** Mirrors backend `_calc_age` in admin.py — DOB is persisted as
 *  DD/MM/YYYY by /apply, so we parse defensively and compute the
 *  today-anchored integer age. Kept intentionally tiny: the Members
 *  list already exposes `row.age` from the backend, and this helper
 *  is only used by the drawer where the raw application doc lacks
 *  the pre-computed field. Returns null for a missing or malformed
 *  DOB so the caller can render an em-dash — matches how the Members
 *  table treats a missing `age` value. */
function _computeAgeFromDob(dob) {
  if (!dob || typeof dob !== "string") return null;
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(dob.trim());
  if (!m) return null;
  const d = Number(m[1]);
  const mth = Number(m[2]);
  const y = Number(m[3]);
  if (!d || !mth || !y) return null;
  const now = new Date();
  let years = now.getFullYear() - y;
  const monthNow = now.getMonth() + 1; // JS months are 0-indexed
  const dayNow = now.getDate();
  if (monthNow < mth || (monthNow === mth && dayNow < d)) years -= 1;
  return years < 0 ? 0 : years;
}
const _bagEntry = (v) => {
  if (v == null || v === "") return { text: "", digits: "" };
  const s = String(v);
  return { text: s.toLowerCase(), digits: _digitsOnly(s) };
};
function _matchesRowQuery(row, q, fields) {
  return _matchesBag(fields.map((f) => _bagEntry(row[f])), q);
}
function _matchesBag(bag, q) {
  const raw = String(q || "").trim();
  if (!raw) return true;
  const tokens = raw.toLowerCase().split(/\s+/).filter(Boolean);
  if (!tokens.length) return true;
  // Whole-query digit form. Used as a phone-shortcut escape hatch:
  // if the entire query looks phone-like (only digits + phone
  // punctuation, and ≥6 digits total), the row is treated as a hit
  // whenever ANY digit-normalised field contains the concatenated
  // digits — OR the trailing 10 digits (which is enough to isolate an
  // Indian mobile even when the query carries a "+91" country code
  // and the DB stores just the 10-digit form).
  const wholeDigits = _digitsOnly(raw);
  const isPhoneLike = wholeDigits.length >= 6
    && /^[\d\s+\-().]+$/.test(raw);
  if (isPhoneLike) {
    const tail10 = wholeDigits.length > 10 ? wholeDigits.slice(-10) : wholeDigits;
    const phoneHit = bag.some(({ digits }) => {
      if (!digits) return false;
      if (digits.includes(wholeDigits)) return true;
      if (tail10 && digits.includes(tail10)) return true;
      return false;
    });
    if (phoneHit) return true;
  }
  return tokens.every((tok) => {
    const tokDigits = _digitsOnly(tok);
    // A token can be either a text-substring hit or (when it's
    // numeric) a digit-run inside any phone/ID-like field. The
    // digit-only fallback fires only if the token itself contained at
    // least one digit — pure text tokens never match spurious digits.
    return bag.some(({ text, digits }) => {
      if (text && text.includes(tok)) return true;
      if (tokDigits && digits && digits.includes(tokDigits)) return true;
      return false;
    });
  });
}

/** Master Search terms: pieces separated by single spaces, ≥2 chars each.
 *  Extra spaces are intentional: a space right after a term ("CA ") means
 *  "CA followed by a space / end", a space before it (" CA") means
 *  "preceded by a space / start". The separator space itself is neutral. */
function _masterTerms(q) {
  const pieces = String(q || "").toLowerCase().split(" ");
  const out = []; const seen = new Set();
  pieces.forEach((p, i) => {
    if (p.length < 2) return;
    const trail = pieces[i + 1] === "";
    const lead = pieces[i - 1] === "" && (i - 1 === 0 || pieces[i - 2] === "");
    const id = `${lead ? " " : ""}${p}${trail ? " " : ""}`;
    if (seen.has(id)) return;
    seen.add(id);
    out.push({ text: p, lead, trail,
               re: (lead || trail) ? new RegExp(`${lead ? "(?:^| )" : ""}${_escRe(p)}${trail ? "(?: |$)" : ""}`) : null });
  });
  return out;
}
/** Master Search match: ANY valid term as a partial, case-insensitive
 *  substring of any field (phone-like terms also match digit runs). */
function _matchesMaster(bag, terms) {
  if (!terms.length) return true;
  return terms.some(({ text: tok, re }) => {
    if (re) return bag.some(({ text }) => text && re.test(text));
    const tokDigits = _digitsOnly(tok);
    const phoneLike = tokDigits.length >= 6 && /^[\d+\-().]+$/.test(tok);
    const tail10 = phoneLike && tokDigits.length > 10 ? tokDigits.slice(-10) : tokDigits;
    return bag.some(({ text, digits }) => {
      if (text && text.includes(tok)) return true;
      if (phoneLike && digits && (digits.includes(tokDigits) || digits.includes(tail10))) return true;
      return false;
    });
  });
}
/** Column search: whole input is one phrase; leading/trailing spaces are
 *  word boundaries ("CA " ⇒ CA followed by a space or end of value). */
function _boundaryMatcher(raw) {
  const s = String(raw || "").toLowerCase();
  const core = s.trim();
  if (!core) return null;
  const lead = s.startsWith(" "), trail = s.endsWith(" ");
  if (!lead && !trail) return (t) => t.includes(core);
  const re = new RegExp(`${lead ? "(?:^| )" : ""}${_escRe(core)}${trail ? "(?: |$)" : ""}`);
  return (t) => re.test(t);
}

// Fields the Payments global search inspects. The Payments row is
// already enriched with `_app_*` mirrors of the linked application, so
// we search BOTH the payment fields (razorpay/order/method/id/email
// captured at pay time) AND the current applicant fields — which
// respects the Members-is-source-of-truth rule.
const _PAYMENT_SEARCH_FIELDS = [
  "name",
  "_app_full_name", "_app_contact_name", "_app_email", "_app_whatsapp",
  "_app_current_city", "_app_city_chapter",
  "_app_primary_circle", "_app_secondary_circle",
  "_app_membership_id",
  "application_id",
  "razorpay_payment_id", "razorpay_order_id",
  "email", "contact",
];
/** Render any ISO date or "YYYY-MM-DD" string as DD/MM/YYYY.
 *  Falls back to "—" for empty inputs and passes through anything that
 *  doesn't parse cleanly (defensive against unexpected shapes). */
/** Format any date-ish string as DD/MM/YYYY in **Indian Standard Time**
 *  (Asia/Kolkata, UTC+5:30). Handles two input shapes:
 *
 *   • Full ISO datetime (e.g. "2026-08-06T19:37:04.123Z" or
 *     "2026-08-06T19:37:04+00:00"): treated as an instant on the
 *     timeline and *converted* to IST before the calendar date is read.
 *     This is what fixes payment timestamps that were previously
 *     appearing 5h30m before their applied date.
 *
 *   • Bare date "YYYY-MM-DD" (e.g. applied_date, already stored IST-side
 *     by the backend): pass through unchanged — no timezone maths, no
 *     off-by-one errors on rows that legitimately have no time part. */
const fmtDate = (iso) => {
  if (!iso) return "—";
  const s = String(iso);
  // Bare calendar date — trust the backend, it's already IST.
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split("-");
    return `${d}/${m}/${y}`;
  }
  // Full ISO instant — parse and re-emit calendar parts in IST.
  const t = Date.parse(s);
  if (Number.isNaN(t)) {
    // Non-ISO string: last-ditch slice so we never crash the row.
    const head = s.slice(0, 10);
    const m10 = /^(\d{4})-(\d{2})-(\d{2})$/.exec(head);
    return m10 ? `${m10[3]}/${m10[2]}/${m10[1]}` : head || "—";
  }
  const ist = new Date(t + 5.5 * 3600 * 1000);
  const dd = String(ist.getUTCDate()).padStart(2, "0");
  const mm = String(ist.getUTCMonth() + 1).padStart(2, "0");
  const yy = ist.getUTCFullYear();
  return `${dd}/${mm}/${yy}`;
};
/** Format any date-ish string as `hh:mm AM/PM` in **Indian Standard Time**.
 *  A bare "YYYY-MM-DD" input returns an empty string (no time part).
 *  Deliberately drops seconds — the Admin Portal-wide contract is a
 *  single human-friendly 12-hour format everywhere a timestamp shows. */
const fmtTime = (iso) => {
  if (!iso) return "";
  const s = String(iso);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return "";
  const t = Date.parse(s);
  if (Number.isNaN(t)) {
    // Best-effort: parse "HH:MM:..." out of the raw string, then reformat.
    const m = /(\d{2}):(\d{2})/.exec(s);
    if (!m) return "";
    const h24 = parseInt(m[1], 10);
    const mm = m[2];
    const period = h24 >= 12 ? "PM" : "AM";
    const h12 = ((h24 + 11) % 12) + 1;
    return `${String(h12).padStart(2, "0")}:${mm} ${period}`;
  }
  const ist = new Date(t + 5.5 * 3600 * 1000);
  const h24 = ist.getUTCHours();
  const MM  = String(ist.getUTCMinutes()).padStart(2, "0");
  const period = h24 >= 12 ? "PM" : "AM";
  const h12 = ((h24 + 11) % 12) + 1;
  return `${String(h12).padStart(2, "0")}:${MM} ${period}`;
};

/** Combined date+time renderer used across drawers, tables and the
 *  Dashboard. Falls back to date-only when the input carries no time
 *  component, so a bare "YYYY-MM-DD" doesn't render "01/08/2026 •".
 *  Kept as a helper so every consumer stays consistent — the "•"
 *  separator is the portal-wide standard. */
/** Reformat a legacy IST-clock string (`HH:MM` or `HH:MM:SS`, already
 *  in Asia/Kolkata) to the portal-wide 12-hour form `hh:mm AM/PM`.
 *  `applied_date` / `applied_time` on the applications collection are
 *  stored as pre-IST-stringified fields (see `_to_row` on the server),
 *  so we cannot pass them through `Date.parse` — this helper handles
 *  those directly. Returns "" on bad input so callers can compose
 *  strings without extra guards. */
const _fmtClockToAmPm = (clock) => {
  if (!clock) return "";
  const m = /^\s*(\d{1,2}):(\d{2})/.exec(String(clock));
  if (!m) return "";
  const h24 = parseInt(m[1], 10) % 24;
  const mm = m[2];
  const period = h24 >= 12 ? "PM" : "AM";
  const h12 = ((h24 + 11) % 12) + 1;
  return `${String(h12).padStart(2, "0")}:${mm} ${period}`;
};

const fmtDateTime = (iso) => {
  const d = fmtDate(iso);
  const t = fmtTime(iso);
  if (!d && !t) return "";
  if (!t) return d;
  return `${d} • ${t}`;
};

const STATUS_META = {
  pending_review: { label: "Review Pending", bg: "#DBEAFE", fg: "#1E40AF" },
  onboarded:      { label: "Onboarded",      bg: "#DCFCE7", fg: "#166534" },
  rejected:       { label: "Rejected",       bg: "#FEE2E2", fg: "#991B1B" },
  removed:        { label: "Removed",        bg: "#F5F5F4", fg: "#57534E" },
  not_applicable: { label: "Not Applicable", bg: "#F1F5F9", fg: "#64748B" },
  unpaid:         { label: "Unpaid",         bg: "#FEF3C7", fg: "#92400E" },
  refunded:       { label: "Refunded",       bg: "#EDE9FE", fg: "#6B21A8" },
  archived:       { label: "Archived",       bg: "#E5E7EB", fg: "#4B5563" },
};
/* ---------- Master Search highlighting --------------------------------
 * TableView provides the active search tokens; leaf cell renderers wrap
 * every case-insensitive occurrence in a light-yellow <mark>. */
const HighlightCtx = createContext(null);
const _escRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
function _hlTokens(q) {
  const terms = _masterTerms(q);
  if (!terms.length) return null;
  const alts = terms.sort((a, b) => b.text.length - a.text.length)
    .map((t) => `${t.lead ? "(?:^| )" : ""}(${_escRe(t.text)})${t.trail ? "(?= |$)" : ""}`);
  return new RegExp(alts.join("|"), "gi");
}
const _MARK_STYLE = { background: "#FED7AA", color: "#1F2937", borderRadius: 2, padding: "0 1px" };
function _hlText(text, re) {
  if (!re || text == null || text === "") return text;
  const s = String(text);
  re.lastIndex = 0;
  const out = []; let last = 0;
  for (const m of s.matchAll(re)) {
    const g = m.slice(1).find((x) => x !== undefined);
    if (!g) continue;
    const start = m.index + m[0].indexOf(g);
    if (start < last) continue;
    out.push(s.slice(last, start), <mark key={start} data-hfa-hl style={_MARK_STYLE}>{s.slice(start, start + g.length)}</mark>);
    last = start + g.length;
  }
  if (!out.length) return s;
  out.push(s.slice(last));
  return out;
}
function _hlTree(node, re) {
  if (!re || node == null || typeof node === "boolean") return node;
  if (typeof node === "string" || typeof node === "number") return _hlText(node, re);
  if (Array.isArray(node)) return Children.map(node, (n) => _hlTree(n, re));
  if (isValidElement(node) && typeof node.type === "string" && node.props.children != null) {
    return cloneElement(node, undefined, _hlTree(node.props.children, re));
  }
  return node;
}
function useHl(text) {
  const re = useContext(HighlightCtx);
  return re ? _hlText(text, re) : text;
}
function useHlValue(v) {
  const re = useContext(HighlightCtx);
  return re && (typeof v === "string" || typeof v === "number") ? _hlText(v, re) : v;
}

function StatusPill({ status, isArchived }) {
  const key = isArchived ? "archived" : status;
  const m = STATUS_META[key] || { label: status || "—", bg: "#F3F4F6", fg: "#374151" };
  const label = useHl(m.label);
  return (
    <span data-testid={`status-pill-${key || "none"}`}
      style={{ display: "inline-block", padding: "2px 10px", borderRadius: 999,
               background: m.bg, color: m.fg, fontSize: 11, fontWeight: 600 }}>
      {label}
    </span>
  );
}

function CopyIcon({ value }) {
  const [ok, setOk] = useState(false);
  const timerRef = useRef(null);
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);
  if (value === null || value === undefined || value === "") return null;
  const copy = async (e) => {
    e.stopPropagation();
    try { await navigator.clipboard.writeText(String(value)); }
    catch (_) {
      try {
        const ta = document.createElement("textarea");
        ta.value = String(value);
        ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      } catch (_) {}
    }
    setOk(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setOk(false), 900);
  };
  return (
    <button onClick={copy} title={ok ? "Copied!" : "Copy"} data-testid="copy-icon"
      style={{ marginLeft: 6, border: "none", background: "transparent",
               cursor: "pointer", opacity: 0.5, fontSize: 11, fontFamily: "inherit" }}
      onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
      onMouseLeave={(e) => (e.currentTarget.style.opacity = 0.5)}>
      {ok ? "✓" : "⧉"}
    </button>
  );
}

function CopyCell({ value, href, testid, strike }) {
  const shown = useHl(value === undefined || value === null ? "" : String(value));
  if (value === undefined || value === null || value === "") return <span style={{ color: T.muted }}>—</span>;
  const isLink = !!href;
  const strikeStyle = strike ? { textDecoration: "line-through", textDecorationColor: T.danger, color: T.muted } : {};
  const inner = isLink ? (
    <a href={href} target="_blank" rel="noreferrer noopener"
      style={{ color: T.brand, textDecoration: "none", ...strikeStyle }}
      data-testid={testid}
      onClick={(e) => e.stopPropagation()}>
      {shown}
    </a>
  ) : <span data-testid={testid} data-strike={strike ? "true" : undefined} style={strikeStyle}>{shown}</span>;
  return (
    <span className="hfa-copy-cell" style={{ display: "inline-flex", alignItems: "center", maxWidth: "100%" }}>
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{inner}</span>
      <CopyIcon value={value} />
    </span>
  );
}

/* ---------- Smart link normalisers ------------------------------------
 * Users enter Instagram / LinkedIn / websites / emails in a dozen
 * different formats. These helpers coerce anything reasonable into a
 * clickable https:// URL (or mailto:). Empty / clearly-invalid values
 * return "" so CopyCell renders as plain text. */
function _mailtoOf(raw) {
  const s = String(raw || "").trim();
  return /.+@.+\..+/.test(s) ? `mailto:${s}` : "";
}
function _instagramUrlOf(raw) {
  // Strip whitespace, drop a leading @, strip https:// / http:// and any
  // instagram.com / www.instagram.com prefix, drop trailing slash. Then
  // lowercase and validate against Instagram's username rules (letters,
  // digits, underscore and period, 1-30 chars). Missing values return "".
  let s = String(raw || "").trim();
  if (!s) return "";
  s = s.replace(/\s+/g, "");
  s = s.replace(/^https?:\/\//i, "");
  s = s.replace(/^www\./i, "");
  s = s.replace(/^(?:m\.)?instagram\.com\//i, "");
  s = s.replace(/^@+/, "");
  s = s.replace(/\/+$/, "");
  s = s.split(/[/?#]/)[0];
  s = s.toLowerCase();
  if (!s || !/^[a-z0-9._]{1,30}$/.test(s)) return "";
  return `https://instagram.com/${s}`;
}
function _linkedinUrlOf(raw) {
  // Accept a variety of common formats and always emit a safe
  // https://www.linkedin.com/... URL. Anything that doesn't resolve
  // cleanly to LinkedIn's own host returns "" so we never render a
  // deceptive or broken link.
  let s = String(raw || "").trim();
  if (!s) return "";
  s = s.replace(/\s+/g, "");
  // 1) Already-a-URL branch — parse and validate hostname.
  if (/^https?:\/\//i.test(s)) {
    try {
      const u = new URL(s);
      const host = u.hostname.toLowerCase();
      if (host === "linkedin.com" || host === "www.linkedin.com"
          || host.endsWith(".linkedin.com")) {
        // Normalise scheme to https and canonicalise host to www.
        u.protocol = "https:";
        if (host === "linkedin.com") u.hostname = "www.linkedin.com";
        return u.toString();
      }
      return "";
    } catch (_) { return ""; }
  }
  // 2) www.linkedin.com/... or linkedin.com/... (no scheme).
  const noSchemeHost = s.replace(/^www\./i, "");
  if (/^linkedin\.com\//i.test(noSchemeHost)) {
    return `https://www.${noSchemeHost.startsWith("www.") ? noSchemeHost.slice(4) : noSchemeHost}`;
  }
  // 3) Bare path like in/johndoe or company/xyz — accept & prefix.
  if (/^(?:in|company|school|showcase|pub)\/[a-zA-Z0-9\-_.%]+\/?$/i.test(s)) {
    return `https://www.linkedin.com/${s.replace(/^\/+/, "")}`;
  }
  // 4) Bare handle — assume /in/<handle>.
  if (/^[a-zA-Z0-9\-_.]{2,60}$/.test(s)) return `https://www.linkedin.com/in/${s}`;
  return "";
}

/** Wrap variant: shows full multi-line text with copy-on-hover.
 *  Used for long free-text columns (descriptions, notes, remarks). */
function CopyCellWrap({ value, href, testid }) {
  const shown = useHl(value === undefined || value === null ? "" : String(value));
  if (value === undefined || value === null || value === "") return <span style={{ color: T.muted }}>—</span>;
  const isLink = !!href;
  const inner = isLink ? (
    <a href={href} target="_blank" rel="noreferrer noopener"
      style={{ color: T.brand, textDecoration: "none" }}
      data-testid={testid}
      onClick={(e) => e.stopPropagation()}>
      {shown}
    </a>
  ) : <span data-testid={testid}>{shown}</span>;
  return (
    <span className="hfa-copy-cell" style={{ display: "inline-flex", alignItems: "flex-start", gap: 4, maxWidth: "100%" }}>
      <span style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.45 }}>{inner}</span>
      <CopyIcon value={value} />
    </span>
  );
}

/** Excel-style multi-select dropdown filter for a column.
 *  Shows distinct values with counts; selecting values narrows the table
 *  to rows whose column value matches ANY selected value (OR within column,
 *  AND across columns — same as Excel). */
function MultiSelectFilter({ colKey, values, selected, onChange, clearSignal = 0 }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [rect, setRect] = useState(null);   // fixed-positioning coords
  const buttonRef = useRef(null);
  const popRef = useRef(null);

  // External "Clear all filters" signal — reset the local option-search
  // query and close the popover so the next open shows a pristine list.
  useEffect(() => {
    if (clearSignal > 0) {
      setQ("");
      setOpen(false);
    }
  }, [clearSignal]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (buttonRef.current && buttonRef.current.contains(e.target)) return;
      if (popRef.current && popRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Reposition on scroll/resize so the popover stays anchored to the
  // trigger — using fixed positioning breaks the popover free from any
  // ancestor `overflow` clip (this is what makes the dropdown appear
  // ABOVE table rows even when the table itself is a scroll container).
  useEffect(() => {
    if (!open) return;
    const measure = () => {
      const el = buttonRef.current;
      if (!el) return;
      setRect(el.getBoundingClientRect());
    };
    measure();
    const opts = { capture: true, passive: true };
    window.addEventListener("scroll", measure, opts);
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("scroll", measure, opts);
      window.removeEventListener("resize", measure);
    };
  }, [open]);

  const toggle = (v) => {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v); else next.add(v);
    onChange(next);
  };
  const clear = (e) => { e.stopPropagation(); onChange(new Set()); };
  const count = selected.size;
  const visible = q
    ? values.filter((x) => String(x.label).toLowerCase().includes(q.toLowerCase()))
    : values;

  // Popover width: match trigger, but with a comfortable minimum.
  const popWidth = Math.max(rect?.width || 240, 260);
  // Vertical placement: prefer below; flip above if too close to bottom.
  const spaceBelow = rect ? window.innerHeight - rect.bottom : 400;
  const openUp = rect && spaceBelow < 320 && rect.top > 320;
  const popTop = rect ? (openUp ? rect.top - 8 - Math.min(340, rect.top - 12) : rect.bottom + 6) : 0;
  const popHeight = Math.min(340, openUp ? rect?.top - 20 : spaceBelow - 20);
  // Keep popover inside viewport horizontally.
  const rawLeft = rect ? rect.left : 0;
  const clampedLeft = Math.min(
    Math.max(8, rawLeft),
    (typeof window !== "undefined" ? window.innerWidth : 1200) - popWidth - 8,
  );

  return (
    <>
      <button ref={buttonRef} data-testid={`dropdown-${colKey}`}
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        style={{ width: "100%", padding: "4px 8px", fontSize: 11,
                 background: "#FFEDD5",
                 border: `1px solid ${count ? "#F97316" : "#FDBA74"}`,
                 borderRadius: 6, boxShadow: count ? "0 0 0 2px rgba(249,115,22,0.28)" : "none",
                 cursor: "pointer", textAlign: "left", fontFamily: "inherit",
                 color: "#0B0B0F", fontWeight: count ? 600 : 500,
                 whiteSpace: "nowrap",
                 overflow: "hidden", textOverflow: "ellipsis",
                 transition: "border-color 150ms ease, box-shadow 150ms ease" }}>
        {count ? `${count} selected ▾` : "All ▾"}
      </button>
      {open && rect && (
        <div ref={popRef} onClick={(e) => e.stopPropagation()}
          style={{ position: "fixed", top: popTop, left: clampedLeft, width: popWidth,
                   background: "white", border: `1px solid ${T.line}`, borderRadius: 12,
                   boxShadow: "0 20px 48px rgba(0,0,0,0.18)", zIndex: 1000,
                   display: "flex", flexDirection: "column", maxHeight: popHeight,
                   overflow: "hidden" }}>
          <div style={{ padding: "8px 10px", borderBottom: `1px solid ${T.line}`,
                        display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
            <input data-testid={`dropdown-search-${colKey}`}
              placeholder="Search values…" value={q} onChange={(e) => setQ(e.target.value)}
              autoFocus
              style={{ flex: 1, padding: "6px 8px", fontSize: 11.5, border: `1px solid ${T.line}`,
                       borderRadius: 6, fontFamily: "inherit" }} />
            <button onClick={clear} data-testid={`dropdown-clear-${colKey}`}
              style={{ fontSize: 10.5, padding: "6px 10px", background: "white",
                       border: `1px solid ${T.line}`, color: T.brand, cursor: "pointer",
                       fontFamily: "inherit", borderRadius: 6 }}>Clear</button>
          </div>
          {/* Guaranteed ≥5 visible rows: 5 rows × ~30px = ~150px min. */}
          <div style={{ overflowY: "auto", minHeight: Math.min(150, popHeight - 60), flex: 1 }}>
            {visible.map((v) => (
              <label key={v.value}
                style={{ display: "flex", alignItems: "center", gap: 8,
                         padding: "7px 12px", fontSize: 12, cursor: "pointer",
                         transition: "background 0.12s ease" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#F5F5F3")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "white")}>
                <input type="checkbox" checked={selected.has(v.value)}
                  onChange={() => toggle(v.value)}
                  data-testid={`dropdown-opt-${colKey}-${v.value.slice(0, 20)}`} />
                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis",
                               whiteSpace: "nowrap", color: v.value === "" ? T.muted : T.ink }}>
                  {v.label}
                </span>
                <span style={{ color: T.muted, fontSize: 10.5 }}>{v.count}</span>
              </label>
            ))}
            {visible.length === 0 && (
              <div style={{ padding: 14, color: T.muted, fontSize: 12 }}>No values</div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

/* =====================================================================
 * API client (auto-attaches token, auto-clears on 401)
 * ===================================================================== */
function useApi(onAuthFail) {
  return useMemo(() => {
    const instance = axios.create({ baseURL: API });
    instance.interceptors.request.use((cfg) => {
      const t = getToken();
      if (t) cfg.headers = { ...(cfg.headers || {}), Authorization: `Bearer ${t}` };
      return cfg;
    });
    instance.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err?.response?.status === 401) {
          clearToken();
          if (onAuthFail) onAuthFail();
        }
        return Promise.reject(err);
      }
    );
    return instance;
  }, [onAuthFail]);
}

/* =====================================================================
 * Login gate
 * ===================================================================== */
function LoginGate({ onLoggedIn }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!password) { setError("Please enter your password."); return; }
    setBusy(true); setError("");
    try {
      const res = await axios.post(`${API}/auth/login`, { password });
      setToken(res.data.token);
      // Login-time prefetch — kick off the three heavy Admin fetches
      // in the background BEFORE the router mounts <Dashboard>. Each
      // response lands in `_ADMIN_CACHE` so the Dashboard's SWR read
      // paints from cache on the very first render (measured: ~40 ms
      // vs the ~1050 ms cold path). Deliberately fire-and-forget:
      // failures here are silently ignored because Dashboard will
      // still run its own `load()` on mount and surface any error.
      // The Bearer token is already stored in localStorage before we
      // reach this point, so `useApi()`'s interceptor picks it up
      // automatically for these calls too.
      try {
        const token = res.data.token;
        const headers = { Authorization: `Bearer ${token}` };
        const prefetchParams = { limit: 2000 };
        // Goes through the shared store so Dashboard's own load() joins
        // these in-flight requests instead of issuing a second pair.
        const primeApi = { get: (url, cfg) => axios.get(`${API}${url}`, { headers, ...(cfg || {}) }) };
        fetchShared(primeApi, "/members", prefetchParams).catch(() => {});
        fetchShared(primeApi, "/payments", prefetchParams).catch(() => {});
      } catch (_) { /* noop — prefetch is best-effort */ }
      onLoggedIn();
    } catch (err) {
      setError(err?.response?.data?.detail || "Login failed.");
    } finally { setBusy(false); }
  };
  return (
    <div data-testid="admin-login" style={{ minHeight: "100vh", background: T.ink, color: "white",
             display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: font }}>
      <form onSubmit={submit}
        style={{ width: "100%", maxWidth: 420, background: "rgba(255,255,255,0.04)",
                 border: `1px solid rgba(255,255,255,0.10)`, borderRadius: 20, padding: "40px 32px" }}>
        <div style={{ fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase",
                      color: T.brand, fontWeight: 700, marginBottom: 12 }}><BrandName /></div>
        <h1 style={{ fontSize: 26, lineHeight: 1.15, fontWeight: 700, margin: 0, letterSpacing: "-0.02em" }}>Admin Portal</h1>
        <p style={{ marginTop: 12, marginBottom: 24, color: "rgba(255,255,255,0.55)", fontSize: 13 }}>
          Sign in with the administrator password.
        </p>
        <label style={{ display: "block", fontSize: 11, color: "rgba(255,255,255,0.7)", marginBottom: 6 }}>Password</label>
        <input data-testid="admin-login-password" type="password" autoFocus autoComplete="current-password"
          value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
          style={{ width: "100%", padding: "12px 14px", background: "rgba(0,0,0,0.5)",
                   border: "1px solid rgba(255,255,255,0.14)", borderRadius: 12, color: "white",
                   fontSize: 14, fontFamily: "inherit", outline: "none" }} />
        {error && (<div data-testid="admin-login-error" style={{ marginTop: 12, color: "#FCA5A5", fontSize: 13 }}>{error}</div>)}
        <button data-testid="admin-login-submit" type="submit" disabled={busy}
          style={{ marginTop: 20, width: "100%", padding: "13px 16px",
                   background: busy ? "rgba(234,88,12,0.5)" : T.brand, border: "none", borderRadius: 12,
                   color: "white", fontSize: 14, fontWeight: 600, cursor: busy ? "default" : "pointer",
                   fontFamily: "inherit" }}>
          {busy ? "Signing in…" : "Log In"}
        </button>
      </form>
    </div>
  );
}

/* =====================================================================
 * Sidebar + shell
 * ===================================================================== */
const NAV = [
  { key: "dashboard",  label: "Dashboard",  icon: "◉" },
  { key: "members",    label: "Members",    icon: "☰" },
  { key: "payments",   label: "Payments",   icon: "₹" },
  { key: "settings",   label: "Settings",   icon: "⚙" },
];

function Shell({ active, setActive, onLogout, children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  return (
    <div style={{ minHeight: "100vh", background: T.bg, color: T.ink, fontFamily: font, display: "flex" }}>
      {/* Sidebar (desktop) / drawer (mobile) */}
      <aside data-testid="admin-sidebar"
        style={{ width: 244, flexShrink: 0, background: T.ink, color: "white",
                 padding: "28px 16px 22px", position: "sticky", top: 0, height: "100vh",
                 display: "flex", flexDirection: "column",
                 transform: mobileOpen ? "translateX(0)" : "",
                 boxShadow: mobileOpen ? "0 20px 60px rgba(0,0,0,0.4)" : "none" }}
        className="hfa-admin-sidebar">
        <div style={{ padding: "0 10px 22px", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
          <div style={{ fontSize: 10, letterSpacing: "0.24em", textTransform: "uppercase",
                        color: T.brand, fontWeight: 700 }}><BrandName /></div>
          <div style={{ fontSize: 16, fontWeight: 700, marginTop: 8, letterSpacing: "-0.01em" }}>Admin Portal</div>
        </div>
        <nav style={{ marginTop: 22, flex: 1, display: "flex", flexDirection: "column", gap: 3 }}>
          {NAV.map((n) => {
            const isActive = active === n.key;
            return (
              <button key={n.key} data-testid={`admin-nav-${n.key}`}
                onClick={() => { setActive(n.key); setMobileOpen(false); }}
                className="hfa-admin-nav-btn"
                style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px",
                         background: isActive ? "rgba(234,88,12,0.18)" : "transparent",
                         color: isActive ? "white" : "rgba(255,255,255,0.68)",
                         border: "none",
                         borderLeft: `3px solid ${isActive ? T.brand : "transparent"}`,
                         borderRadius: 10, cursor: "pointer", fontFamily: "inherit",
                         fontSize: 13.5, textAlign: "left",
                         fontWeight: isActive ? 600 : 500,
                         letterSpacing: "0.005em",
                         transition: "background 0.15s ease, color 0.15s ease" }}>
                <span style={{ width: 18, display: "inline-block",
                               opacity: isActive ? 1 : 0.6,
                               color: isActive ? T.brand : "rgba(255,255,255,0.55)",
                               fontSize: 14, textAlign: "center" }}>{n.icon}</span>
                {n.label}
              </button>
            );
          })}
        </nav>
        <button data-testid="admin-logout" onClick={onLogout}
          style={{ marginTop: 20, padding: "10px 14px", background: "transparent",
                   color: "rgba(255,255,255,0.55)", border: "1px solid rgba(255,255,255,0.12)",
                   borderRadius: 10, cursor: "pointer", fontFamily: "inherit", fontSize: 12,
                   transition: "border-color 0.15s ease, color 0.15s ease" }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.25)"; e.currentTarget.style.color = "white"; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)"; e.currentTarget.style.color = "rgba(255,255,255,0.55)"; }}>
          Log out
        </button>
      </aside>

      <main className="hfa-admin-main" style={{ flex: 1, minWidth: 0, padding: "28px 32px 60px" }}>
        <button data-testid="admin-mobile-menu-toggle"
          onClick={() => setMobileOpen((v) => !v)}
          className="hfa-admin-mobile-toggle"
          style={{ display: "none", padding: "6px 12px", border: "1px solid " + T.line,
                   borderRadius: 8, background: T.card, fontSize: 12, fontFamily: "inherit",
                   cursor: "pointer", marginBottom: 14 }}>
          ☰ Menu
        </button>
        {children}
      </main>

      <style>{`
        /* Single-date navigation circle buttons. Same class used on
           every Admin page (Dashboard / Members / Payments) so sizing
           + hover stays identical. 36px desktop / 44px mobile —
           comfortable finger target while staying subtle. Hover +
           focus land on the CIRCLE, not just the glyph, so anywhere
           inside the ring is the click target. Kept at the *top* of
           this style block (i.e. outside the responsive @media rules
           below) so Tailwind's preflight button reset doesn't nullify
           the border/radius on desktop. */
        .hfa-date-nav-btn {
          width: 36px; height: 36px;
          display: inline-flex; align-items: center; justify-content: center;
          padding: 0; border-radius: 999px;
          border: 1px solid #E5E5E7;
          background: white;
          color: #23232A;
          cursor: pointer;
          font-family: inherit;
          transition: background 140ms ease, border-color 140ms ease, color 140ms ease;
          -webkit-tap-highlight-color: transparent;
        }
        .hfa-date-nav-btn:hover:not(:disabled) {
          background: #FFF5EE;
          border-color: #EA580C;
          color: #EA580C;
        }
        .hfa-date-nav-btn:focus-visible {
          outline: 2px solid #EA580C;
          outline-offset: 2px;
        }
        .hfa-date-nav-btn:disabled {
          color: #A0A0A6;
          opacity: 0.55;
          cursor: not-allowed;
        }
        @media (max-width: 720px) {
          .hfa-date-nav-btn { width: 44px; height: 44px; }
        }

        @media (max-width: 900px) {
          .hfa-admin-sidebar {
            position: fixed !important;
            z-index: 40;
            transform: translateX(-100%);
            transition: transform 0.22s ease;
          }
          .hfa-admin-sidebar[style*="translateX(0)"] { transform: translateX(0) !important; }
          .hfa-admin-mobile-toggle { display: inline-flex !important; }
        }
        .hfa-admin-nav-btn:hover {
          background: rgba(255,255,255,0.06) !important;
          color: white !important;
        }
        .hfa-metric-card {
          /* Premium subtle branded look for every Dashboard card in
             its resting state — a whisper of warm orange under a soft
             dark-orange border. The 200ms transition covers every
             animatable property so hover feels smooth and cohesive. */
          background: #FFF7ED;
          border-color: #FDBA74 !important;
          box-shadow: 0 1px 2px rgba(234,88,12,0.05);
          transition: transform 200ms ease, box-shadow 200ms ease,
                      background-color 200ms ease, border-color 200ms ease;
          cursor: default;
        }
        .hfa-metric-card:hover {
          /* Lift + gentle emphasis. Slight background brighten, stronger
             dark-orange border, richer shadow — never bouncy. */
          transform: translateY(-5px);
          background: #FFEDD5;
          border-color: #EA580C !important;
          box-shadow: 0 14px 32px rgba(234,88,12,0.16);
        }
        .hfa-metric-card[data-accent="green"] {
          background: #DCFCE7;
          border-color: #22C55E !important;
          box-shadow: 0 1px 2px rgba(34,197,94,0.06);
        }
        /* Cohort parent (Applications Received): a touch deeper cream and a
           firmer orange edge so it reads as the total above its breakdown. */
        .hfa-metric-card[data-role="parent"] {
          background: linear-gradient(160deg, #FFEDD5 0%, #FFE4C4 100%);
          border-color: #FB923C !important;
          box-shadow: 0 2px 6px rgba(234,88,12,0.12), inset 0 1px 0 rgba(255,255,255,0.6);
        }
        .hfa-metric-card[data-role="parent"]:hover {
          background: linear-gradient(160deg, #FFE4C4 0%, #FED7AA 100%);
          border-color: #EA580C !important;
          box-shadow: 0 14px 32px rgba(234,88,12,0.2);
        }
        .hfa-metric-card[data-accent="green"]:hover {
          /* Net Revenue keeps its distinctive green identity — brighten
             the green on hover instead of switching to orange. */
          background: #BBF7D0;
          border-color: #16A34A !important;
          box-shadow: 0 14px 32px rgba(22,163,74,0.18);
        }

        @keyframes hfa-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes hfa-caret-blink { 0%, 49% { opacity: 1; } 50%, 100% { opacity: 0; } }
        .hfa-ms-input::placeholder { color: #A1A1AA; font-weight: 500; opacity: 1; }

        /* =====================================================================
         * Mobile UX pass — desktop unchanged. Every rule below sits behind a
         * max-width media query so nothing bleeds into laptop/desktop use.
         * The goal is a premium, touch-friendly experience: comfortable
         * padding, single-column stacking of filter clusters, full-width
         * actions, larger tap targets, and cleaner vertical rhythm.
         * =================================================================== */
        @media (max-width: 900px) {
          .hfa-admin-main {
            /* Tighter side padding + extra top gap for the menu button
               so the page never feels cramped against the viewport edge. */
            padding: 18px 16px 48px !important;
          }
          .hfa-admin-mobile-toggle {
            padding: 8px 14px !important;
            font-size: 13px !important;
            font-weight: 600 !important;
            border-radius: 10px !important;
          }
        }
        @media (max-width: 720px) {
          /* Page titles — proportionate down-scale, never shrink weight. */
          .hfa-list-header h1 { font-size: 26px !important; letter-spacing: -0.028em !important; }
          .hfa-list-header-row1 { gap: 14px !important; flex-direction: column !important;
                                  align-items: stretch !important; }
          .hfa-list-header-row1 > div:last-child { width: 100% !important; }
          /* Row 2 holds the filter cluster alone — cards stack. */
          .hfa-list-header-row2 { gap: 12px !important; }
          /* Action strip → one compact, horizontally scrollable chip row
             that mirrors the Quick Date strip directly above it. */
          .hfa-list-header-actions {
            justify-content: flex-start !important;
            width: 100% !important;
            flex-wrap: nowrap !important;
            overflow-x: auto !important;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
            gap: 6px !important;
            padding-bottom: 4px;
          }
          .hfa-list-header-actions::-webkit-scrollbar { display: none; }
          .hfa-list-header-actions > * { flex: 0 0 auto; }
          .hfa-list-header-actions [data-hfa-btn] {
            padding: 7px 12px !important;
            font-size: 12px !important;
            line-height: 1.2 !important;
            min-height: 0 !important;
            height: 32px !important;
            border-radius: 999px !important;
            box-shadow: none !important;
            white-space: nowrap;
          }
          .hfa-list-header-actions [data-hfa-refresh] {
            width: 32px !important;
            padding: 0 !important;
          }
          .hfa-list-header-actions [data-hfa-export-split] { height: 32px; border-radius: 999px; }
          .hfa-list-header-actions [data-hfa-export-split] > button:first-child { padding: 0 10px 0 12px !important; font-size: 12px !important; height: 30px; }
          .hfa-list-header-actions [data-hfa-export-split] > button:last-child { width: 24px !important; height: 24px !important; margin-right: 4px !important; }
          /* Dashboard metric cards — single column on phones, still
             using the same premium card styling so the identity is
             preserved end-to-end. */
          .hfa-metric-grid { grid-template-columns: 1fr !important; }
          .hfa-metric-card { padding: 18px 20px !important; }
          .hfa-metric-card > div:last-child { font-size: 28px !important; }
          /* Cohort breakdown (Dashboard → Members): parent card full width,
             connectors hidden, children in a 2 × 2 "ludo" grid. */
          .hfa-cohort-parent { max-width: none !important; width: auto !important; }
          .hfa-cohort-connectors { display: none !important; }
          .hfa-cohort-children { grid-template-columns: 1fr 1fr !important; grid-auto-rows: 1fr; gap: 10px !important; margin-top: 12px !important; }
          .hfa-cohort-children .hfa-metric-card { padding: 14px 14px !important; min-width: 0; }
          .hfa-cohort-children .hfa-metric-card > div:first-of-type { padding-right: 40px; }
          .hfa-cohort-children .hfa-metric-card > div:last-child { font-size: 24px !important; }
          /* Quick Date chip strip → horizontal scroll so admins can
             pick any preset without wrapping into two rows. */
          [data-hfa-chips] {
            flex-wrap: nowrap !important;
            overflow-x: auto !important;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
            padding-bottom: 4px;
            /* Anchor to the left edge so the first preset (Today) is
               visible on mobile. Without this the desktop's
               justify-content: flex-end would push chips out to the
               right and the initial scroll position would clip Today /
               Yesterday off-screen. The width/min-width constraint
               also prevents the intrinsic width of the picker (~550px)
               from stretching the whole document past 100vw. */
            justify-content: flex-start !important;
            width: 100% !important;
            min-width: 0 !important;
            max-width: 100% !important;
          }
          [data-hfa-chips]::-webkit-scrollbar { display: none; }
          [data-hfa-chips] > * { flex: 0 0 auto; }

          /* Custom Range calendar popover — the parent chip strip is
             overflow-x: auto on mobile, which would otherwise CLIP the
             absolute-positioned popover to the (scroll-bounded) chip
             row. On mobile the popover is *also* portaled to
             document.body (see DateRangePicker) to escape every
             ancestor stacking context that Dashboard cards / hover
             transforms / sticky containers might create. The CSS below
             centres it and forces it above every Admin Portal element.
             Desktop rules are untouched — media-scoped only. */
          [data-testid="date-range-popover"] {
            position: fixed !important;
            top: 50% !important;
            left: 50% !important;
            right: auto !important;
            transform: translate(-50%, -50%) !important;
            width: min(340px, calc(100vw - 24px)) !important;
            max-width: none !important;
            min-width: 0 !important;
            z-index: 2147483000 !important;
            box-shadow: 0 24px 60px rgba(0,0,0,0.32) !important;
          }
          /* Dedicated backdrop rendered alongside the portaled popover.
             Sits *below* the popover but above every Dashboard tile,
             filter card, sticky element and horizontal scroller so
             taps that miss the calendar dismiss it and nothing on the
             Dashboard leaks through visually. */
          [data-testid="date-range-popover-backdrop"] {
            position: fixed !important;
            inset: 0 !important;
            background: rgba(11, 11, 15, 0.35) !important;
            z-index: 2147482999 !important;
            -webkit-tap-highlight-color: transparent;
          }
          /* Members drills a second wrapper around DateRangePicker
             (to stack the "Rejected Date" hint underneath). On mobile
             we need that wrapper to fill the row so the rail inside
             also stretches to the row width instead of clipping to
             its intrinsic 540-ish px and shifting Today off-screen. */
          .hfa-date-slot {
            width: 100% !important;
            min-width: 0 !important;
            align-items: stretch !important;
          }
          /* Filter Status cards (Payment / Review) stretch full-width
             and stack. Keeps every option reachable without pinching. */
          [data-hfa-filter-card] { flex: 1 1 100% !important; min-width: 0 !important; }
          .hfa-master-search { width: 180px !important; min-width: 0 !important; }
          [data-hfa-filter-card] { max-width: none !important; }
          [data-hfa-filter-card-wrap] { flex: 1 1 100% !important; display: flex !important; min-width: 0 !important; }
          [data-hfa-seg-toggle] {
            flex-wrap: wrap !important;
          }
          [data-hfa-seg-toggle] > button {
            min-height: 36px !important;
            padding: 8px 14px !important;
            font-size: 12.5px !important;
          }
          /* Drawer + Modal — full-viewport with proper safe padding. */
          [data-hfa-drawer] {
            width: 100vw !important;
            max-width: 100vw !important;
            border-radius: 0 !important;
          }
          [data-hfa-modal] {
            width: 100vw !important;
            max-width: 100vw !important;
            height: 100vh !important;
            border-radius: 0 !important;
            margin: 0 !important;
          }
          /* Sidebar in mobile drawer mode: slightly narrower + rounded. */
          .hfa-admin-sidebar {
            width: 82vw !important;
            max-width: 300px !important;
          }
        }
        @media (max-width: 480px) {
          .hfa-admin-main { padding: 14px 12px 40px !important; }
          .hfa-list-header h1 { font-size: 24px !important; }
          .hfa-metric-card > div:last-child { font-size: 24px !important; }
        }
        /* Dashboard stat cards: extra side breathing room (≈3× the main padding) on desktop. */
        .hfa-dash-stats { padding: 0 64px; }
        @media (max-width: 1280px) { .hfa-dash-stats { padding: 0 24px; } }
        @media (max-width: 900px) {
          .hfa-dash-stats { padding: 0 14px; }
          .hfa-dash-note { text-align: left !important; margin-left: 0 !important; max-width: none !important; white-space: normal !important; }
          /* Dashboard City Chapters quick filter: options centred under the heading. */
          [data-hfa-filter-card-wrap] [data-hfa-filter-card] { text-align: center; }
        }
        /* Analytics filter row: stack + centre on narrow screens. */
        @media (max-width: 900px) {
          .hfa-analytics-filter-row { display: flex !important; flex-direction: column; align-items: center; }
          .hfa-analytics-filter-row > span[aria-hidden] { display: none; }
          .hfa-analytics-filter-row > div { width: 100%; display: flex; justify-content: center; }
        }
      `}</style>
    </div>
  );
}

/* =====================================================================
 * Dashboard — premium client-side view with date-range filtering
 *
 * Backend rule: we do NOT modify any admin API. Every card is computed
 * client-side from the existing listing endpoints. `applied_date`
 * (YYYY-MM-DD in IST) is the single source of truth for date filtering
 * — a member who applied on Aug 2 remains counted for Aug 2 even if
 * refunded on Aug 4.
 * =================================================================== */

/** Return YYYY-MM-DD (IST) for a Date object. */
function _ymdIST(d) {
  // IST = UTC + 5:30. Shift the epoch and read UTC components.
  const shifted = new Date(d.getTime() + 5.5 * 3600 * 1000);
  return shifted.toISOString().slice(0, 10);
}
function _todayIST() { return _ymdIST(new Date()); }
function _addDays(dstr, n) {
  // dstr is YYYY-MM-DD; produce YYYY-MM-DD n days later (IST-safe: no TZ math).
  const [y, m, d] = dstr.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + n);
  return dt.toISOString().slice(0, 10);
}
function _mondayOfWeek(dstr) {
  const [y, m, d] = dstr.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const wd = dt.getUTCDay(); // 0=Sun..6=Sat
  const back = wd === 0 ? 6 : wd - 1; // move back to Monday
  dt.setUTCDate(dt.getUTCDate() - back);
  return dt.toISOString().slice(0, 10);
}
function _firstOfMonth(dstr) { return dstr.slice(0, 8) + "01"; }
function _lastOfMonth(dstr) {
  const [y, m] = dstr.split("-").map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return `${dstr.slice(0, 8)}${String(last).padStart(2, "0")}`;
}

const QUICK_RANGES = [
  { key: "today",       label: "Today" },
  { key: "yesterday",   label: "Yesterday" },
  { key: "this_week",   label: "This Week" },
  { key: "last_week",   label: "Last Week" },
  { key: "this_month",  label: "This Month" },
  { key: "last_month",  label: "Last Month" },
  { key: "last_30",     label: "Last 30 Days" },
  { key: "all_time",    label: "All Time" },
  { key: "custom",      label: "Custom Range" },
];

// Sentinel lower bound for the "All Time" preset. Kept far enough in the
// past that it precedes every real HFA record while still being a valid
// YYYY-MM-DD string so `inRange` (lexicographic compare) matches every
// dated row. Archived exclusion is enforced independently upstream.
const _ALL_TIME_FROM = "1970-01-01";

function computeRange(key, custom) {
  const today = _todayIST();
  if (key === "today") return { from: today, to: today };
  if (key === "yesterday") { const y = _addDays(today, -1); return { from: y, to: y }; }
  if (key === "this_week") return { from: _mondayOfWeek(today), to: today };
  if (key === "last_week") {
    const mon = _mondayOfWeek(today);
    return { from: _addDays(mon, -7), to: _addDays(mon, -1) };
  }
  if (key === "this_month") return { from: _firstOfMonth(today), to: today };
  if (key === "last_month") {
    const firstThis = _firstOfMonth(today);
    const lastPrev = _addDays(firstThis, -1);
    return { from: _firstOfMonth(lastPrev), to: _lastOfMonth(lastPrev) };
  }
  if (key === "last_30") return { from: _addDays(today, -29), to: today };
  if (key === "all_time") return { from: _ALL_TIME_FROM, to: today };
  if (key === "custom" && custom && custom.from && custom.to) return { from: custom.from, to: custom.to };
  return { from: today, to: today };
}

/** One shared Quick Date range for Dashboard, Members and Payments.
 *  Module-level so it survives tab navigation; the first page mounted
 *  seeds it from its own Settings default, afterwards the latest
 *  selection on any page wins. */
let _GLOBAL_RANGE = null;
const _rangeSubs = new Set();
function _setGlobalRange(v) {
  _GLOBAL_RANGE = typeof v === "function" ? v(_GLOBAL_RANGE) : v;
  _rangeSubs.forEach((fn) => fn(_GLOBAL_RANGE));
}
const TODAY_RANGE = () => ({ preset: "today", ...computeRange("today") });
function useGlobalDateRange(seedPrefKey) {
  const [range, setLocal] = useState(() => {
    if (!_GLOBAL_RANGE) {
      const pref = _prefGet(seedPrefKey, "this_month");
      _GLOBAL_RANGE = { preset: pref, ...computeRange(pref) };
    }
    return _GLOBAL_RANGE;
  });
  useEffect(() => { _rangeSubs.add(setLocal); return () => { _rangeSubs.delete(setLocal); }; }, []);
  return [range, _setGlobalRange];
}


function inRange(dateStr, range) {
  if (!dateStr) return false;
  // Compare YYYY-MM-DD strings lexicographically — safe & fast.
  return dateStr >= range.from && dateStr <= range.to;
}

/** Convert a bare "YYYY-MM-DD" string into DD/MM/YYYY. Keeps the
 *  admin-portal-wide date format consistent (never ISO). Returns ""
 *  on empty input so callers can compose strings without extra guards. */
function _ymdToDMY(s) {
  if (!s) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(s);
}

/** Present-tense label for a Quick Date Filter preset. Falls back to
 *  a title-cased echo of the raw preset key so unknown presets still
 *  render safely. Kept in sync with DateRangePicker's chip labels. */
const _PRESET_LABEL = {
  today:       "Today",
  yesterday:   "Yesterday",
  this_week:   "This Week",
  last_week:   "Last Week",
  this_month:  "This Month",
  last_month:  "Last Month",
  last_30:     "Last 30 Days",
  all_time:    "All Time",
  custom:      "Custom Range",
};

/** Build the {label, range} pair displayed above the record count on
 *  every list page. Same-day ranges (Today/Yesterday) collapse to a
 *  single DD/MM/YYYY value; wider ranges show a `from – to` span.
 *  Returns null when no valid range is set so the caller can render
 *  nothing at all (keeps the header compact). */
/** Compact prev / next circular buttons that flank the active
 *  single-date label ("( ‹ ) DD/MM/YYYY ( › )"). Each control is a
 *  visible outlined circle — the arrow sits centred inside so the
 *  whole circle is the hit-target, not just the glyph. Responsive
 *  sizing via the `.hfa-date-nav-btn` class in the top-of-file style
 *  block: 36px on desktop, 44px on mobile (spec §4). Forward arrow is
 *  disabled once the current selection reaches today, mirroring the
 *  DateRangePicker calendar's future-date rule so arrows never
 *  navigate into an empty tomorrow. `testid` prefixes the two buttons
 *  so per-page smoke tests can target them unambiguously. */
const _WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
function _weekdayOfYmd(ymd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || ""));
  if (!m) return "";
  return _WEEKDAYS[new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])).getUTCDay()];
}

function SingleDayArrows({ ymd, onShift, testidPrefix = "date-nav", label }) {
  const isFuture = !ymd || ymd >= _todayIST();
  const Chevron = ({ dir }) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="2.25" strokeLinecap="round"
         strokeLinejoin="round" aria-hidden="true">
      {dir === "left" ? <polyline points="15 18 9 12 15 6" />
                      : <polyline points="9 18 15 12 9 6" />}
    </svg>
  );
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8,
                   fontVariantNumeric: "tabular-nums" }}>
      <button
        className="hfa-date-nav-btn"
        data-testid={`${testidPrefix}-prev`}
        type="button"
        title="Previous day"
        aria-label="Previous day"
        onClick={(e) => { e.stopPropagation(); onShift(-1); }}
      >
        <Chevron dir="left" />
      </button>
      <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-start", gap: 3 }}>
        <span style={{ fontSize: 15, color: T.ink, fontWeight: 600,
                       lineHeight: 1, whiteSpace: "nowrap" }}>{label}</span>
        <span data-testid={`${testidPrefix}-weekday`}
              style={{ fontSize: 15, color: T.ink, fontWeight: 600,
                       lineHeight: 1, whiteSpace: "nowrap" }}>{_weekdayOfYmd(ymd)}</span>
      </span>
      <button
        className="hfa-date-nav-btn"
        data-testid={`${testidPrefix}-next`}
        type="button"
        title={isFuture ? "You're already at today" : "Next day"}
        aria-label="Next day"
        disabled={isFuture}
        onClick={(e) => { e.stopPropagation(); if (!isFuture) onShift(1); }}
      >
        <Chevron dir="right" />
      </button>
    </span>
  );
}

function activeDateChipFor(dateRange) {
  if (!dateRange || !dateRange.from || !dateRange.to) return null;
  const label = _PRESET_LABEL[dateRange.preset] || "Date Filter";
  // All Time spans the entire history — showing a literal "01/01/1970 – …"
  // is misleading and noisy. Render just the label with a friendly hint.
  if (dateRange.preset === "all_time") {
    return { label, range: "All records", isSingleDay: false, singleDay: null };
  }
  const fromDMY = _ymdToDMY(dateRange.from);
  const toDMY = _ymdToDMY(dateRange.to);
  const isSingleDay = dateRange.from === dateRange.to;
  const range = isSingleDay ? fromDMY : `${fromDMY} – ${toDMY}`;
  return {
    label,
    range,
    isSingleDay,
    // `singleDay` is the raw YYYY-MM-DD anchor — the caller uses it
    // (together with `_shiftSingleDay`) to wire the ‹ / › one-day
    // navigation arrows that surround the active-date label.
    singleDay: isSingleDay ? dateRange.from : null,
  };
}

/** Shift a single-day date filter by `delta` calendar days (may be
 *  negative). Always returns a new range object with the "custom"
 *  preset so the Admin Portal stops trying to snap back to the quick
 *  filter label the user originally clicked (spec §3, §10 — "the
 *  arrows must not force the user back into the original quick-filter
 *  label"). Caller is responsible for skipping this when the current
 *  filter is not a single-day selection. */
function _shiftSingleDay(range, delta) {
  if (!range || !range.from || !range.to || range.from !== range.to) return range;
  const next = _addDays(range.from, delta);
  return { preset: "custom", from: next, to: next };
}

/** Extract the payment DATE in **Indian Standard Time** (YYYY-MM-DD)
 *  from a row. This is the calendar key used by every quick date
 *  filter (Today / Yesterday / This Week / etc.) and every Dashboard
 *  aggregation. Because Atlas stores `paid_at` as a UTC ISO instant
 *  (e.g. "2026-08-06T19:37:04Z"), a naive slice(0,10) would classify
 *  a payment made at IST 01:07 on Aug 7 as belonging to Aug 6 — off
 *  by a full calendar day. We shift the instant by +5:30h and then
 *  read the calendar parts so filtering and display agree.
 *
 *  Returns "" when the row was never paid (unpaid rows never match
 *  any payment-date range, which is the intended behaviour). */
function _paymentDateOf(row) {
  const raw = row && (row.paid_at || row.payment_date || "");
  if (!raw) return "";
  const s = String(raw);
  // Bare "YYYY-MM-DD" — already an IST calendar date, use as-is.
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const t = Date.parse(s);
  if (Number.isNaN(t)) return s.slice(0, 10);
  const ist = new Date(t + 5.5 * 3600 * 1000);
  const y = ist.getUTCFullYear();
  const m = String(ist.getUTCMonth() + 1).padStart(2, "0");
  const d = String(ist.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** COHORT DATE for a member row: the Applied Date (IST calendar day). A
 *  member belongs permanently to the day they applied; later onboarding,
 *  rejection, removal or refund changes their status, never their cohort.
 *  "" when undated (e.g. the Lifetime import) — no date is ever inferred. */
function _memberDateOf(row) {
  const raw = row && (row.applied_date || "");
  if (!raw) return "";
  const s = String(raw);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const t = Date.parse(s);
  if (Number.isNaN(t)) return s.slice(0, 10);
  const ist = new Date(t + 5.5 * 3600 * 1000);
  return `${ist.getUTCFullYear()}-${String(ist.getUTCMonth() + 1).padStart(2, "0")}-${String(ist.getUTCDate()).padStart(2, "0")}`;
}

/** Range match shared by Dashboard and Members: "All Time" means every
 *  record, so undated historical members are kept there; every other
 *  preset needs a real date inside the window. */
function _memberInRange(row, range) {
  const d = _memberDateOf(row);
  if (!d) return !!(range && range.preset === "all_time");
  return inRange(d, range);
}

/** Extract the REJECTED DATE in IST (YYYY-MM-DD) from a row. Backs
 *  the Dashboard's "Refunded" and "To Be Refunded" cards, which are
 *  date-basis'd on when the admin marked the member as rejected —
 *  NOT the original payment date. Falls back to `updated_at` for
 *  legacy rejected records created before `rejected_at` was stamped
 *  server-side, so no rejected member disappears from those cards
 *  after backfill. Returns "" when the row has no relevant stamp. */
function _rejectedDateOf(row) {
  const raw = row && (row.rejected_at || row.updated_at || "");
  if (!raw) return "";
  const s = String(raw);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const t = Date.parse(s);
  if (Number.isNaN(t)) return s.slice(0, 10);
  const ist = new Date(t + 5.5 * 3600 * 1000);
  const y = ist.getUTCFullYear();
  const m = String(ist.getUTCMonth() + 1).padStart(2, "0");
  const d = String(ist.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Range picker — Quick chips + a two-click custom calendar.
 *  Supports:
 *   • any previous date as Start (no lower bound)
 *   • End Date cannot exceed today
 *   • cross-month + cross-year selection via ‹ › nav and year jumper
 *   • same-day selection: click a date, then click the same date again
 *   • double-click selects that day as Start AND End in one gesture
 */
function DateRangePicker({ value, onChange, chipOutline = "line" }) {
  const hairChips = chipOutline === "hairline-ink";
  const [open, setOpen] = useState(false);
  const [pickFrom, setPickFrom] = useState(null);
  const [monthCursor, setMonthCursor] = useState(() => _todayIST().slice(0, 7));
  const rootRef = useRef(null);
  const popoverRef = useRef(null);
  // Mobile-only portal — on ≤720px viewports the calendar is rendered
  // as a *portaled* overlay directly under document.body so it escapes
  // every ancestor stacking context (Dashboard metric cards, transforms
  // on hover, overflow-x scrollers, sticky filter bars, etc.) and can
  // never be clipped or covered by other Admin Portal content. Desktop
  // (>720px) continues to render the popover in-place, exactly as
  // before — no visual, position or behaviour change on desktop.
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(max-width: 720px)");
    const apply = () => setIsMobile(mq.matches);
    apply();
    if (mq.addEventListener) {
      mq.addEventListener("change", apply);
      return () => mq.removeEventListener("change", apply);
    }
    // Safari <14 fallback
    mq.addListener(apply);
    return () => mq.removeListener(apply);
  }, []);
  useEffect(() => {
    if (!open) return;
    // A tap counts as "outside" only when it lands outside BOTH the
    // chip strip (rootRef) AND the portaled popover (popoverRef). This
    // keeps the mobile portal working even though its DOM parent is
    // now document.body rather than the DateRangePicker root.
    const h = (e) => {
      const t = e.target;
      const insideRoot = rootRef.current && rootRef.current.contains(t);
      const insidePopover = popoverRef.current && popoverRef.current.contains(t);
      if (!insideRoot && !insidePopover) { setOpen(false); setPickFrom(null); }
    };
    document.addEventListener("mousedown", h);
    // Mobile Safari sometimes suppresses the synthesised `mousedown`
    // when a tap lands on a scrollable ancestor — bind `touchstart` too
    // so tapping outside the mobile bottom-sheet popover closes it on
    // the very first tap.
    document.addEventListener("touchstart", h, { passive: true });
    return () => {
      document.removeEventListener("mousedown", h);
      document.removeEventListener("touchstart", h);
    };
  }, [open]);

  const applyPreset = (key) => {
    if (key === "custom") { setOpen(true); return; }
    const r = computeRange(key);
    onChange({ preset: key, ...r });
    setOpen(false);
  };

  const gridDays = useMemo(() => {
    const [y, m] = monthCursor.split("-").map(Number);
    const first = new Date(Date.UTC(y, m - 1, 1));
    const firstWd = first.getUTCDay(); // 0=Sun..
    const back = firstWd === 0 ? 6 : firstWd - 1;
    const start = new Date(first);
    start.setUTCDate(start.getUTCDate() - back);
    const days = [];
    for (let i = 0; i < 42; i++) {
      const dt = new Date(start);
      dt.setUTCDate(dt.getUTCDate() + i);
      days.push(dt.toISOString().slice(0, 10));
    }
    return days;
  }, [monthCursor]);

  const monthLabel = useMemo(() => {
    const [y, m] = monthCursor.split("-").map(Number);
    return new Date(Date.UTC(y, m - 1, 1)).toLocaleString("en-IN", { month: "long" });
  }, [monthCursor]);
  const yearLabel = useMemo(() => Number(monthCursor.split("-")[0]), [monthCursor]);

  const shiftMonth = (n) => {
    const [y, m] = monthCursor.split("-").map(Number);
    const d = new Date(Date.UTC(y, m - 1 + n, 1));
    setMonthCursor(d.toISOString().slice(0, 7));
  };
  const shiftYear = (n) => {
    const [y, m] = monthCursor.split("-").map(Number);
    const d = new Date(Date.UTC(y + n, m - 1, 1));
    setMonthCursor(d.toISOString().slice(0, 7));
  };

  // Single-click behaviour: 1st sets Start; 2nd sets End (or same-day if equal).
  const onDayClick = (d) => {
    if (d > _todayIST()) return; // future dates are always disabled
    if (!pickFrom) {
      setPickFrom(d);
      return;
    }
    const from = pickFrom <= d ? pickFrom : d;
    const to   = pickFrom <= d ? d : pickFrom;
    onChange({ preset: "custom", from, to });
    setPickFrom(null);
    setOpen(false);
  };
  // Double-click: instantly select that single day as Start=End.
  const onDayDoubleClick = (d) => {
    if (d > _todayIST()) return;
    onChange({ preset: "custom", from: d, to: d });
    setPickFrom(null);
    setOpen(false);
  };

  const isSelected = (d) => value.preset === "custom" && d >= value.from && d <= value.to;
  const inPreviewRange = (d) => {
    if (!pickFrom) return false;
    const lo = pickFrom, hi = _todayIST();
    return d > lo && d <= hi;
  };

  return (
    <div ref={rootRef} data-hfa-chips style={{ position: "relative", display: "flex", flexWrap: "wrap",
                                 gap: 6, justifyContent: "flex-end" }}>
      {QUICK_RANGES.map((q) => {
        const active = value.preset === q.key;
        return (
          <button key={q.key} data-testid={`dash-range-${q.key}`}
            onClick={() => applyPreset(q.key)}
            style={{
              padding: "7px 12px", fontSize: 12, fontWeight: active ? 600 : 500,
              border: active ? `1px solid ${T.ink}` : (hairChips ? HAIR_INK : `1px solid ${T.line}`),
              background: active ? T.ink : "white",
              color: active ? "white" : (hairChips ? T.ink : T.slate),
              borderRadius: 999, cursor: "pointer", fontFamily: "inherit",
              transition: "all 0.15s ease",
            }}
            onMouseEnter={(e) => { if (!active) { if (hairChips) e.currentTarget.style.background = "#F7F7F8"; else e.currentTarget.style.borderColor = T.slate; } }}
            onMouseLeave={(e) => { if (!active) { if (hairChips) e.currentTarget.style.background = "white"; else e.currentTarget.style.borderColor = T.line; } }}>
            {q.label}
          </button>
        );
      })}
      {open && (() => {
        const popover = (
          <div ref={popoverRef}
               style={{ position: "absolute", right: 0, top: "calc(100% + 8px)",
                        background: "white", border: `1px solid ${T.line}`, borderRadius: 14,
                        boxShadow: "0 20px 48px rgba(0,0,0,0.16)", padding: 14, zIndex: 50,
                        minWidth: 320 }} onClick={(e) => e.stopPropagation()}
               data-testid="date-range-popover">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                          marginBottom: 12, gap: 6 }}>
              <button data-testid="cal-prev-year" onClick={() => shiftYear(-1)}
                title="Previous year"
                style={{ background: "transparent", border: `1px solid ${T.line}`, cursor: "pointer",
                         fontSize: 12, color: T.slate, padding: "4px 10px", borderRadius: 6,
                         fontFamily: "inherit" }}>«</button>
              <button data-testid="cal-prev-month" onClick={() => shiftMonth(-1)}
                title="Previous month"
                style={{ background: "transparent", border: "none", cursor: "pointer",
                         fontSize: 16, color: T.slate, padding: "4px 8px" }}>‹</button>
              <div style={{ fontSize: 13, fontWeight: 600, flex: 1, textAlign: "center" }}>
                {monthLabel} <span style={{ color: T.muted, fontWeight: 500 }}>{yearLabel}</span>
              </div>
              <button data-testid="cal-next-month" onClick={() => shiftMonth(1)}
                title="Next month"
                style={{ background: "transparent", border: "none", cursor: "pointer",
                         fontSize: 16, color: T.slate, padding: "4px 8px" }}>›</button>
              <button data-testid="cal-next-year" onClick={() => shiftYear(1)}
                title="Next year"
                style={{ background: "transparent", border: `1px solid ${T.line}`, cursor: "pointer",
                         fontSize: 12, color: T.slate, padding: "4px 10px", borderRadius: 6,
                         fontFamily: "inherit" }}>»</button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
              {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => (
                <div key={d} style={{ fontSize: 10, textAlign: "center", color: T.muted, fontWeight: 600, padding: "6px 0" }}>{d}</div>
              ))}
              {gridDays.map((d) => {
                const inMonth = d.slice(0, 7) === monthCursor;
                const isStart = d === pickFrom;
                const selected = isSelected(d) || isStart;
                const preview  = inPreviewRange(d);
                const disabled = d > _todayIST();
                return (
                  <button key={d} data-testid={`cal-day-${d}`}
                    onClick={() => onDayClick(d)}
                    onDoubleClick={() => onDayDoubleClick(d)}
                    disabled={disabled}
                    style={{
                      padding: "6px 0", fontSize: 12, borderRadius: 6,
                      border: "none", cursor: disabled ? "not-allowed" : "pointer",
                      background: selected ? T.ink : preview ? T.brandSoft : "transparent",
                      color: selected ? "white" : disabled ? T.muted : inMonth ? T.ink : T.muted,
                      fontFamily: "inherit", opacity: !inMonth ? 0.35 : 1,
                      fontWeight: selected ? 600 : 400,
                    }}>
                    {Number(d.slice(-2))}
                  </button>
                );
              })}
            </div>
            {pickFrom ? (
              <div style={{ marginTop: 10, fontSize: 11, color: T.slate }}>
                Start: <b>{fmtDate(pickFrom)}</b> — click another day to set End (or double-click for same-day).
              </div>
            ) : (
              <div style={{ marginTop: 10, fontSize: 11, color: T.muted }}>
                Click a date to set Start · double-click a date to select same-day range.
              </div>
            )}
          </div>
        );

        // Desktop: render inline (position: absolute relative to
        // DateRangePicker) — untouched behaviour. Mobile: portal into
        // document.body with a dim backdrop so the calendar sits above
        // every Dashboard card, sticky filter and overflow-x container
        // regardless of their stacking contexts. `popoverRef` still
        // resolves correctly through the portal, so the outside-click
        // handler above continues to treat portal-internal taps as
        // "inside".
        if (isMobile && typeof document !== "undefined") {
          return createPortal(
            <>
              <div data-testid="date-range-popover-backdrop"
                   onClick={() => { setOpen(false); setPickFrom(null); }} />
              {popover}
            </>,
            document.body
          );
        }
        return popover;
      })()}
    </div>
  );
}

/* =====================================================================
 * Analytics Line Charts (Dashboard) — Onboarded Members + Revenue
 *
 * Reads from the exact same Dashboard payload (`members` + `payments`)
 * and honours the exact same date filter + archived exclusion. Every
 * data point is anchored to `paid_at` (via `_paymentDateOf`) so the
 * timeline never shifts if a row is later rejected or refunded, matching
 * the reporting-date rule the metric cards use above.
 *
 * Daily granularity is preserved for every range including "All Time".
 * X-axis label density is thinned only visually via `minTickGap`; every
 * daily point remains present and hover/tap-interactive through the
 * tooltip + dot markers.
 * ===================================================================== */
const _MONTHS_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
function _fmtDayShort(ymd) {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return String(ymd || "");
  const [_, m, d] = ymd.split("-");
  return `${Number(d)} ${_MONTHS_SHORT[Number(m) - 1]}`;
}
function _fmtRupeesFull(n) {
  // Full-rupee currency formatter (Indian grouping) for the Revenue
  // chart's Y-axis, tooltip and total.
  const v = Number(n);
  if (!Number.isFinite(v)) return "₹0";
  return `₹${fmtIN(Math.round(v))}`;
}

/** Interactive tooltip renderer shared across both analytics charts.
 *  `unit` is either "count" or "rupees" — controls formatting. Kept
 *  compact and premium so it never obstructs the chart body. */
function _AnalyticsTooltip({ active, payload, label, unit, seriesLabel, seriesColor }) {
  if (!active || !payload || !payload.length) return null;
  const raw = payload[0].value;
  // Revenue tooltip must show full rupees ("₹4000") — spec §3P.
  const display = unit === "rupees" ? _fmtRupeesFull(raw) : fmtIN(raw);
  return (
    <div data-testid="analytics-tooltip"
         style={{
           background: "white",
           border: `1px solid ${T.line}`,
           borderRadius: 10,
           padding: "8px 12px",
           boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
           fontFamily: "inherit",
         }}>
      <div style={{ fontSize: 11, color: T.muted, letterSpacing: "0.02em",
                    fontVariantNumeric: "tabular-nums" }}>
        {_ymdToDMY(label)}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
        <span style={{ width: 8, height: 8, borderRadius: 999,
                       background: seriesColor, display: "inline-block" }} />
        <span style={{ fontSize: 12, color: T.slate, fontWeight: 500 }}>{seriesLabel}</span>
        <span style={{ fontSize: 13, color: T.ink, fontWeight: 700,
                       marginLeft: "auto", fontVariantNumeric: "tabular-nums" }}>
          {display}
        </span>
      </div>
    </div>
  );
}

// Event used by the Dashboard's "Clear all filters" button to nudge
// the Analytics section to also reset its Review Status selection back
// to All. Kept lightweight (a plain window CustomEvent) so the
// Analytics component owns its own state without needing to be a
// controlled child.
const _ANALYTICS_RESET_EVENT = "hfa:analytics-reset";

/** Metric selector (Members ↔ Revenue). Two-option segmented control
 *  visually matching the Members/Payments filter chips: pill-shape,
 *  soft border, orange active state, generous tap target. Kept small
 *  and standalone so it can sit on the same row as the Review Status
 *  filter without competing for hierarchy. */
function _AnalyticsMetricToggle({ value, onChange }) {
  const options = [
    { k: "members", label: "Members" },
    { k: "revenue", label: "Revenue" },
  ];
  return (
    <div data-testid="analytics-metric-toggle"
      style={{ display: "inline-flex", gap: 3, background: T.card,
               border: `1px solid ${T.line}`, borderRadius: 999, padding: 3,
               boxShadow: "0 1px 2px rgba(0,0,0,0.02)" }}>
      {options.map((o) => {
        const active = value === o.k;
        return (
          <button key={o.k}
            data-testid={`analytics-metric-${o.k}`}
            onClick={() => onChange(o.k)}
            title={`Show ${o.label} in the Analytics chart`}
            style={{
              padding: "6px 16px", fontSize: 12, fontWeight: active ? 600 : 500,
              borderRadius: 999, border: "none", fontFamily: "inherit",
              background: active ? T.brand : "transparent",
              color: active ? "white" : T.slate,
              cursor: "pointer",
              transition: "background 0.15s ease, color 0.15s ease",
              boxShadow: active ? "0 2px 8px rgba(234,88,12,0.28)" : "none",
              userSelect: "none", touchAction: "manipulation",
            }}
            onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = T.brandTint; }}
            onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "transparent"; }}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function AnalyticsCharts({ members, payments, range }) {
  // ------------------------------------------------------------------
  // Analytics-scoped state — deliberately isolated inside this
  // component so the upper Dashboard cards can never depend on it
  // (spec §1, §18: Review Status is Analytics-only).
  //
  // `rFilter` is a Set of status keys and follows the SAME multi-select
  // semantics as Members/Payments: single-click replaces, Ctrl/⌘-click
  // (or double-tap on touch) toggles a key in/out of the set without
  // wiping the others. That behaviour is entirely provided by the
  // shared `SegToggle` primitive — we just wire the reducer.
  //
  // `metric` is the currently displayed series ("members" | "revenue"),
  // defaulting to Members per spec §6.
  // ------------------------------------------------------------------
  const [rFilter, setRFilter] = useState(() => new Set(["all"]));
  const [metric, setMetric] = useState("members");

  // Listen for the Dashboard's Clear-all-filters event and reset the
  // Analytics-scoped selection back to "All". `metric` intentionally
  // does NOT reset — the metric selector is a display preference, not
  // a filter, so clearing filters shouldn't flip it.
  useEffect(() => {
    const h = () => setRFilter(new Set(["all"]));
    window.addEventListener(_ANALYTICS_RESET_EVENT, h);
    return () => window.removeEventListener(_ANALYTICS_RESET_EVENT, h);
  }, []);

  /** Reducer that matches Members's `handleRSelect` semantics:
   *   • replace   → set = {k}       (unless k === "all", which clears)
   *   • toggle    → add/remove k from set; "all" and specific keys are
   *                 mutually exclusive (spec §3: "All means no
   *                 restriction, not an additional status")
   *   • toggle-from → same as toggle but rebuilt from a supplied
   *                 pre-tap snapshot (double-tap flow inside SegToggle) */
  const handleRSelect = useCallback((k, mode, preSet) => {
    setRFilter((prev) => {
      const base = mode === "toggle-from" && preSet ? new Set(preSet) : new Set(prev);
      if (mode === "replace") {
        return new Set([k]);
      }
      // toggle / toggle-from — keep All ↔ specific-keys mutually exclusive.
      if (k === "all") {
        return new Set(["all"]);
      }
      // Adding a specific status auto-drops "all".
      base.delete("all");
      if (base.has(k)) {
        base.delete(k);
        if (base.size === 0) base.add("all");
      } else {
        base.add(k);
      }
      return base;
    });
  }, []);

  // ------------------------------------------------------------------
  // Series computation — one memo covers both metrics so switching
  // Members ↔ Revenue is instant (no refetch, no recompute round-trip).
  // Every point carries BOTH values; the chart just reads the one the
  // metric selector points at.
  // ------------------------------------------------------------------
  const series = useMemo(() => {
    const activeMembers = members.filter((r) => !r.is_archived);
    const refundedAppIds = new Set();
    payments.forEach((p) => {
      if ((p.status || "").toLowerCase() === "refunded" && p.application_id) {
        refundedAppIds.add(p.application_id);
      }
    });
    const displayPay = (r) => refundedAppIds.has(r.application_id)
      ? "refunded"
      : ((r.payment_status || "").toLowerCase() || "unpaid");

    // Multi-select semantics: All (or empty) → no restriction.
    // Any specific keys selected → keep only those statuses.
    const rAll = rFilter.has("all") || rFilter.size === 0;
    const reviewMatch = (r) => rAll || rFilter.has(r.status);

    // Only rows whose payment date falls in the active filter window
    // AND whose current Review Status matches the active selection.
    const inRangeRows = activeMembers.filter((r) => _memberInRange(r, range) && reviewMatch(r));

    // Chart date span. For All Time clamp to the actual data extent so
    // the timeline isn't padded with decades of empty days.
    let fromDate = range.from;
    let toDate = range.to;
    if (range.preset === "all_time") {
      const paidDates = inRangeRows.map((r) => _memberDateOf(r)).filter(Boolean).sort();
      fromDate = paidDates[0] || _todayIST();
      toDate = _todayIST();
    }

    // Daily buckets from fromDate → toDate (inclusive) with safety cap.
    const buckets = new Map();
    let cursor = fromDate;
    let iter = 0;
    while (cursor <= toDate && iter < 20000) {
      buckets.set(cursor, { date: cursor, members: 0, revenue: 0 });
      cursor = _addDays(cursor, 1);
      iter += 1;
    }

    inRangeRows.forEach((r) => {
      const d = _memberDateOf(r);
      const b = buckets.get(d);
      if (!b) return;
      // Members metric — daily count of records matching the current
      // Review Status + Date filters (spec §5, §7). No status-specific
      // narrowing here: rFilter has already narrowed the pool. When
      // rFilter is "all", this becomes the daily count of every
      // non-archived, in-range member.
      b.members += 1;
      // Revenue metric — the member's actual Amount (same normalised
      // amount the Revenue cards use), summed per Applied Date.
      const s = displayPay(r);
      if (PAID_LIKE.has(s)) {
        b.revenue += (adminDisplayMembershipAmount(r.amount_paise) || 0) / 100;
      }
    });

    return Array.from(buckets.values());
  }, [members, payments, range, rFilter]);

  const hasData = series.length > 0;
  const membersTotal = series.reduce((s, d) => s + d.members, 0);
  const revenueTotal = series.reduce((s, d) => s + d.revenue, 0);

  const brand = T.brand;
  const successGreen = "#16A34A";
  const isRevenue = metric === "revenue";
  const seriesColor = isRevenue ? successGreen : brand;
  const seriesLabel = isRevenue ? "Revenue" : "Members";
  const dataKey = isRevenue ? "revenue" : "members";
  const totalDisplay = isRevenue ? _fmtRupeesFull(revenueTotal) : fmtIN(membersTotal);

  return (
    <div data-testid="dash-analytics-section" style={{ marginTop: 32 }}>
      <SectionHeader title="Analytics" badge style={{ margin: "0 0 14px", textAlign: "center" }} />

      {/* Filter row — Review Status (multi-select) centred under the
          heading, Metric selector (Members / Revenue) kept on the right.
          On mobile the row stacks (see .hfa-analytics-filter-row). */}
      <div className="hfa-analytics-filter-row"
           style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 14,
                    alignItems: "center", marginBottom: 14 }}>
        <span aria-hidden="true" />
        <div data-testid="analytics-r-filter-wrap" style={{ justifySelf: "center" }}>
          <FilterGroupCard heading="Review Status">
            <SegToggle options={R_OPTS} valueSet={rFilter}
                       onSelect={handleRSelect} testid="analytics-r-toggle" />
          </FilterGroupCard>
        </div>
        <div style={{ justifySelf: "end" }}>
          <_AnalyticsMetricToggle value={metric} onChange={setMetric} />
        </div>
      </div>

      <div data-testid="analytics-note"
           style={{ fontSize: 11.5, color: T.muted, marginTop: -6, marginBottom: 14,
                    letterSpacing: "0.01em", textAlign: "center" }}>
        Daily activity anchored to Applied Date (cohort). Hover or tap any point to see the exact value.
        {isRevenue && " Revenue sums each member's actual Amount per Applied Date."}
      </div>

      {/* Unified chart — one line, one Y-axis. The metric selector
          swaps the underlying series, colour and Y-axis formatter
          without re-mounting the chart so switching feels instant. */}
      <div data-testid="chart-analytics"
           style={{
             background: "white",
             border: `1px solid ${T.line}`,
             borderRadius: 16,
             padding: "18px 18px 10px",
           }}>
        <div style={{ display: "flex", alignItems: "baseline",
                      justifyContent: "space-between", marginBottom: 12, gap: 12 }}>
          <div data-testid="analytics-chart-title"
               style={{ fontSize: 13, letterSpacing: "0.12em",
                        textTransform: "uppercase",
                        color: isRevenue ? "#166534" : "#7C2D12",
                        fontWeight: 700 }}>{seriesLabel}</div>
          <div data-testid="analytics-chart-total"
               style={{ fontSize: 11, color: T.muted,
                        fontVariantNumeric: "tabular-nums" }}>
            {totalDisplay} total
          </div>
        </div>
        <div style={{ width: "100%", height: 280 }}>
          {hasData ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={series} margin={{ top: 6, right: 12, left: 0, bottom: 6 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={T.lineSoft} vertical={false} />
                <XAxis dataKey="date"
                       tick={{ fontSize: 11, fill: T.muted }}
                       tickFormatter={_fmtDayShort}
                       tickLine={false}
                       axisLine={{ stroke: T.line }}
                       minTickGap={44}
                       interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 11, fill: T.muted }}
                       tickLine={false}
                       axisLine={{ stroke: T.line }}
                       tickFormatter={isRevenue ? _fmtRupeesFull : fmtIN}
                       allowDecimals={false}
                       width={isRevenue ? 72 : 36} />
                <RechartsTooltip
                  content={<_AnalyticsTooltip
                    unit={isRevenue ? "rupees" : "count"}
                    seriesLabel={seriesLabel}
                    seriesColor={seriesColor} />} />
                <Line type="monotone"
                      dataKey={dataKey}
                      stroke={seriesColor}
                      strokeWidth={2}
                      dot={{ r: 2.5, fill: seriesColor, stroke: seriesColor }}
                      activeDot={{ r: 5, fill: seriesColor, stroke: "white", strokeWidth: 2 }}
                      isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center",
                          height: "100%", color: T.muted, fontSize: 12 }}>
              No data in this range.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** "Refunded application ids" from the payments feed — the ONLY source of
 *  refund truth, so Dashboard, Members and Payments never disagree. */
function _refundedAppIdsOf(payments) {
  const ids = new Set();
  payments.forEach((p) => {
    if ((p.status || "").toLowerCase() === "refunded" && p.application_id) ids.add(p.application_id);
  });
  return ids;
}

/** The nine Dashboard metrics for ONE Applied-Date cohort (`rows` are the
 *  already range-filtered, non-archived members). Shared by the Dashboard
 *  cards and the Dashboard export so both always compute identically.
 *   • Counts are mutually exclusive by current Review Status.
 *   • Gross Revenue = every successful payment (paid + refunded).
 *   • Refunded / To Be Refunded = Rejected OR Removed members whose money
 *     was / must be returned, attributed to their Applied Date cohort.
 *   • Net Revenue = Gross − Refunded − To Be Refunded. */
function _cohortStatsOf(rows, refundedAppIds) {
  const displayPay = (r) => refundedAppIds.has(r.application_id)
    ? "refunded"
    : ((r.payment_status || "").toLowerCase() || "unpaid");
  const amt = (r) => adminDisplayMembershipAmount(r.amount_paise) || 0;
  const sumAmt = (list) => list.reduce((s, r) => s + amt(r), 0);

  const applicationsReceived = rows.length;
  const reviewPending = rows.filter((r) => r.status === "pending_review").length;
  const onboarded     = rows.filter((r) => r.status === "onboarded").length;
  const rejected      = rows.filter((r) => r.status === "rejected").length;
  const removed       = rows.filter((r) => r.status === "removed").length;

  const totalRevenuePaise = sumAmt(rows.filter((r) => PAID_LIKE.has(displayPay(r))));
  // Financial treatment comes from Payment Status only — never from Review Status.
  const refundedPaise     = sumAmt(rows.filter((r) => displayPay(r) === "refunded"));
  const toBeRefundedPaise = sumAmt(rows.filter((r) => displayPay(r) === "to_be_refunded"));
  const netPaise = Math.max(0, totalRevenuePaise - refundedPaise - toBeRefundedPaise);

  return { applicationsReceived, reviewPending, onboarded, rejected, removed,
           totalRevenuePaise, refundedPaise, toBeRefundedPaise, netPaise };
}

const _MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
/** "YYYY-MM-DD" → "DD/MM/YY" (export date format — never US ordering). */
function _ymdToDMY2(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s || ""));
  return m ? `${m[3]}/${m[2]}/${m[1].slice(2)}` : String(s || "");
}

/** Date-wise Dashboard export rows (earliest → latest) for the current
 *  Dashboard filters. One row per day / calendar week / calendar month in
 *  the selected range, every bucket emitted even when empty. All Time spans
 *  the earliest dated cohort through today; undated historical members are
 *  never given a date — they land in one trailing "Undated" row so the
 *  export still reconciles with the All Time Dashboard cards. */
function _dashboardExportRows({ members, payments, range, granularity }) {
  const refundedAppIds = _refundedAppIdsOf(payments);
  const isAllTime = range.preset === "all_time";
  const dated = [];
  const undated = [];
  members.filter((r) => !r.is_archived).forEach((r) => {
    const d = _memberDateOf(r);
    if (!d) { if (isAllTime) undated.push(r); return; }
    if (inRange(d, range)) dated.push({ r, d });
  });
  const to = range.to;
  const from = isAllTime
    ? (dated.length ? dated.reduce((min, x) => (x.d < min ? x.d : min), dated[0].d) : "")
    : range.from;

  const buckets = [];
  let keyOf = (d) => d;
  if (from) {
    if (granularity === "weekly") {
      keyOf = _mondayOfWeek;
      for (let s = _mondayOfWeek(from); s <= to; s = _addDays(s, 7)) {
        const e = _addDays(s, 6);
        buckets.push({ key: s, label: `${_ymdToDMY2(s < from ? from : s)} – ${_ymdToDMY2(e > to ? to : e)}`, rows: [] });
      }
    } else if (granularity === "monthly") {
      keyOf = (d) => d.slice(0, 7);
      for (let s = _firstOfMonth(from); s <= to; s = _addDays(_lastOfMonth(s), 1)) {
        buckets.push({ key: s.slice(0, 7), label: `${_MONTH_ABBR[Number(s.slice(5, 7)) - 1]} ${s.slice(0, 4)}`, rows: [] });
      }
    } else {
      for (let d = from; d <= to; d = _addDays(d, 1)) buckets.push({ key: d, label: _ymdToDMY2(d), rows: [] });
    }
  }
  const byKey = new Map(buckets.map((b) => [b.key, b]));
  dated.forEach(({ r, d }) => { const b = byKey.get(keyOf(d)); if (b) b.rows.push(r); });
  if (undated.length) buckets.push({ key: "undated", label: "Undated (historical)", rows: undated });
  return buckets.map((b) => ({ label: b.label, ..._cohortStatsOf(b.rows, refundedAppIds) }));
}

const DASH_EXPORT_HEADERS = [
  "Date", "Applications Received", "Review Pending", "Onboarded", "Rejected", "Removed",
  "Gross Revenue", "Refunded", "To Be Refunded", "Net Revenue",
];
const _rs = (paise) => Math.round(Number(paise) || 0) / 100;

/** .xlsx with REAL formulas: B = SUM(C:F) per row, J = G−H−I per row. */
async function downloadDashboardXlsx(name, rows) {
  const XLSX = await import("xlsx");
  const data = [
    DASH_EXPORT_HEADERS,
    ...rows.map((r) => [r.label, null, r.reviewPending, r.onboarded, r.rejected, r.removed,
                        _rs(r.totalRevenuePaise), _rs(r.refundedPaise), _rs(r.toBeRefundedPaise), null]),
  ];
  const ws = XLSX.utils.aoa_to_sheet(data);
  rows.forEach((r, i) => {
    const n = i + 2;
    ws[`B${n}`] = { t: "n", f: `SUM(C${n}:F${n})`, v: r.applicationsReceived };
    ws[`J${n}`] = { t: "n", f: `G${n}-H${n}-I${n}`,
                    v: _rs(r.totalRevenuePaise - r.refundedPaise - r.toBeRefundedPaise) };
  });
  ws["!cols"] = [{ wpx: 150 }, { wpx: 150 }, { wpx: 110 }, { wpx: 100 }, { wpx: 90 }, { wpx: 90 },
                 { wpx: 120 }, { wpx: 100 }, { wpx: 120 }, { wpx: 120 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Dashboard");
  XLSX.writeFile(wb, name);
}

/** CSV twin — formulas are emitted as text so Excel / Sheets evaluate them on open. */
function _dashboardCsv(rows) {
  const q = (v) => `"${String(v).replace(/"/g, '""')}"`;
  const lines = [DASH_EXPORT_HEADERS.map(q).join(",")];
  rows.forEach((r, i) => {
    const n = i + 2;
    lines.push([q(r.label), `=SUM(C${n}:F${n})`, r.reviewPending, r.onboarded, r.rejected, r.removed,
                _rs(r.totalRevenuePaise), _rs(r.refundedPaise), _rs(r.toBeRefundedPaise), `=G${n}-H${n}-I${n}`].join(","));
  });
  return "\ufeff" + lines.join("\r\n");
}

const _GRANULARITIES = [
  { key: "daily", label: "Daily", hint: "One row per calendar date" },
  { key: "weekly", label: "Weekly", hint: "One row per calendar week" },
  { key: "monthly", label: "Monthly", hint: "One row per calendar month" },
];

/** Dashboard export dialog — format + time granularity, filters carry over. */
function DashboardExportModal({ onExport, onClose, rangeText, scopeText }) {
  const [fmt, setFmt] = useState("xlsx");
  const [gran, setGran] = useState("daily");
  const choice = (active) => ({
    display: "block", width: "100%", textAlign: "left", padding: "12px 14px", background: active ? T.brandTint : "white",
    border: active ? `2px solid ${T.brand}` : `1px solid ${T.line}`, borderRadius: 12, cursor: "pointer",
    fontFamily: "inherit", marginBottom: 8,
  });
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 90,
                  display: "flex", alignItems: "center", justifyContent: "center" }}
      data-testid="dash-export-picker" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: "white", borderRadius: 14, padding: 26, maxWidth: 480, width: "92%",
                 boxShadow: "0 32px 64px rgba(0,0,0,0.3)" }}>
        <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 4 }}>Export Dashboard</div>
        <div data-testid="dash-export-scope" style={{ fontSize: 12.5, color: T.muted, marginBottom: 18 }}>
          Date-wise breakdown of the 9 Dashboard metrics for <b>{rangeText}</b>{scopeText ? <> · {scopeText}</> : null}. Current Dashboard filters apply.
        </div>
        <div style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: T.slate, fontWeight: 700, marginBottom: 8 }}>Export format</div>
        <button data-testid="dash-export-fmt-xlsx" onClick={() => setFmt("xlsx")} style={choice(fmt === "xlsx")}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: T.ink }}>Excel (.xlsx)</div>
              <div style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>Live formulas for Applications Received and Net Revenue.</div>
            </div>
            <span style={{ padding: "3px 8px", background: T.brand, color: "white",
                           borderRadius: 999, fontSize: 10, fontWeight: 700, letterSpacing: "0.06em" }}>RECOMMENDED</span>
          </div>
        </button>
        <button data-testid="dash-export-fmt-csv" onClick={() => setFmt("csv")} style={choice(fmt === "csv")}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.ink }}>CSV (.csv)</div>
          <div style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>Universal plain-text — opens in any spreadsheet tool.</div>
        </button>
        <div style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: T.slate, fontWeight: 700, margin: "14px 0 8px" }}>Time granularity</div>
        <div data-testid="dash-export-granularity" style={{ display: "flex", gap: 8, marginBottom: 18 }}>
          {_GRANULARITIES.map((g) => {
            const active = gran === g.key;
            return (
              <button key={g.key} data-testid={`dash-export-gran-${g.key}`} onClick={() => setGran(g.key)}
                title={g.hint} aria-pressed={active}
                style={{ flex: 1, padding: "9px 10px", borderRadius: 999, fontFamily: "inherit", fontSize: 12.5, fontWeight: 600,
                         cursor: "pointer", background: active ? T.brand : "white", color: active ? "white" : T.slate,
                         border: `1px solid ${active ? T.brand : T.line}` }}>{g.label}</button>
            );
          })}
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button data-testid="dash-export-cancel" onClick={onClose}
            style={{ padding: "8px 14px", background: "white", color: T.slate,
                     border: `1px solid ${T.line}`, borderRadius: 8, cursor: "pointer",
                     fontFamily: "inherit", fontSize: 12 }}>Cancel</button>
          <button data-testid="dash-export-confirm" onClick={() => onExport(fmt, gran)}
            style={{ padding: "8px 16px", background: T.brand, color: "white", border: "none",
                     borderRadius: 8, cursor: "pointer", fontFamily: "inherit", fontSize: 12, fontWeight: 700 }}>
            Export {fmt === "xlsx" ? ".xlsx" : ".csv"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Dashboard({ api }) {
  const [membersAll, setMembers] = useState([]);    // paid memberships: pending_review + onboarded + rejected + removed
  const [mtFilter, setMtFilter] = useState(_readMtPref);
  // Membership Type scopes every Dashboard number + chart (by member_id).
  const members = useMemo(() => membersAll.filter((r) => _mtMatches(mtFilter, r.membership_type)), [membersAll, mtFilter]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  // Refresh tick — bumped by the global "data changed" broadcast so
  // Review Status / Payment Status edits performed elsewhere (e.g. the
  // Member Detail Drawer) re-fetch dashboard totals instantly.
  const [refreshTick, setRefreshTick] = useState(0);
  const forceRef = useRef(false);
  useEffect(() => {
    const onChange = (e) => {
      // Row-level mutations arrive via the store event; anything else
      // (bulk/import/refresh/poll) forces a silent background revalidate.
      if (e?.detail?.app_uuid) return;
      forceRef.current = true;
      setRefreshTick((n) => n + 1);
    };
    window.addEventListener(DATA_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(DATA_CHANGED_EVENT, onChange);
  }, []);
  // Repaint from the shared store whenever any page/mutation updates it.
  useEffect(() => {
    const onStore = () => {
      const c = cachedPair();
      if (c) { setMembers(c.m.rows || []); setPayments(c.p.rows || []); setLoading(false); }
    };
    window.addEventListener(STORE_EVENT, onStore);
    return () => window.removeEventListener(STORE_EVENT, onStore);
  }, []);
  const [range, setRange] = useGlobalDateRange(PREF_KEYS.defaultDateFilter);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Stale-While-Revalidate via the shared store: paint whatever is
      // cached immediately (never blank when data exists), then
      // revalidate silently only if the cache is stale or a refresh was
      // requested. Skeleton only on the very first load (cache miss).
      const force = forceRef.current; forceRef.current = false;
      const c = cachedPair();
      if (c) { setMembers(c.m.rows || []); setPayments(c.p.rows || []); setLoading(false); }
      else setLoading(true);
      setErr("");
      try {
        const { m, p } = await loadAdminData(api, { force });
        if (cancelled) return;
        setMembers(m.rows || []);
        setPayments(p.rows || []);
      } catch (e) { setErr(e?.response?.data?.detail || "Failed to load."); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [api, refreshTick]);

  const stats = useMemo(() => {
    // Archived records are excluded from every metric. The Review Status
    // Analytics filter is DELIBERATELY not applied here — the upper
    // Dashboard cards stay independent of the Analytics chart.
    const activeMembers = members.filter((r) => !r.is_archived);
    const refundedAppIds = _refundedAppIdsOf(payments);
    const s = _cohortStatsOf(activeMembers.filter((r) => _memberInRange(r, range)), refundedAppIds);
    return {
      ...s,
      // Legacy alias kept in case any other view still reads it.
      revenuePaise: s.totalRevenuePaise,
      refundPaise: s.refundedPaise,
    };
  }, [members, payments, range]);

  const [exportOpen, setExportOpen] = useState(false);
  const doDashboardExport = (fmt, granularity) => {
    const rows = _dashboardExportRows({ members, payments, range, granularity });
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:.]/g, "-");
    const name = `hfa-dashboard-${granularity}-${stamp}`;
    if (fmt === "xlsx") downloadDashboardXlsx(`${name}.xlsx`, rows);
    else downloadCsv(`${name}.csv`, _dashboardCsv(rows));
    setExportOpen(false);
  };

  const activeChip = useMemo(() => activeDateChipFor(range), [range]);

  // Width of the City Chapters filter box → the note beneath it shares its left edge.
  /** Drill-down helper — stash the exact Dashboard filter context (date
   *  preset + review status) so Members opens with the same records. */
  const drill = (review, payment_filter) => () => {
    _stashMembersDeeplink({
      date_preset: range.preset,
      date_from: range.from,
      date_to: range.to,
      review,
      mt: mtFilter,
      ...(payment_filter ? { payment_filter } : {}),
    });
    window.location.hash = "#/members";
  };

  return (
    <div>
      {/* Header row */}
      <div className="hfa-list-header hfa-list-header-row1" data-dash-header
           style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between",
                    alignItems: "flex-start", gap: 12, marginBottom: 0 }}>
        <div>
          <h1 style={{ fontSize: 34, margin: 0, letterSpacing: "-0.035em",
                       fontWeight: 800, color: T.ink }}>Dashboard</h1>
          {/* Same premium two-line active-date chip as Members and
              Payments. Renders the Quick Date preset name plus the
              exact range in DD/MM/YYYY; collapses cleanly when no
              filter is active. */}
          {activeChip && (
            <div data-testid="dash-active-date"
                 style={{ marginTop: 8, marginBottom: 4,
                          display: "flex", flexDirection: "column",
                          gap: 3, lineHeight: 1.2 }}>
              <span style={{ fontSize: 13, letterSpacing: "0.08em",
                             textTransform: "uppercase", color: T.brand,
                             fontWeight: 700 }}>{activeChip.label}</span>
              {activeChip.isSingleDay ? (
                // Single calendar date → wrap the DD/MM/YYYY label
                // with ‹ / › arrows for one-tap day-by-day navigation.
                // Uses the shared _shiftSingleDay helper so the
                // active preset flips to "custom" and stays there
                // through subsequent taps (spec §3).
                <SingleDayArrows
                  ymd={activeChip.singleDay}
                  label={activeChip.range}
                  testidPrefix="dash-date-nav"
                  onShift={(delta) => setRange((r) => _shiftSingleDay(r, delta))}
                />
              ) : (
                <span style={{ fontSize: 15, color: T.ink, fontWeight: 600,
                               fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>{activeChip.range}</span>
              )}
            </div>
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
          <DateRangePicker value={range} onChange={setRange} />
          {/* Second layer — Refresh → Clear all filters → Membership
              Type on one right-aligned line directly beneath the
              Quick Date chips. Same RefreshIconButton as Members /
              Payments: bumps `refreshTick` and broadcasts
              `hfa:data-changed` so other mounted admin surfaces
              refetch in the same tick. */}
          <div data-testid="dash-actions-row"
               style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center",
                        justifyContent: "flex-end" }}>
            <RefreshIconButton
              testid="dash-refresh"
              onClick={() => {
                setRefreshTick((n) => n + 1);
                try { broadcastDataChanged({ kind: "dashboard_refresh" }); } catch (_) {}
              }} />
            <button data-testid="btn-clear-filters" data-hfa-btn
              onClick={() => {
                setMtFilter(_readMtPref());
                // Restore this page's *configured* default (Settings →
                // Default Date Filter), never a hardcoded value. This
                // ensures an admin who prefers "Today" or "Last 30 Days"
                // gets it back on clear instead of being forced to
                // "This Month". The Analytics Review Status filter is
                // owned by `AnalyticsCharts` and resets independently
                // (see `_ANALYTICS_RESET_EVENT` dispatched below).
                setRange(TODAY_RANGE());
                try { window.dispatchEvent(new CustomEvent(_ANALYTICS_RESET_EVENT)); } catch (_) {}
              }}
              title="Reset the quick date filter to your configured default. Records are never affected."
              style={{ padding: "5px 12px", fontSize: 11, color: T.brand,
                       background: "white", border: `1px solid ${T.brand}`,
                       borderRadius: 999, cursor: "pointer", fontFamily: "inherit",
                       fontWeight: 600, letterSpacing: "0.02em" }}>
              Clear all filters
            </button>
            <button data-testid="dash-export" data-hfa-btn
              disabled={loading}
              onClick={() => setExportOpen(true)}
              title="Export the Dashboard metrics as a date-wise Excel or CSV file using the current filters"
              style={{ padding: "5px 12px", fontSize: 11, color: loading ? T.muted : T.brand,
                       background: loading ? "#F4F4F5" : "white", border: `1px solid ${loading ? T.line : T.brand}`,
                       borderRadius: 999, cursor: loading ? "not-allowed" : "pointer", fontFamily: "inherit",
                       fontWeight: 600, letterSpacing: "0.02em" }}>
              Export
            </button>
            <div data-hfa-filter-card-wrap style={{ display: "inline-flex" }}>
              <MembershipTypeFilter value={mtFilter} onChange={setMtFilter} testid="dash-mt-toggle" />
            </div>
          </div>
          <div data-testid="dash-cohort-note" className="hfa-dash-note"
               style={{ fontSize: 11, color: T.muted, textAlign: "right", lineHeight: 1.45,
                        whiteSpace: "nowrap", maxWidth: "100%", marginLeft: "auto", marginTop: 8 }}>
            <span aria-hidden="true" style={{ color: T.brand, fontWeight: 700, marginRight: 4 }}>*</span>
            Members and revenue figures are based on applied date.
          </div>
        </div>
      </div>

      {exportOpen && (
        <DashboardExportModal
          rangeText={activeChip ? `${activeChip.label} (${activeChip.range})` : "the selected range"}
          scopeText={mtFilter && mtFilter !== "all" ? `City Chapters: ${mtFilter === "single" ? "Single" : "Multiple"}` : ""}
          onExport={doDashboardExport}
          onClose={() => setExportOpen(false)} />
      )}

      {err && <div style={{ color: T.danger, marginTop: 16 }}>{err}</div>}
      {loading && <div style={{ color: T.muted, marginTop: 16, fontSize: 13 }}>Loading…</div>}

      {!loading && !err && (
        <>
          {/* Members section — workflow states in the order the member
              travels through them (Review Pending → Onboarded/Rejected →
              Removed). "Total Members" is deliberately omitted because
              summing across workflow stages is not a meaningful KPI. */}
          <div className="hfa-dash-stats">
          <CohortBreakdown title="Members"
                parent={{ label: "Applications Received", value: fmtIN(stats.applicationsReceived), testid: "dash-applications-received", role: "parent",
                          onDrillDown: drill("all", ["all"]) }}
                children={[
                  { label: "Review Pending", value: fmtIN(stats.reviewPending), testid: "dash-review-pending", onDrillDown: drill("pending_review", ["paid"]) },
                  { label: "Onboarded",      value: fmtIN(stats.onboarded),     testid: "dash-onboarded",      onDrillDown: drill("onboarded", ["paid"]) },
                  { label: "Rejected",       value: fmtIN(stats.rejected),      testid: "dash-rejected",       onDrillDown: drill("rejected", ["refunded", "to_be_refunded"]) },
                  { label: "Removed",        value: fmtIN(stats.removed),       testid: "dash-removed",        onDrillDown: drill("removed", ["refunded", "to_be_refunded", "no_refund"]) },
                ]} />

          {/* Revenue section — every card carries an Eye drill-down
              that opens Members with the exact payment-status subset
              that produced the metric (Revenue = paid+refunded, Refunds
              = refunded only, Net Revenue = paid only). Dashboard's
              current date preset is preserved. */}
          {/* Revenue section — four cards separating money received
              from refund operations. Gross Revenue and Net Revenue are
              date-basis'd on Payment Date. Refunded and To Be Refunded
              are date-basis'd on Rejected Date so admins can see how
              much refund workload originated in the selected window.
              Every card supports drill-down into the exact underlying
              records. Archived applications are excluded upstream in
              the `stats` memo. */}
          <SectionHeader title="Revenue" mark badge style={{ marginTop: 32, textAlign: "center" }} />
          {(() => {
            const revDrill = (opts) => () => {
              _stashMembersDeeplink({
                date_preset: range.preset,
                date_from: range.from,
                date_to: range.to,
                mt: mtFilter,
                ...opts,
              });
              window.location.hash = "#/members";
            };
            return (
              <MetricGrid cards={[
                // Gross Revenue — every member in the cohort, all payment statuses.
                { label: "Gross Revenue", value: rupeesOrZero(stats.totalRevenuePaise),
                  testid: "dash-total-revenue",
                  onDrillDown: revDrill({ review: ["all"], payment_filter: ["all"] }) },
                // Refunded — Rejected/Removed members whose Payment Status is Refunded.
                { label: "Refunded", value: rupeesOrZero(stats.refundedPaise),
                  testid: "dash-refunded",
                  onDrillDown: revDrill({
                    review: ["rejected", "removed"],
                    payment_filter: ["refunded"],
                  }) },
                // To Be Refunded — Rejected/Removed members whose Payment Status is
                // explicitly "To Be Refunded" (never inferred from Review Status).
                { label: "To Be Refunded", value: rupeesOrZero(stats.toBeRefundedPaise),
                  testid: "dash-to-be-refunded",
                  onDrillDown: revDrill({
                    review: ["rejected", "removed"],
                    payment_filter: ["to_be_refunded"],
                  }) },
                // Net Revenue — money HFA has actually retained.
                // Excludes both completed refunds and refund obligations.
                { label: "Net Revenue", value: rupeesOrZero(stats.netPaise),
                  testid: "dash-net-revenue", accent: "green",
                  onDrillDown: revDrill({ payment_filter: ["paid", "no_refund"] }) },
              ]} />
            );
          })()}
          </div>

          {/* Analytics — one unified chart with a metric switcher
              (Members / Revenue) plus its own Review Status filter
              that scopes the chart only. Fully self-contained: the
              upper Dashboard cards above are entirely independent of
              these filters. Daily granularity preserved; anchored to
              `paid_at`; archived rows excluded upstream. */}
          <AnalyticsCharts members={members} payments={payments} range={range} />
        </>
      )}
    </div>
  );
}

function SectionHeader({ title, style, mark, badge }) {
  const label = <>{title}{mark && <sup aria-hidden="true" style={{ color: T.brand, fontSize: 11, marginLeft: 2, letterSpacing: 0 }}>*</sup>}</>;
  return (
    <h2 style={{
      fontSize: 13, letterSpacing: "0.12em", textTransform: "uppercase",
      color: T.slate, fontWeight: 700, margin: "28px 0 14px", ...style,
    }}>{badge ? (
      <span data-testid={`section-badge-${String(title).toLowerCase()}`}
        style={{ display: "inline-flex", alignItems: "center", justifyContent: "center",
                 padding: "4px 14px", borderRadius: 8, background: "#FFF7ED", color: T.brand,
                 border: "1px solid rgba(234,88,12,0.18)", lineHeight: 1.3 }}>{label}</span>
    ) : label}</h2>
  );
}

/** Dashboard → Members: the Applications Received cohort card centred on
 *  top (same size as one status card), four static Bézier connectors, then
 *  its complete status breakdown in one row. On wide desktops the parent
 *  card lifts into the empty centre of the header zone (beside the section
 *  label) so the whole section uses less vertical space. Pure layout —
 *  numbers and drill-downs come from the caller. */
function CohortBreakdown({ title, parent, children }) {
  const wrapRef = useRef(null);
  const parentRef = useRef(null);
  const [w, setW] = useState(0);
  const [parentH, setParentH] = useState(0);
  // Lift tiers: ≥1536px the heading sits just under the quick-date chips (48px
  // from the header top, beside the action buttons); 1340–1535px the card
  // starts right below the action-button row (116px) so it never collides
  // with Refresh / Clear / Export / City Chapters. Below 1340px: stacked flow.
  const liftTopFor = () => {
    if (typeof window === "undefined") return 0;
    if (window.matchMedia("(min-width: 1536px)").matches) return 48;
    if (window.matchMedia("(min-width: 1340px)").matches) return 116;
    return 0;
  };
  const [liftTop, setLiftTop] = useState(liftTopFor);
  const lift = liftTop > 0;
  useEffect(() => {
    const mqs = [window.matchMedia("(min-width: 1536px)"), window.matchMedia("(min-width: 1340px)")];
    const onChange = () => setLiftTop(liftTopFor());
    mqs.forEach((m) => m.addEventListener("change", onChange));
    return () => mqs.forEach((m) => m.removeEventListener("change", onChange));
  }, []);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setW(entry.contentRect.width));
    ro.observe(el);
    setW(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);
  useEffect(() => {
    const el = parentRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setParentH(entry.contentRect.height));
    ro.observe(el);
    setParentH(el.getBoundingClientRect().height);
    return () => ro.disconnect();
  }, [lift]);
  // Header height → lift the section so the heading sits 48px below the header top
  // (just under the quick-date chips) regardless of how tall the header is.
  const [headerH, setHeaderH] = useState(0);
  useEffect(() => {
    const el = document.querySelector("[data-dash-header]");
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setHeaderH(entry.contentRect.height));
    ro.observe(el);
    setHeaderH(el.getBoundingClientRect().height);
    return () => ro.disconnect();
  }, []);
  const H = 56;
  const n = children.length;
  const gap = 12;
  const colW = w > 0 ? (w - gap * (n - 1)) / n : 0;
  const targets = Array.from({ length: n }, (_, i) => i * (colW + gap) + colW / 2);
  const cx = w / 2;
  const parentCard = <MetricGrid compact cards={[parent]} />;
  const parentW = colW ? Math.max(272, Math.round(colW * 0.8)) : undefined;
  const liftMargin = lift && headerH ? -(headerH - liftTop) : 0;
  return (
    <div data-testid="dash-cohort-breakdown" style={{ marginTop: liftMargin, pointerEvents: lift ? "none" : undefined }}>
      {lift ? (
        <div style={{ position: "relative", minHeight: parentH || undefined }}>
          <div ref={parentRef} className="hfa-cohort-parent"
               style={{ position: "absolute", top: 0, left: "50%", transform: "translateX(-50%)",
                        width: parentW || 320, pointerEvents: "auto" }}>
            <SectionHeader title={title} mark badge style={{ margin: "0 0 8px", textAlign: "center" }} />
            {parentCard}
          </div>
        </div>
      ) : (
        <>
          <SectionHeader title={title} mark badge style={{ marginTop: 0, marginBottom: 10, textAlign: "center" }} />
          <div ref={parentRef} className="hfa-cohort-parent"
               style={{ width: parentW, maxWidth: parentW || 440, margin: "0 auto" }}>
            {parentCard}
          </div>
        </>
      )}
      <div ref={wrapRef} style={{ position: "relative", pointerEvents: "auto" }}>
        {w > 0 && (
          <svg className="hfa-cohort-connectors" data-testid="dash-cohort-connectors" width={w} height={H}
               viewBox={`0 0 ${w} ${H}`} style={{ display: "block" }} aria-hidden="true">
            <defs>
              <marker id="hfa-cohort-arrow" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto" markerUnits="userSpaceOnUse">
                <path d="M0,0.5 L7,4 L0,7.5" fill="none" stroke="#EA580C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </marker>
            </defs>
            {targets.map((tx, i) => {
              const sx = cx + (tx - cx) * 0.12;      // fan out gently from the parent's underside
              const d = `M ${sx} 2 C ${sx} ${H * 0.5}, ${tx} ${H * 0.5}, ${tx} ${H - 9}`;
              return <path key={i} d={d} fill="none" stroke="#EA580C" strokeWidth="1.5" strokeOpacity="0.55"
                           strokeLinecap="round" markerEnd="url(#hfa-cohort-arrow)" />;
            })}
          </svg>
        )}
        <div className="hfa-cohort-children" style={{ display: "grid", gap, gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))` }}>
          {children.map((c) => <MetricGrid key={c.testid} compact cards={[c]} />)}
        </div>
      </div>
    </div>
  );
}

function MetricGrid({ cards, compact = false }) {
  return (
    <div className="hfa-metric-grid" style={{ display: "grid", gap: compact ? 12 : 14,
                  gridTemplateColumns: `repeat(auto-fit, minmax(${compact ? 180 : 220}px, 1fr))` }}>
      {cards.map((k) => {
        // Two accent flavours:
        //   accent: "green" → premium green background (Net Revenue).
        //   accent: true    → orange-tinted border, matches prior style.
        const isGreen = k.accent === "green";
        return (
        <div key={k.label} data-testid={k.testid}
          className="hfa-metric-card"
          data-accent={isGreen ? "green" : "orange"}
          data-role={k.role || undefined}
          style={{
            /* Colors, border, shadow and transition are owned by the
               `.hfa-metric-card` CSS block (see App-level <style>).
               Inline styles here handle only layout so the hover
               animation can drive every visual property smoothly. */
            borderStyle: "solid",
            borderWidth: 1,
            borderRadius: 16,
            padding: compact ? "16px 18px" : "20px 22px",
            position: "relative",
          }}>
          {/* Optional drill-down affordance (eye icon in the top-right, or an
              "i" for cohort-wide views). Cards that provide `onDrillDown`
              show it; others render clean. Clicking it hands the current
              Dashboard filter context to the Members page via sessionStorage
              so admins see exactly the same records behind this number. */}
          {k.onDrillDown && (
            <button data-testid={`${k.testid}-drill`}
              onClick={(e) => { e.stopPropagation(); k.onDrillDown(); }}
              title="View these members with the current date filter"
              aria-label={`View ${k.label} members`}
              style={{ position: "absolute", top: 12, right: 12,
                       width: 28, height: 28, display: "inline-flex",
                       alignItems: "center", justifyContent: "center",
                       border: `1px solid ${T.line}`, borderRadius: 8,
                       background: "white", cursor: "pointer",
                       color: T.slate, fontFamily: "inherit", padding: 0,
                       transition: "background 120ms ease, border-color 120ms ease, color 120ms ease" }}
              onMouseEnter={(e) => { e.currentTarget.style.background = T.brandSoft; e.currentTarget.style.borderColor = T.brand; e.currentTarget.style.color = T.brand; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "white"; e.currentTarget.style.borderColor = T.line; e.currentTarget.style.color = T.slate; }}>
              {/* Simple inline SVG eye — no lucide dependency needed. */}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                strokeLinejoin="round" aria-hidden="true">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            </button>
          )}
          <div style={{ fontSize: compact ? 11.5 : 12.5, color: isGreen ? "#166534" : "#7C2D12", textTransform: "uppercase",
                        letterSpacing: "0.12em", fontWeight: 700, lineHeight: 1.2, textAlign: "center",
                        padding: k.onDrillDown ? "0 30px" : 0 }}>{k.label}</div>
          <div style={{ fontSize: 24, fontWeight: 800, marginTop: compact ? 8 : 10,
                        color: T.ink, letterSpacing: "-0.025em", textAlign: "center",
                        fontVariantNumeric: "tabular-nums" }}>{k.value}</div>
        </div>
        );
      })}
    </div>
  );
}

/** SessionStorage key + reader/writer used to hand off the Dashboard's
 *  current filter context (date preset + review status) to the Members
 *  page when the admin clicks an Eye icon on a workflow card. */
const MEMBERS_DEEPLINK_KEY = "hfa_admin_members_deeplink";
function _stashMembersDeeplink(payload) {
  try { sessionStorage.setItem(MEMBERS_DEEPLINK_KEY, JSON.stringify(payload || {})); }
  catch (_) { /* noop */ }
}
function _consumeMembersDeeplink() {
  try {
    const raw = sessionStorage.getItem(MEMBERS_DEEPLINK_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(MEMBERS_DEEPLINK_KEY);
    return JSON.parse(raw);
  } catch (_) { return null; }
}

/* =====================================================================
 * Members table  (also reused by Exceptions page — different endpoint)
 * ===================================================================== */
/* Column layout — order + headings match the /apply form exactly, then payment + admin fields at the end.
 * Every applicant answer is present. Underscores never surface to the UI.
 * `w` sets a comfortable minWidth; description columns get much more room. */
/** Admin-only Star Member glyph: hollow outline (No) or filled gold (Yes). */
function StarGlyph({ filled = false, size = 16, style }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true"
      fill={filled ? "#F5B301" : "none"} stroke={filled ? "#D99A00" : "#9CA3AF"} strokeWidth={1.8}
      strokeLinejoin="round" style={{ display: "inline-block", flexShrink: 0, ...style }}>
      <path d="M12 2.8l2.9 6.1 6.7.8-4.9 4.6 1.3 6.6L12 17.6 6 20.9l1.3-6.6L2.4 9.7l6.7-.8L12 2.8z" />
    </svg>
  );
}

/* ---------- Column sort descriptors (Members: 7 sortable columns) ------
 * `first` is the direction applied on the first click; `value` turns a
 * row into a comparable primitive (null = empty, always sorted last). */
const _dateMs = (v) => {
  if (!v) return null;
  const s = String(v);
  const t = Date.parse(/^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s}T00:00:00+05:30` : s);
  return Number.isNaN(t) ? null : t;
};
const _num = (v) => (v === null || v === undefined || v === "" || Number.isNaN(Number(v)) ? null : Number(v));
const _clockMs = (clock) => {
  const m = /^\s*(\d{1,2}):(\d{2})(?::(\d{2}))?/.exec(String(clock || ""));
  if (!m) return null;
  return ((parseInt(m[1], 10) % 24) * 3600 + parseInt(m[2], 10) * 60 + (m[3] ? parseInt(m[3], 10) : 0)) * 1000;
};
/** Default Members ordering: Applied Date (latest first) then Applied
 *  Time (latest first). Rows without an Applied Date fall back to their
 *  payment / creation instant so they still sit in time order. */
const _appliedInstant = (r) => {
  const d = _dateMs(r.applied_date);
  if (d !== null) return d + (_clockMs(r.applied_time) ?? 0);
  return _dateMs(r.paid_at) ?? _dateMs(r.created_at) ?? _dateMs(r.historical_sort_key);
};
const SORT_TEXT   = { kind: "text",   first: "asc",  dirs: { asc: "A–Z", desc: "Z–A" },
                      value: (r, k) => (r[k] ? String(r[k]).trim().toLowerCase() : null) || null };
const SORT_DATE   = { kind: "date",   first: "desc", dirs: { desc: "New to Old", asc: "Old to New" },
                      value: (r, k) => _dateMs(r[k]) };
const SORT_TIME   = { kind: "number", first: "desc", dirs: { desc: "New to Old", asc: "Old to New" },
                      value: (r) => _clockMs(r.applied_time) };
const SORT_AMOUNT = { kind: "number", first: "desc", dirs: { desc: "High to Low", asc: "Low to High" },
                      value: (r) => _num(adminDisplayMembershipAmount(r.amount_paise)) };
const SORT_AGE    = { kind: "number", first: "asc",  dirs: { asc: "Low to High", desc: "High to Low" },
                      value: (r) => _num(r.age) };
const _sortTip = (sd) => `${sd.dirs[sd.first]} / ${sd.dirs[sd.first === "asc" ? "desc" : "asc"]}`;

/** Applied Date + Applied Time are one linked pair: Time never sorts on
 *  its own, and whichever of the two is picked pulls the other in right
 *  next to it (Date immediately before Time). Missing partner defaults
 *  to "latest first". Other entries keep their relative order. */
function _linkAppliedPair(list) {
  const hasDate = list.some((u) => u.key === "applied_date");
  const hasTime = list.some((u) => u.key === "applied_time");
  if (!hasDate && !hasTime) return list;
  const date = list.find((u) => u.key === "applied_date") || { key: "applied_date", dir: "desc" };
  const time = list.find((u) => u.key === "applied_time") || { key: "applied_time", dir: "desc" };
  const anchor = list.findIndex((u) => u.key === (hasDate ? "applied_date" : "applied_time"));
  const rest = list.filter((u) => u.key !== "applied_date" && u.key !== "applied_time");
  const before = list.slice(0, anchor).filter((u) => u.key !== "applied_date" && u.key !== "applied_time");
  return [...before, date, time, ...rest.slice(before.length)];
}

function _sortRowsMulti(rows, specs) {
  const keyed = rows.map((r, i) => ({ r, i, v: specs.map((s) => s.col.sortable.value(r, s.col.key)) }));
  keyed.sort((a, b) => {
    for (let k = 0; k < specs.length; k++) {
      const av = a.v[k], bv = b.v[k];
      if (av === null && bv === null) continue;
      if (av === null) return 1;
      if (bv === null) return -1;
      const c = typeof av === "number" ? av - bv : av.localeCompare(bv);
      if (c) return c * (specs[k].dir === "asc" ? 1 : -1);
    }
    return a.i - b.i;
  });
  return keyed.map((k) => k.r);
}

/** Compact circular ▲▼ toggle shown in sortable column headers. */
function SortToggle({ colKey, label, sortable, active, dir, onToggle, priority = null }) {
  const [hover, setHover] = useState(null);
  const ref = useRef(null);
  const show = () => {
    const b = ref.current?.getBoundingClientRect();
    if (b) setHover({ x: b.left + b.width / 2, y: b.bottom + 6 });
  };
  const upOn = active && dir === "asc", downOn = active && dir === "desc";
  const order = [sortable.first, sortable.first === "asc" ? "desc" : "asc"];
  return (
    <>
      <button type="button" ref={ref} data-testid={`col-sort-${colKey}`}
        data-sort-dir={active ? dir : "none"} aria-label={`Sort ${label}: ${_sortTip(sortable)}`}
        aria-pressed={active}
        draggable={false}
        onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
        onMouseEnter={show} onMouseLeave={() => setHover(null)}
        onFocus={show} onBlur={() => setHover(null)}
        style={{ width: 18, height: 18, marginLeft: 6, borderRadius: 999, verticalAlign: "-4px",
                 display: "inline-flex", alignItems: "center", justifyContent: "center", padding: 0,
                 cursor: "pointer", fontFamily: "inherit", flex: "0 0 auto",
                 background: active ? "#FFF7ED" : "rgba(255,255,255,0.08)",
                 border: `1px solid ${active ? T.brand : "rgba(255,255,255,0.38)"}`,
                 color: active ? T.brand : "rgba(255,255,255,0.85)",
                 transition: "background-color 120ms ease, border-color 120ms ease" }}>
        <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
          <path d="M5 1 L8 4.4 H2 Z" fill="currentColor" opacity={active ? (upOn ? 1 : 0.28) : 0.9} />
          <path d="M5 9 L2 5.6 H8 Z" fill="currentColor" opacity={active ? (downOn ? 1 : 0.28) : 0.9} />
        </svg>
      </button>
      {priority !== undefined && (
        <span data-testid={priority ? `col-sort-priority-${colKey}` : undefined}
          aria-label={priority ? `Sort priority ${priority}` : undefined} aria-hidden={!priority}
          style={{ display: "inline-flex", alignItems: "center", justifyContent: "center",
                   width: 16, height: 13, marginLeft: 3, borderRadius: 999, flex: "0 0 auto",
                   background: priority ? T.brand : "transparent", color: "white",
                   fontSize: 8.5, fontWeight: 800, lineHeight: 1, letterSpacing: 0, verticalAlign: "-2px",
                   visibility: priority ? "visible" : "hidden" }}>{priority || ""}</span>
      )}
      {hover && createPortal(
        <div role="tooltip" data-testid={`col-sort-tip-${colKey}`}
          style={{ position: "fixed", left: hover.x, top: hover.y, transform: "translateX(-50%)",
                   zIndex: 2147483000, pointerEvents: "none", background: T.ink, color: "white",
                   fontSize: 11, lineHeight: 1.35, fontWeight: 500, letterSpacing: 0, textTransform: "none",
                   padding: "6px 8px", borderRadius: 8, whiteSpace: "nowrap",
                   boxShadow: "0 8px 24px rgba(0,0,0,0.22)", fontFamily: font }}>
          <div style={{ fontWeight: 700, marginBottom: 3, padding: "0 2px" }}>{label}</div>
          {order.map((d) => {
            const on = active && dir === d;
            return (
              <div key={d} data-testid={`col-sort-tip-${colKey}-${d}`} data-active={on ? "true" : "false"}
                style={{ padding: "2px 6px", borderRadius: 5, marginTop: 1,
                         color: on ? "#FB923C" : "white", fontWeight: on ? 700 : 500,
                         background: on ? "rgba(249,115,22,0.16)" : "transparent" }}>
                {sortable.dirs[d]}
              </div>
            );
          })}
        </div>, document.body)}
    </>
  );
}

const MEMBER_COLS = [
  { key: "membership_id",           label: "Member ID",          w: 150, render: (r) => <CopyCell value={r.membership_id} /> },
  { key: "applied_date",            label: "Applied Date",       w: 120, sortable: SORT_DATE, render: (r) => {
    const v = fmtDate(r.applied_date);
    return v === "—" ? <span style={{ color: T.muted }}>—</span> : <span style={{ display: "inline-flex", alignItems: "center" }}><span>{v}</span><CopyIcon value={v} /></span>;
  } },
  { key: "applied_time",            label: "Applied Time",       w: 110, sortable: SORT_TIME, render: (r) => {
    // Reformat the legacy IST-clock string into `hh:mm AM/PM`. Reuses
    // the portal-wide central helper so this cell can never drift out
    // of format with Payment Time / drawer timestamps.
    const v = _fmtClockToAmPm(r.applied_time);
    return !v ? <span style={{ color: T.muted }}>—</span> : <span style={{ display: "inline-flex", alignItems: "center" }}><span>{v}</span><CopyIcon value={v} /></span>;
  } },
  { key: "full_name",               label: "Full Name",          w: 180, sortable: SORT_TEXT, render: (r) => (
    <span style={{ display: "inline-flex", alignItems: "center" }}>
      <span style={{ fontWeight: 600 }}>{r.full_name || "—"}</span>
      <CopyIcon value={r.full_name} />
    </span>
  ) },
  { key: "gender",                  label: "Gender",             w: 90 },
  { key: "star_member",             label: "Star Member", headerIcon: "star", w: 150, render: (r) => (
    <span data-testid={`cell-star-member-${r.id}`} style={{ fontWeight: r.star_member === "Yes" ? 600 : 400 }}>{r.star_member || "No"}</span>
  ) },
  { key: "date_of_birth",           label: "Date of Birth",      w: 120 },
  { key: "age",                     label: "Age",                w: 112, sortable: SORT_AGE, render: (r) => (
    <span style={{ display: "inline-flex", alignItems: "center" }}>
      <span>{r.age ?? "—"}</span>
      <CopyIcon value={r.age} />
    </span>
  ) },
  { key: "whatsapp_number",         label: "WhatsApp",           w: 140, render: (r) => <CopyCell value={r.whatsapp_number} href={r.whatsapp_number ? `https://wa.me/91${r.whatsapp_number.replace(/\D/g, "")}` : ""} /> },
  { key: "current_city",            label: "Current City",       w: 130 },
  { key: "city_chapter",            label: "City Chapter",       w: 130, sortable: SORT_TEXT },
  // Annual / Lifetime — set only by the historical Excel import; blank for every /apply record.
  { key: "membership_plan",         label: "Membership Type",    w: 140, render: (r) => <CopyCell value={r.membership_plan} testid={`cell-membership-plan-${r.id}`} /> },
  // Website / Imported Lifetime Members / Imported Annual Members — derived from import batch, never stored on existing records.
  { key: "source",                  label: "Source",             w: 200, render: (r) => <CopyCell value={r.source} testid={`cell-source-${r.id}`} /> },
  { key: "email",                   label: "Email",              w: 220, render: (r) => <CopyCell value={r.email} href={_mailtoOf(r.email)} /> },
  { key: "linkedin_url",            label: "LinkedIn",           w: 200, render: (r) => <CopyCell value={r.linkedin_url} href={_linkedinUrlOf(r.linkedin_url)} /> },
  { key: "instagram_username",      label: "Instagram",          w: 160, render: (r) => <CopyCell value={r.instagram_username} href={_instagramUrlOf(r.instagram_username)} /> },
  { key: "professional_type",       label: "Professional Type",  w: 160 },
  { key: "professional_type_other", label: "Other Profession",   w: 160 },
  { key: "primary_circle",          label: "Primary Circle",     w: 200, render: (r) => <CopyCell value={r.primary_circle} strike={r.primary_circle_unsuitable} testid={`cell-primary-circle-${r.id}`} /> },
  { key: "professional_description",label: "Primary Description",w: 340, wrap: true },
  { key: "experience_range",        label: "Experience",         w: 120 },
  { key: "secondary_circle",        label: "Secondary Circle",   w: 200, render: (r) => <CopyCell value={r.secondary_circle} strike={r.secondary_circle_unsuitable} testid={`cell-secondary-circle-${r.id}`} /> },
  { key: "secondary_description",   label: "Secondary Description", w: 340, wrap: true },
  { key: "looking_for",             label: "Looking For",        w: 280, wrap: true, render: (r) => <CopyCellWrap value={(r.looking_for && r.looking_for.length) ? r.looking_for.join(", ") : ""} /> },
  { key: "notes",                   label: "Any Suggestions",    w: 300, wrap: true },
  { key: "payment_status",          label: "Payment Status",     w: 130, render: (r) => {
    // Members is the single source of truth — read from the derived
    // `_display_payment_status` (Refunded if any payment for this app
    // is refunded, else the application's own payment_status). Falls
    // back to r.payment_status only for the transient window before
    // enrichment runs. Never renders the raw application field alone,
    // otherwise the Members cell would stay "Paid" after an admin
    // marks the payment "Refunded" (the mutation lives on the payment
    // document, not on the application).
    const ps = r._display_payment_status || r.payment_status || "unpaid";
    return (
      <span style={{ display: "inline-flex", alignItems: "center" }}>
        <PaymentStatusPill status={ps} />
        <CopyIcon value={PAYMENT_STATUS_META[ps]?.label || ps} />
      </span>
    );
  } },
  { key: "razorpay_payment_id",     label: "Payment ID",         w: 190, render: (r) => <CopyCell value={r.razorpay_payment_id} /> },
  { key: "paid_at",                 label: "Payment Date",       w: 120, sortable: SORT_DATE, render: (r) => {
    const v = fmtDate(r.paid_at);
    return v === "—" ? <span style={{ color: T.muted }}>—</span> : <span style={{ display: "inline-flex", alignItems: "center" }}><span>{v}</span><CopyIcon value={v} /></span>;
  } },
  { key: "paid_at_time",            label: "Payment Time",       w: 110, render: (r) => {
    const v = fmtTime(r.paid_at);
    return !v ? <span style={{ color: T.muted }}>—</span> : <span style={{ display: "inline-flex", alignItems: "center" }}><span>{v}</span><CopyIcon value={v} /></span>;
  } },
  // Membership Start Date — populated by the backend the moment
  // Review Status flips to Onboarded. Stored as YYYY-MM-DD; empty
  // for anyone who has not been onboarded yet (spec §4).
  { key: "membership_start_date",   label: "Membership Start Date", w: 170, sortable: SORT_DATE, render: (r) => {
    const v = fmtDate(r.membership_start_date);
    return v === "—" ? <span style={{ color: T.muted }}>—</span> : <span style={{ display: "inline-flex", alignItems: "center" }}><span>{v}</span><CopyIcon value={v} /></span>;
  } },
  { key: "amount_paise",            label: "Amount",             w: 110, sortable: SORT_AMOUNT, render: (r) => {
    const v = rupees(adminDisplayMembershipAmount(r.amount_paise));
    return v === "—" ? <span style={{ color: T.muted }}>—</span> : <span style={{ display: "inline-flex", alignItems: "center" }}><span>{v}</span><CopyIcon value={v} /></span>;
  } },
  { key: "contact_name",            label: "Contact Name",       w: 220, render: (r) => <CopyCell value={r.contact_name} /> },
  { key: "status",                  label: "Review Status",      w: 160, render: (r) => {
    // Unpaid applications haven't entered the review pipeline —
    // surface as "Not Applicable" instead of surfacing the raw
    // lifecycle value ("unpaid") which would confuse admins.
    const effective = (r.status === "unpaid" || (r.payment_status || "") === "unpaid")
      ? "not_applicable" : r.status;
    return (
      <span style={{ display: "inline-flex", alignItems: "center" }}>
        <StatusPill status={effective} />
        <CopyIcon value={STATUS_META[effective]?.label || effective} />
      </span>
    );
  } },
  { key: "review_remark",           label: "Review Remark",      w: 260, wrap: true, render: (r) => <CopyCellWrap value={r.review_remark} /> },
  { key: "updated_at",              label: "Last Updated",       w: 200, render: (r) => {
    // Combined DD/MM/YYYY • hh:mm AM/PM via the central helper —
    // matches Payments' Created column and drawer timestamps.
    const v = fmtDateTime(r.updated_at);
    return !v ? <span style={{ color: T.muted }}>—</span> : <span style={{ display: "inline-flex", alignItems: "center" }}><span>{v}</span><CopyIcon value={v} /></span>;
  } },
];

/** Master Search: every value a Members row can show, raw + as rendered
 *  (dates as DD/MM/YYYY, amount as ₹, status pills as their labels). */
const _MASTER_RAW_FIELDS = [
  "membership_id", "application_id", "full_name", "contact_name", "gender", "star_member",
  "date_of_birth", "age", "whatsapp_number", "current_city", "city_chapter",
  "membership_plan", "membership_type", "source", "email", "linkedin_url", "instagram_username",
  "professional_type", "professional_type_other", "primary_circle", "professional_description",
  "experience_range", "secondary_circle", "secondary_description", "notes",
  "razorpay_payment_id", "razorpay_order_id", "review_remark",
  "applied_date", "paid_at", "membership_start_date",
];
function _memberMasterBag(r) {
  const ps = r._display_payment_status || r.payment_status || "unpaid";
  const effective = (r.status === "unpaid" || (r.payment_status || "") === "unpaid") ? "not_applicable" : r.status;
  const amt = adminDisplayMembershipAmount(r.amount_paise);
  const vals = _MASTER_RAW_FIELDS.map((f) => r[f]);
  vals.push(
    Array.isArray(r.looking_for) ? r.looking_for.join(", ") : r.looking_for,
    fmtDate(r.applied_date), _fmtClockToAmPm(r.applied_time),
    fmtDate(r.paid_at), fmtTime(r.paid_at),
    fmtDate(r.membership_start_date), fmtDateTime(r.updated_at),
    rupees(amt), amt ? String(Number(amt) / 100) : "",
    PAYMENT_STATUS_META[ps]?.label || ps,
    STATUS_META[effective]?.label || effective,
  );
  return vals.map(_bagEntry);
}
function TableView({ rows, cols, onOpenRow, testid = "admin-table",
                    selectable = false, selectedIds = null, onSelectionChange = null,
                    hiddenCols = null, tableKey = null, onReorderCols = null,
                    onFilteredCountChange = null, onFilteredMembersChange = null, onFilteredRowsChange = null, rowsCap = null,
                    defaultSort = { key: "paid_at", dir: "desc" },
                    clearSignal = 0, highlight = "", enableSort = false, sortResetSignal = 0, multiSort = false }) {
  const hlRe = useMemo(() => _hlTokens(highlight), [highlight]);
  // Sort state stays module-private now — the parent enforces the
  // "latest first" default via `defaultSort` and there is no
  // user-initiated sort toggle any more (column headers are pure
  // labels + resize handles). Keeping the state and its dependency in
  // the memo below preserves the newest-first ordering exactly as
  // before.
  const [sort] = useState(defaultSort);
  // Manual column sorts in priority order: [{ key, dir }]. Single-sort
  // mode keeps at most one manual pick; Multi-Sort appends new columns.
  // Applied Date + Applied Time are a linked pair (see _linkAppliedPair).
  const [userSorts, setUserSorts] = useState([]);
  const toggleUserSort = useCallback((c) => {
    setUserSorts((cur) => {
      const idx = cur.findIndex((u) => u.key === c.key);
      if (idx >= 0) {
        // 1st click = first direction, 2nd = reverse, 3rd = off.
        if (cur[idx].dir === c.sortable.first) {
          return cur.map((u, i) => (i === idx ? { ...u, dir: u.dir === "asc" ? "desc" : "asc" } : u));
        }
        const pair = c.key === "applied_date" || c.key === "applied_time";
        return cur.filter((u) => pair ? (u.key !== "applied_date" && u.key !== "applied_time") : u.key !== c.key);
      }
      const next = { key: c.key, dir: c.sortable.first };
      return _linkAppliedPair(multiSort ? [...cur, next] : [next]);
    });
  }, [multiSort]);
  // Leaving Multi-Sort keeps only the primary sort (plus its linked partner).
  useEffect(() => { if (!multiSort) setUserSorts((cur) => (cur.length > 1 ? _linkAppliedPair([cur[0]]) : cur)); }, [multiSort]);
  const sortIndexOf = (key) => userSorts.findIndex((u) => u.key === key);
  const [filters, setFilters] = useState({});      // { colKey: text }
  const [dropdowns, setDropdowns] = useState({});  // { colKey: Set<string> }
  const [lastToggledIdx, setLastToggledIdx] = useState(null);

  // External "Clear All Filters" signal — parent bumps a counter and
  // TableView wipes its internal filter state and any manual column
  // sort (back to the default ordering). Column widths, order,
  // freeze and hidden-column preferences are NOT touched.
  useEffect(() => {
    if (clearSignal > 0) {
      setFilters({});
      setDropdowns({});
      setUserSorts([]);
    }
  }, [clearSignal]);
  useEffect(() => { if (sortResetSignal > 0) setUserSorts([]); }, [sortResetSignal]);

  // ---- Excel-style column layout state -------------------------------
  //  widths            — per-column pixel width (stored on tableKey)
  //  frozenCount       — how many left-most columns are pinned via
  //                      position:sticky. Global preference (0..3).
  //  dragKey / dropKey — transient drag state used to render a drop
  //                      placeholder while the admin is reordering.
  const [widths, setWidths] = useState(() => tableKey ? _readColWidths(tableKey) : {});
  const [frozenCount, setFrozenCount] = useState(() => tableKey ? _readFrozenCount(tableKey) : 0);
  const [dragKey, setDragKey] = useState(null);
  const [dropKey, setDropKey] = useState(null);
  // Live resize tracking — kept in a ref so mousemove doesn't cause
  // React re-renders on every pixel move.
  const resizeRef = useRef(null);
  const bodyRef = useRef(null);

  // Persist widths only after a user-initiated change (resize / auto-fit),
  // never on mount — so a fresh device with no widths yet can't wipe the
  // account's saved widths before the server copy has been hydrated.
  const widthsDirtyRef = useRef(false);
  useEffect(() => {
    if (tableKey && widthsDirtyRef.current) _writeColWidths(tableKey, widths);
  }, [widths, tableKey]);
  // Server-side preference hydration finished → adopt the account's
  // saved widths (per column) unless the user is mid-resize.
  useEffect(() => {
    if (!tableKey) return undefined;
    const onHydrated = () => { if (!resizeRef.current) setWidths((w) => ({ ...w, ..._readColWidths(tableKey) })); };
    window.addEventListener(PREFS_HYDRATED_EVENT, onHydrated);
    return () => window.removeEventListener(PREFS_HYDRATED_EVENT, onHydrated);
  }, [tableKey]);
  // Listen for global frozen-count changes so the Column Manager can
  // update the mounted TableView instantly after Apply.
  useEffect(() => {
    if (!tableKey) return;
    const handler = (e) => {
      // Ignore broadcasts for other tables (per-table freeze counts).
      if (e && e.detail && e.detail.tableKey && e.detail.tableKey !== tableKey) return;
      setFrozenCount(_readFrozenCount(tableKey));
    };
    window.addEventListener(FROZEN_CHANGE_EVENT, handler);
    return () => window.removeEventListener(FROZEN_CHANGE_EVENT, handler);
  }, [tableKey]);

  // Mobile detection — drag + resize + freeze are desktop-only features.
  // Keep them off small viewports where they'd be more of a nuisance
  // than a productivity boost.
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined"
    && window.matchMedia && window.matchMedia("(max-width: 768px)").matches);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(max-width: 768px)");
    const h = (e) => setIsMobile(e.matches);
    try { mq.addEventListener("change", h); return () => mq.removeEventListener("change", h); }
    catch (_) { mq.addListener(h); return () => mq.removeListener(h); }
  }, []);

  // Hide any columns that the user has personalised as hidden
  // (localStorage-backed set of column keys).
  const visibleCols = useMemo(() => {
    if (!hiddenCols || hiddenCols.size === 0) return cols;
    return cols.filter((c) => !hiddenCols.has(c.key));
  }, [cols, hiddenCols]);

  // Resolve every column's rendered width. Custom width (from resize)
  // wins over the column definition's default `w`, which itself wins
  // over the 140 px baseline.
  const widthOf = useCallback(
    (c) => widths[c.key] || c.w || 140,
    [widths]
  );

  // Cumulative left offset for sticky-frozen columns. Selection
  // checkbox — when present — is ALWAYS considered part of the frozen
  // block (index 0) so it never scrolls away.
  const effectiveFrozen = isMobile ? 0 : Math.min(frozenCount, visibleCols.length);
  const SELECT_COL_W = 42;
  const leftOffsetForCol = useCallback((colIdx) => {
    let left = selectable ? SELECT_COL_W : 0;
    for (let i = 0; i < colIdx; i++) left += widthOf(visibleCols[i]);
    return left;
  }, [selectable, visibleCols, widthOf]);

  // Apply BOTH text filter (contains) AND multi-select filter (equals) per column.
  // Across columns filters combine with AND — same as Excel.
  // Hidden columns' filters are ignored so toggling visibility never traps
  // rows behind an invisible filter. `excludeDropdownKey` lets the
  // dropdown-count logic evaluate "every filter except my own column".
  const applyColumnFilters = useCallback((input, excludeDropdownKey = null) => {
    const activeKeys = new Set(visibleCols.map((c) => c.key));
    let out = input;
    Object.entries(filters).forEach(([k, v]) => {
      if (!activeKeys.has(k)) return;
      const match = _boundaryMatcher(v);
      if (match) out = out.filter((r) => {
        let cell = r[k];
        if (Array.isArray(cell)) cell = cell.join(", ");
        return match(String(cell ?? "").toLowerCase());
      });
    });
    Object.entries(dropdowns).forEach(([k, sel]) => {
      if (!activeKeys.has(k) || k === excludeDropdownKey) return;
      if (sel && sel.size) {
        out = out.filter((r) => {
          let cell = r[k];
          if (Array.isArray(cell)) cell = cell.join(", ");
          return sel.has(String(cell ?? ""));
        });
      }
    });
    return out;
  }, [visibleCols, filters, dropdowns]);

  // Distinct values per column (with counts) for the multi-select filter.
  // Excel semantics: each column's counts reflect every OTHER active
  // filter, but not its own dropdown selection — so the list stays
  // stable while the user is picking values in it, and the numbers
  // always describe the currently filtered dataset.
  const distinctByCol = useMemo(() => {
    const map = {};
    visibleCols.forEach((c) => {
      const scoped = applyColumnFilters(rows, c.key);
      const counts = {};
      scoped.forEach((r) => {
        let v = r[c.key];
        if (Array.isArray(v)) v = v.join(", ");
        if (v === null || v === undefined) v = "";
        const s = String(v);
        counts[s] = (counts[s] || 0) + 1;
      });
      // Keep currently-selected values visible (count 0) so they can
      // always be unticked, even if other filters exclude them.
      const sel = dropdowns[c.key];
      if (sel && sel.size) sel.forEach((v) => { if (!(v in counts)) counts[v] = 0; });
      map[c.key] = Object.entries(counts)
        .map(([value, count]) => ({ value, label: value === "" ? "(empty)" : value, count }))
        .sort((a, b) => (b.count - a.count) || a.label.localeCompare(b.label));
    });
    return map;
  }, [rows, visibleCols, dropdowns, applyColumnFilters]);

  const filtered = useMemo(() => {
    let out = applyColumnFilters(rows);
    // Default sort ALWAYS runs regardless of column visibility so
    // rows stay in newest-first order even when the "Payment Date"
    // column has been hidden. When the sort key is paid_at we fall
    // back through applied_date/created_at so unpaid rows (with no
    // paid_at) still sort correctly by their creation moment.
    if (sort.key && !userSorts.length) {
      const dir = sort.dir === "asc" ? 1 : -1;
      const sortVal = sort.key === "applied_instant"
        ? _appliedInstant
        : sort.key === "paid_at"
        ? (r) => (r.import_batch_id
            // historical import: real Applied Date first, then Payment Date, then the undated sentinel
            ? (r.applied_date || r.paid_at || r.historical_sort_key || "")
            : (r.paid_at || r.applied_date || r.created_at || ""))
        : (r) => r[sort.key] ?? "";
      out = [...out].sort((a, b) => {
        const av = sortVal(a);
        const bv = sortVal(b);
        if (av === null || av === undefined) return (bv === null || bv === undefined) ? 0 : 1;
        if (bv === null || bv === undefined) return -1;
        if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
        return String(av).localeCompare(String(bv)) * dir;
      });
    }
    // Manual column sorts (▲▼ header toggles) re-order the already
    // filtered rows; they never change which rows are included and
    // fully replace the default ordering while active.
    if (userSorts.length) {
      const specs = userSorts.map((u) => ({ col: cols.find((c) => c.key === u.key && c.sortable), dir: u.dir }))
                             .filter((x) => x.col);
      if (specs.length) out = _sortRowsMulti(out, specs);
    }
    return out;
  }, [rows, sort, applyColumnFilters, userSorts, cols]);

  const activeFilterCount =
    Object.values(filters).filter((v) => v && String(v).trim()).length +
    Object.values(dropdowns).filter((s) => s && s.size).length;

  // Bubble the post-column-filter row count up to the parent so the
  // page header ("X records") reflects every filter — top toggles,
  // global search, column text filter and multi-select dropdowns.
  // NB: This is the PRE-cap count so it represents the true filtered
  // dataset, not the visible page.
  useEffect(() => {
    if (onFilteredCountChange) onFilteredCountChange(filtered.length);
  }, [filtered.length, onFilteredCountChange]);
  useEffect(() => {
    if (!onFilteredMembersChange) return;
    const ids = new Set(filtered.map((r) => r.member_id || String(r.whatsapp_number || "").replace(/\D/g, "").slice(-10) || r.id));
    onFilteredMembersChange(ids.size);
  }, [filtered, onFilteredMembersChange]);
  // Expose the exact post-column-filter row set (pre-cap) — used by
  // Export so it mirrors what the table currently shows.
  useEffect(() => {
    if (onFilteredRowsChange) onFilteredRowsChange(filtered);
  }, [filtered, onFilteredRowsChange]);

  // Optional "Rows Per View" cap — applied AFTER column filtering so
  // the count callback still reflects the full filtered dataset.
  const rendered = useMemo(
    () => (rowsCap && Number.isFinite(rowsCap)) ? filtered.slice(0, rowsCap) : filtered,
    [filtered, rowsCap]
  );

  const clearAll = () => { setFilters({}); setDropdowns({}); };

  // ---- Selection helpers (only used when `selectable` is true) --------
  const visibleRowIds = rendered.map((r) => r.id);

  // ---- Row windowing -----------------------------------------------------
  // Only the rows inside the scroll viewport (+ overscan) are mounted; the
  // rest are represented by two spacer rows so scrolling, selection,
  // filtering, sorting and export all still operate on the full `rendered`
  // array. Heights are measured per row (keyed by id) so wrapped rows don't
  // make the spacers drift; unmeasured rows use the running average.
  const OVERSCAN = 12;
  const heightsRef = useRef(new Map());
  const avgHRef = useRef(46);
  const tbodyRef = useRef(null);
  const [win, setWin] = useState({ start: 0, end: 40 });
  const [, bumpMeasure] = useState(0);
  const rowH = (r) => heightsRef.current.get(r.id) ?? avgHRef.current;
  const recomputeWindow = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    const headH = el.querySelector("thead")?.offsetHeight || 0;
    const top = Math.max(0, el.scrollTop - headH);
    const viewH = el.clientHeight || 600;
    let y = 0, start = 0;
    while (start < rendered.length && y + rowH(rendered[start]) < top) { y += rowH(rendered[start]); start++; }
    let end = start;
    for (let yy = y; end < rendered.length && yy < top + viewH; end++) yy += rowH(rendered[end]);
    start = Math.max(0, start - OVERSCAN);
    end = Math.min(rendered.length, end + OVERSCAN);
    setWin((w) => (w.start === start && w.end === end) ? w : { start, end });
  }, [rendered]);
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return undefined;
    el.addEventListener("scroll", recomputeWindow, { passive: true });
    window.addEventListener("resize", recomputeWindow);
    recomputeWindow();
    return () => {
      el.removeEventListener("scroll", recomputeWindow);
      window.removeEventListener("resize", recomputeWindow);
    };
  }, [recomputeWindow]);
  const winStart = Math.min(win.start, Math.max(0, rendered.length - 1));
  const winEnd = Math.min(rendered.length, Math.max(win.end, winStart + 1));
  const windowRows = rendered.slice(winStart, winEnd);
  const spacerCols = visibleCols.length + (selectable ? 1 : 0);
  let topSpacer = 0, bottomSpacer = 0;
  for (let i = 0; i < winStart; i++) topSpacer += rowH(rendered[i]);
  for (let i = winEnd; i < rendered.length; i++) bottomSpacer += rowH(rendered[i]);
  // Measure mounted rows after paint; re-render once if any height changed.
  useEffect(() => {
    const trs = tbodyRef.current?.querySelectorAll("tr[data-hfa-row]");
    if (!trs || !trs.length) return;
    let changed = false;
    trs.forEach((tr, i) => {
      const h = tr.getBoundingClientRect().height;
      const r = windowRows[i];
      if (!r || !(h > 0)) return;
      if (Math.abs((heightsRef.current.get(r.id) ?? -1) - h) > 0.5) { heightsRef.current.set(r.id, h); changed = true; }
    });
    // Estimate for unmeasured rows = running mean over EVERY measured row
    // (stable; the current window alone would make spacers oscillate).
    if (changed) {
      let sum = 0, n = 0;
      heightsRef.current.forEach((h) => { sum += h; n++; });
      const avg = n ? sum / n : avgHRef.current;
      if (Math.abs(avg - avgHRef.current) > 0.5) avgHRef.current = avg;
      bumpMeasure((k) => k + 1); recomputeWindow();
    }
  });
  const allVisibleSelected = selectable && selectedIds && visibleRowIds.length > 0
    && visibleRowIds.every((id) => selectedIds.has(id));
  const someVisibleSelected = selectable && selectedIds && visibleRowIds.some((id) => selectedIds.has(id));
  const toggleAllVisible = () => {
    if (!onSelectionChange) return;
    const next = new Set(selectedIds || []);
    if (allVisibleSelected) visibleRowIds.forEach((id) => next.delete(id));
    else visibleRowIds.forEach((id) => next.add(id));
    onSelectionChange(next);
  };
  const toggleOne = (rowId, idx, shiftKey) => {
    if (!onSelectionChange) return;
    const next = new Set(selectedIds || []);
    if (shiftKey && lastToggledIdx !== null && lastToggledIdx !== idx) {
      const [lo, hi] = idx < lastToggledIdx ? [idx, lastToggledIdx] : [lastToggledIdx, idx];
      const shouldSelect = !next.has(rowId);
      for (let i = lo; i <= hi; i++) {
        const rid = visibleRowIds[i];
        if (shouldSelect) next.add(rid); else next.delete(rid);
      }
    } else {
      if (next.has(rowId)) next.delete(rowId); else next.add(rowId);
    }
    setLastToggledIdx(idx);
    onSelectionChange(next);
  };

  // ---- Column resize + auto-fit --------------------------------------
  const startResize = useCallback((e, colKey) => {
    e.preventDefault(); e.stopPropagation();
    const startX = e.clientX;
    const startWidth = widths[colKey] || (cols.find((c) => c.key === colKey)?.w) || 140;
    resizeRef.current = { colKey, startX, startWidth };
    const onMove = (ev) => {
      const rr = resizeRef.current;
      if (!rr) return;
      const delta = ev.clientX - rr.startX;
      const next = Math.max(60, Math.min(700, rr.startWidth + delta));
      widthsDirtyRef.current = true;
      setWidths((w) => ({ ...w, [rr.colKey]: next }));
    };
    const onUp = () => {
      resizeRef.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, [widths, cols]);
  const autoFitColumn = useCallback((colKey) => {
    // Measure the widest rendered cell in this column by inspecting the
    // live DOM. Uses each td's scrollWidth so we account for exact fonts,
    // paddings and inline elements as displayed — not just raw text.
    const tbl = bodyRef.current;
    if (!tbl) return;
    let max = 60;
    const cells = tbl.querySelectorAll(`td[data-col='${colKey}']`);
    cells.forEach((el) => {
      // Temporarily strip width constraints to measure natural width.
      const prevMax = el.style.maxWidth;
      const prevMin = el.style.minWidth;
      const prevW   = el.style.width;
      el.style.maxWidth = "none"; el.style.minWidth = "0"; el.style.width = "auto";
      // scrollWidth accounts for content overflow; add horizontal padding.
      const w = el.scrollWidth + 24;
      el.style.maxWidth = prevMax; el.style.minWidth = prevMin; el.style.width = prevW;
      if (w > max) max = w;
    });
    // Also consider header label width so a short-content column doesn't
    // squash below its title.
    const hd = tbl.querySelector(`th[data-col='${colKey}']`);
    if (hd) {
      const label = hd.querySelector("[data-col-label]");
      if (label) {
        const w = label.scrollWidth + 40;   // extra room for sort arrow + resize grip
        if (w > max) max = w;
      }
    }
    widthsDirtyRef.current = true;
    setWidths((w) => ({ ...w, [colKey]: Math.min(700, Math.max(80, max)) }));
  }, []);

  // ---- Column reorder via header drag --------------------------------
  const headerDragProps = useCallback((colKey, colIdx) => {
    if (!onReorderCols || isMobile) return {};
    return {
      draggable: true,
      onDragStart: (e) => {
        setDragKey(colKey);
        // Some browsers require setData for dragstart to fire; supply
        // the key even though we already track it in state.
        try { e.dataTransfer.setData("text/plain", colKey); e.dataTransfer.effectAllowed = "move"; } catch (_) {}
      },
      onDragEnd: () => { setDragKey(null); setDropKey(null); },
      onDragOver: (e) => {
        if (!dragKey || dragKey === colKey) return;
        e.preventDefault();
        try { e.dataTransfer.dropEffect = "move"; } catch (_) {}
        setDropKey(colKey);
      },
      onDragLeave: () => {
        // Clearing dropKey on leave would flicker across neighbouring
        // cells; leave it and let the next dragOver overwrite it.
      },
      onDrop: (e) => {
        e.preventDefault();
        const from = dragKey || e.dataTransfer.getData("text/plain");
        if (from && from !== colKey) onReorderCols(from, colKey);
        setDragKey(null); setDropKey(null);
      },
    };
  }, [onReorderCols, dragKey, isMobile]);

  return (
    <HighlightCtx.Provider value={hlRe}>
    <div data-testid={testid}
      style={{ background: T.card, border: `1px solid ${T.line}`, borderRadius: 14, overflow: "hidden",
               boxShadow: "0 4px 24px -8px rgba(11,11,15,0.08), 0 1px 2px rgba(11,11,15,0.04)" }}>
      {activeFilterCount > 0 && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                      padding: "8px 14px", background: T.brandSoft, borderBottom: `1px solid ${T.line}`,
                      fontSize: 12, color: T.brand }}>
          <span data-testid="active-filter-count">
            {activeFilterCount} filter{activeFilterCount === 1 ? "" : "s"} active — {filtered.length} row{filtered.length === 1 ? "" : "s"} match
          </span>
          <button data-testid="clear-all-filters" onClick={clearAll}
            style={{ padding: "4px 10px", background: "white", border: `1px solid ${T.brand}`,
                     color: T.brand, borderRadius: 6, fontSize: 11, fontWeight: 600,
                     cursor: "pointer", fontFamily: "inherit" }}>Clear all filters</button>
        </div>
      )}
      <div ref={bodyRef} style={{ overflow: "auto", maxHeight: "70vh", overflowAnchor: "none" }}>
        <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, fontSize: 12.5, minWidth: 1600 }}>
          <thead>
            <tr style={{ background: T.ink, position: "sticky", top: 0, zIndex: 3 }}>
              {selectable && (
                <th style={{ padding: "12px 12px", borderBottom: `1px solid ${T.ink}`,
                             width: SELECT_COL_W, minWidth: SELECT_COL_W, background: T.ink,
                             position: effectiveFrozen > 0 ? "sticky" : undefined,
                             left: effectiveFrozen > 0 ? 0 : undefined,
                             zIndex: effectiveFrozen > 0 ? 6 : undefined }}>
                  <input type="checkbox" data-testid="row-select-all"
                    checked={allVisibleSelected}
                    ref={(el) => { if (el) el.indeterminate = !allVisibleSelected && someVisibleSelected; }}
                    onChange={toggleAllVisible} onClick={(e) => e.stopPropagation()} />
                </th>
              )}
              {visibleCols.map((c, colIdx) => {
                const isFrozen = colIdx < effectiveFrozen;
                const isLastFrozen = isFrozen && colIdx === effectiveFrozen - 1;
                const w = widthOf(c);
                const dragging = dragKey === c.key;
                const isDropTarget = dropKey === c.key && dragKey && dragKey !== c.key;
                return (
                <th key={c.key} data-col={c.key}
                  data-testid={`col-header-${c.key}`}
                  {...headerDragProps(c.key, colIdx)}
                  style={{ padding: "12px 22px 12px 12px", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.10em",
                           color: "white", fontWeight: 700, borderBottom: `1px solid ${T.ink}`, textAlign: "left",
                           cursor: dragging ? "grabbing" : "grab", userSelect: "none", whiteSpace: "nowrap",
                           width: w, minWidth: w, maxWidth: w,
                           background: T.ink,
                           // Establish a containing block for the
                           // absolutely-positioned resize handle on
                           // NON-frozen columns too. Frozen columns
                           // already have `position: sticky` which
                           // creates its own containing block, so the
                           // handle anchors correctly for both.
                           position: isFrozen ? "sticky" : "relative",
                           left: isFrozen ? leftOffsetForCol(colIdx) : undefined,
                           zIndex: isFrozen ? 5 : undefined,
                           boxShadow: isLastFrozen ? "2px 0 6px -3px rgba(0,0,0,0.35)" : undefined,
                           borderLeft: isDropTarget ? `3px solid ${T.brand}` : undefined,
                           opacity: dragging ? 0.45 : 1,
                           transition: "background-color 150ms ease",
                           overflow: "hidden", textOverflow: "ellipsis" }}
                  title={c.label}>
                  {enableSort && c.sortable ? (
                    <span style={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                      <span data-col-label style={{ overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>{c.label}</span>
                      <SortToggle colKey={c.key} label={c.label} sortable={c.sortable}
                        active={sortIndexOf(c.key) >= 0} dir={userSorts[sortIndexOf(c.key)]?.dir}
                        priority={multiSort && userSorts.length > 1 && sortIndexOf(c.key) >= 0 ? sortIndexOf(c.key) + 1 : null}
                        onToggle={() => toggleUserSort(c)} />
                    </span>
                  ) : (
                    <span data-col-label>{c.label}{c.headerIcon === "star" && <StarGlyph filled size={12} style={{ marginLeft: 5, verticalAlign: "-1px" }} />}</span>
                  )}
                  {!isMobile && (
                    <span data-resize-handle="1"
                      data-testid={`col-resize-${c.key}`}
                      onMouseDown={(e) => startResize(e, c.key)}
                      onDoubleClick={(e) => { e.stopPropagation(); autoFitColumn(c.key); }}
                      title="Drag to resize · double-click to auto-fit"
                      onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(234,88,12,0.55)")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(11,11,15,0.08)")}
                      style={{ position: "absolute", top: 6, right: 0,
                               width: 6, height: "calc(100% - 12px)",
                               cursor: "col-resize", zIndex: 7,
                               background: "rgba(11,11,15,0.08)",
                               borderRadius: 3,
                               transition: "background 120ms ease" }} />
                  )}
                </th>
                );
              })}
            </tr>
            <tr style={{ background: T.brand, position: "sticky", top: 38, zIndex: 3 }}>
              {selectable && <th style={{ background: T.brand, borderBottom: `1px solid rgba(255,255,255,0.14)`,
                             width: SELECT_COL_W, minWidth: SELECT_COL_W,
                             position: effectiveFrozen > 0 ? "sticky" : undefined,
                             left: effectiveFrozen > 0 ? 0 : undefined,
                             zIndex: effectiveFrozen > 0 ? 6 : undefined }} />}
              {visibleCols.map((c, colIdx) => {
                const isFrozen = colIdx < effectiveFrozen;
                const isLastFrozen = isFrozen && colIdx === effectiveFrozen - 1;
                const w = widthOf(c);
                return (
                <th key={c.key}
                  style={{ padding: "8px 8px 10px", borderBottom: `1px solid rgba(255,255,255,0.14)`,
                           verticalAlign: "top",
                           width: w, minWidth: w, maxWidth: w,
                           background: T.brand,
                           position: isFrozen ? "sticky" : undefined,
                           left: isFrozen ? leftOffsetForCol(colIdx) : undefined,
                           zIndex: isFrozen ? 5 : undefined,
                           boxShadow: isLastFrozen ? "2px 0 6px -3px rgba(11,11,15,0.18)" : undefined }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                    <input data-testid={`filter-${c.key}`}
                      placeholder="Search…" value={filters[c.key] || ""}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => setFilters((f) => ({ ...f, [c.key]: e.target.value }))}
                      style={{ width: "100%", padding: "5px 8px", fontSize: 11.5,
                               border: `1px solid #FDBA74`, borderRadius: 6,
                               background: "#FFEDD5", color: "#0B0B0F",
                               fontWeight: 500, fontFamily: "inherit",
                               outline: "none",
                               transition: "border-color 150ms ease, box-shadow 150ms ease" }}
                      onFocus={(e) => { e.currentTarget.style.borderColor = "#F97316"; e.currentTarget.style.boxShadow = "0 0 0 3px rgba(255,255,255,0.55)"; }}
                      onBlur={(e) => { e.currentTarget.style.borderColor = "#FDBA74"; e.currentTarget.style.boxShadow = "none"; }} />
                    <MultiSelectFilter colKey={c.key}
                      values={distinctByCol[c.key] || []}
                      selected={dropdowns[c.key] || new Set()}
                      onChange={(set) => setDropdowns((d) => ({ ...d, [c.key]: set }))}
                      clearSignal={clearSignal} />
                  </div>
                </th>
                );
              })}
            </tr>
          </thead>
          <tbody ref={tbodyRef}>
            {winStart > 0 && (
              <tr aria-hidden="true" data-hfa-spacer="top" style={{ height: topSpacer }}>
                <td colSpan={spacerCols} style={{ padding: 0, border: 0 }} />
              </tr>
            )}
            {windowRows.map((r, wi) => {
              const idx = winStart + wi;
              const isSelected = selectable && selectedIds && selectedIds.has(r.id);
              const rowBg = isSelected ? T.brandTint : "white";
              return (
              <tr key={r.id} data-hfa-row data-selected={!!isSelected}
                onClick={() => onOpenRow && onOpenRow(r)}
                style={{ borderBottom: `1px solid ${T.line}`, cursor: "pointer" }}>
                {selectable && (
                  <td onClick={(e) => e.stopPropagation()}
                    style={{ padding: "10px 12px", verticalAlign: "top",
                             width: SELECT_COL_W, minWidth: SELECT_COL_W,
                             background: rowBg,
                             position: effectiveFrozen > 0 ? "sticky" : undefined,
                             left: effectiveFrozen > 0 ? 0 : undefined,
                             zIndex: effectiveFrozen > 0 ? 2 : undefined }}>
                    <input type="checkbox" data-testid={`row-select-${r.id}`}
                      checked={!!isSelected}
                      onChange={(e) => toggleOne(r.id, idx, e.nativeEvent.shiftKey)} />
                  </td>
                )}
                {visibleCols.map((c, colIdx) => {
                  const isFrozen = colIdx < effectiveFrozen;
                  const isLastFrozen = isFrozen && colIdx === effectiveFrozen - 1;
                  const w = widthOf(c);
                  return (
                  <td key={c.key} data-col={c.key}
                    style={{ padding: "10px 12px", verticalAlign: "top",
                             width: w, minWidth: w, maxWidth: w,
                             overflow: "hidden",
                             whiteSpace: c.wrap ? "normal" : "nowrap",
                             textOverflow: c.wrap ? undefined : "ellipsis",
                             background: rowBg,
                             position: isFrozen ? "sticky" : undefined,
                             left: isFrozen ? leftOffsetForCol(colIdx) : undefined,
                             zIndex: isFrozen ? 2 : undefined,
                             boxShadow: isLastFrozen ? "2px 0 6px -3px rgba(0,0,0,0.10)" : undefined }}>
                    {c.render
                      ? _hlTree(c.render(r), hlRe)
                      : c.wrap
                        ? <CopyCellWrap value={r[c.key]} />
                        : <CopyCell value={r[c.key]} />}
                  </td>
                  );
                })}
              </tr>
              );
            })}
            {rendered.length - winEnd > 0 && (
              <tr aria-hidden="true" data-hfa-spacer="bottom" style={{ height: bottomSpacer }}>
                <td colSpan={spacerCols} style={{ padding: 0, border: 0 }} />
              </tr>
            )}
            {rendered.length === 0 && (
              <tr><td colSpan={visibleCols.length + (selectable ? 1 : 0)} style={{ padding: 30, color: T.muted, textAlign: "center", fontSize: 13 }}>No rows match the current filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
    </HighlightCtx.Provider>
  );
}

/** Filter group card — thin HFA-orange outline, white background, soft
 *  shadow, centered heading above the toggle group. Used by both
 *  Members (two cards) and Payments (one card) for a consistent look. */
function FilterGroupCard({ heading, children, rightAdornment, style }) {
  return (
    <div data-hfa-filter-card style={{ display: "inline-block", verticalAlign: "top",
                  padding: "10px 16px 12px",
                  background: "white",
                  border: `1px solid ${T.brandTint}`,
                  borderRadius: 12,
                  boxShadow: "0 1px 3px rgba(234,88,12,0.05)", ...style }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center",
                    position: "relative", marginBottom: 8, minHeight: 18 }}>
        <span style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em",
                       color: T.brand, fontWeight: 800 }}>{heading}</span>
        {rightAdornment && (
          <span style={{ position: "absolute", right: 0, top: "50%", transform: "translateY(-50%)" }}>
            {rightAdornment}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

const _DUP_CITIES = ["Mumbai", "Delhi NCR", "Bangalore", "Pune", "Hyderabad", "Raipur"];

/** Admin → Members → Duplicate: direct-QR additional City Chapter flow. */
function DuplicateMembershipModal({ api, source, onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [date, setDate] = useState(() => _todayIST());
  const [city, setCity] = useState("");
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const startDate = useMemo(() => (/^\d{4}-\d{2}-\d{2}$/.test(date) ? _addDays(date, 2) : ""), [date]);

  useEffect(() => {
    if (step !== 3 || !city || !date) return;
    let alive = true;
    setQuote(null); setErr("");
    api.get("/pricing/duplicate-quote", { params: { city_chapter: city, application_date: date } })
      .then((r) => { if (alive) setQuote(r.data); })
      .catch((e) => { if (alive) setErr(String(e?.response?.data?.detail || "Could not fetch the membership amount.")); });
    return () => { alive = false; };
  }, [step, city, date, api]);

  const create = async () => {
    setBusy(true); setErr("");
    try {
      await api.post(`/applications/${source.id}/duplicate`, { application_date: date, city_chapter: city });
      await onCreated();
    } catch (e) {
      setErr(String(e?.response?.data?.detail || e?.message || "Failed to duplicate membership."));
    } finally { setBusy(false); }
  };

  const inputStyle = { width: "100%", padding: "9px 12px", fontSize: 13, fontFamily: "inherit",
                       border: `1px solid ${T.line}`, borderRadius: 8, background: "white", color: T.ink };
  const btn = (primary) => ({ padding: "8px 14px", fontSize: 12, fontWeight: 600, borderRadius: 8, fontFamily: "inherit",
                              cursor: busy ? "wait" : "pointer", border: primary ? "none" : `1px solid ${T.line}`,
                              background: primary ? T.brand : "white", color: primary ? "white" : T.slate });
  const Row = ({ k, v, testid }) => (
    <div style={{ display: "grid", gridTemplateColumns: "170px 1fr", padding: "7px 0", borderBottom: `1px solid ${T.line}`, fontSize: 13 }}>
      <span style={{ color: T.muted }}>{k}</span><span data-testid={testid} style={{ fontWeight: 600, color: T.ink }}>{v}</span>
    </div>
  );

  return createPortal(
    <div data-testid="duplicate-membership-modal" onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 300 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: "white", borderRadius: 14, padding: 24, width: "min(520px, 92%)", boxShadow: "0 40px 80px rgba(0,0,0,0.4)" }}>
        <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 4 }}>Duplicate Membership</div>
        <div style={{ fontSize: 12.5, color: T.slate, marginBottom: 16 }}>
          Source: <b>{source.full_name || source.contact_name || "—"}</b> · {source.city_chapter || "—"} · applied {fmtDate(source.applied_date)}
        </div>

        {step === 1 && (<>
          <label style={{ display: "block", fontSize: 11.5, color: T.muted, fontWeight: 600, marginBottom: 6 }}>Application Date</label>
          <input type="date" data-testid="dup-application-date" value={date} max={_todayIST()} autoFocus
            onChange={(e) => setDate(e.target.value)} onFocus={(e) => { try { e.target.showPicker?.(); } catch (_) {} }}
            style={inputStyle} />
          {startDate && (
            <div style={{ marginTop: 10, fontSize: 12.5, color: T.slate }}>
              Membership Start Date will be <b data-testid="dup-start-date-preview">{_ymdToDMY(startDate)}</b> (application date + 2 days).
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
            <button data-testid="dup-cancel" onClick={onClose} style={btn(false)}>Cancel</button>
            <button data-testid="dup-next-1" disabled={!startDate} onClick={() => setStep(2)} style={{ ...btn(true), opacity: startDate ? 1 : 0.5 }}>Next</button>
          </div>
        </>)}

        {step === 2 && (<>
          <div style={{ fontSize: 13.5, color: T.ink, lineHeight: 1.55, marginBottom: 12 }}>
            It seems like a case of direct QR payment for an additional City Chapter. Which City Chapter have you applied for additionally?
          </div>
          <select data-testid="dup-city-select" value={city} onChange={(e) => setCity(e.target.value)} autoFocus style={inputStyle}>
            <option value="">Select a City Chapter</option>
            {_DUP_CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginTop: 18 }}>
            <button data-testid="dup-back-2" onClick={() => setStep(1)} style={btn(false)}>Back</button>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={onClose} style={btn(false)}>Cancel</button>
              <button data-testid="dup-next-2" disabled={!city} onClick={() => setStep(3)} style={{ ...btn(true), opacity: city ? 1 : 0.5 }}>Next</button>
            </div>
          </div>
        </>)}

        {step === 3 && (<>
          <div style={{ fontSize: 12.5, color: T.slate, marginBottom: 10 }}>Please confirm the new membership record:</div>
          <Row k="Application Date" v={_ymdToDMY(date)} testid="dup-confirm-date" />
          <Row k="City Chapter" v={city} testid="dup-confirm-city" />
          <Row k="Payment Status" v="Paid" />
          <Row k="Membership Amount" v={quote ? rupees(quote.amount_paise) : (err ? "—" : "Fetching…")} testid="dup-confirm-amount" />
          <Row k="Membership Start Date" v={_ymdToDMY(startDate)} testid="dup-confirm-start" />
          <Row k="Review Status" v="Review Pending" />
          {err && <div data-testid="dup-error" style={{ color: T.danger, fontSize: 12.5, marginTop: 10 }}>{err}</div>}
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginTop: 18 }}>
            <button data-testid="dup-back-3" disabled={busy} onClick={() => setStep(2)} style={btn(false)}>Back</button>
            <div style={{ display: "flex", gap: 8 }}>
              <button data-testid="dup-cancel-3" disabled={busy} onClick={onClose} style={btn(false)}>Cancel</button>
              <button data-testid="dup-confirm" disabled={busy || !quote} onClick={create} style={{ ...btn(true), opacity: quote ? 1 : 0.5 }}>
                {busy ? "Creating…" : "Duplicate Membership"}
              </button>
            </div>
          </div>
        </>)}
      </div>
    </div>, document.body);
}

/** Shared style for the Members action buttons (Clear all filters / Export /
 *  Columns / Duplicate): identical size & type; orange outline when the
 *  action is available, grey when it cannot currently be performed. */
const actionBtnStyle = (enabled) => ({
  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7,
  height: 32, padding: "0 14px", fontSize: 12, fontWeight: 600, lineHeight: 1,
  letterSpacing: "0.02em", borderRadius: 999, fontFamily: "inherit", boxSizing: "border-box",
  background: enabled ? "white" : "#F4F4F5",
  border: `1px solid ${enabled ? T.brand : T.line}`,
  color: enabled ? T.brand : T.muted,
  cursor: enabled ? "pointer" : "not-allowed",
});
/** Members action controls: orange outline + orange text at rest, solid
 *  orange + white while the control is ON / its dialog is open. */
const outlineBtnStyle = (enabled, active = false) => ({
  ...actionBtnStyle(enabled),
  background: active ? T.brand : (enabled ? "white" : "#F4F4F5"),
  border: active ? `1px solid ${T.brand}` : (enabled ? HAIR_BRAND : `1px solid ${T.line}`),
  color: active ? "white" : (enabled ? T.brand : T.muted),
  transition: "background-color 120ms ease, color 120ms ease",
});

function ListPageHeader({ title, count, totalCount, memberCount, onExport, onExportFilter, exportEnabled = true, onRefresh, extra, topRight, leftBlock, onClearFilters, activeDateChip, onShiftSingleDay, secondaryActions = false, secondaryLead = null, exportActive = false, refreshAttention = false, hairline = secondaryActions }) {
  return (
    <div className="hfa-list-header" style={{ marginBottom: 10 }}>
      {/* Row 1 — Title + count on the left; Quick Date chips with the
       *  action strip (Refresh → Export → Columns → … → Clear all
       *  filters) directly beneath them on the right. */}
      <div className="hfa-list-header-row1" style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between",
                    alignItems: "flex-start", gap: 12, marginBottom: 10,
                    paddingBottom: 10, borderBottom: `1px solid ${T.line}` }}>
        <div>
          <h1 style={{ fontSize: 34, margin: 0, letterSpacing: "-0.035em",
                       fontWeight: 800, color: T.ink }}>{title}</h1>
          {/* Active date filter — subtle two-line label (preset + range
              in DD/MM/YYYY) sitting immediately above the record count.
              Only rendered when a quick preset or custom range is
              actually applied; the section collapses cleanly when the
              filter is off (e.g. after Clear All). */}
          {activeDateChip && (
            <div data-testid="list-active-date" style={{ marginTop: 8, marginBottom: 4,
                                                          display: "flex", flexDirection: "column",
                                                          gap: 3, lineHeight: 1.2 }}>
              <span style={{ fontSize: 13, letterSpacing: "0.08em",
                             textTransform: "uppercase", color: T.brand,
                             fontWeight: 700 }}>{activeDateChip.label}</span>
              {activeDateChip.isSingleDay && onShiftSingleDay ? (
                // Single calendar date → wrap the DD/MM/YYYY label
                // with ‹ / › arrows. Delegates the actual state
                // mutation to the page (Members/Payments), which owns
                // its own `dateRange` reducer, so preset semantics and
                // downstream memos stay in sync.
                <SingleDayArrows
                  ymd={activeDateChip.singleDay}
                  label={activeDateChip.range}
                  testidPrefix="list-date-nav"
                  onShift={onShiftSingleDay}
                />
              ) : (
                <span style={{ fontSize: 15, color: T.ink, fontWeight: 600,
                               fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>{activeDateChip.range}</span>
              )}
            </div>
          )}
          <div data-testid="list-record-count" style={{ color: T.muted, fontSize: 12.5, marginTop: 4 }}>
            {typeof memberCount === "number" ? (
              count > memberCount ? (
                <><span style={{ color: T.brand, fontWeight: 700 }}>{count}</span> memberships
                  <span aria-hidden="true" style={{ display: "inline-block", margin: "0 8px", fontSize: 16, lineHeight: 0, verticalAlign: "middle", color: T.ink }}>•</span>
                  <span style={{ color: T.brand, fontWeight: 700 }}>{memberCount}</span> member{memberCount === 1 ? "" : "s"}</>
              ) : (
                <><span style={{ color: T.brand, fontWeight: 700 }}>{memberCount}</span> member{memberCount === 1 ? "" : "s"}</>
              )
            ) : (<>
            <span style={{ color: T.brand, fontWeight: 700 }}>{count}</span>
            {typeof totalCount === "number" && totalCount !== count
              ? <> of <span style={{ color: T.ink, fontWeight: 700 }}>{totalCount}</span> record{totalCount === 1 ? "" : "s"} <span style={{ color: T.muted, fontWeight: 500 }}>(column filter active)</span></>
              : <> record{count === 1 ? "" : "s"}</>}
            </>)}
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
          {topRight}
          <div className="hfa-list-header-actions" data-testid="list-header-actions"
               style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end", alignItems: "center" }}>
            {!secondaryActions && <RefreshIconButton onClick={onRefresh} />}
            {onExportFilter ? (
              /* Members / Payments — uniform action set: [Clear all filters →] Export (split) → … .
               * With `secondaryActions`, Refresh + Clear all filters move to their own row underneath.
               * Every button shares ACTION_BTN; enabled = orange outline, unavailable = grey. */
              <>
                {onClearFilters && !secondaryActions && (
                  <button data-testid="btn-clear-filters" data-hfa-btn data-hfa-outline={hairline ? "1" : undefined} onClick={onClearFilters}
                    title="Reset every filter and date range on this page. Records are never affected."
                    style={hairline ? outlineBtnStyle(true) : actionBtnStyle(true)}>Clear all filters</button>
                )}
                <div data-testid="export-split" data-hfa-export-split data-hfa-outline={hairline ? "1" : undefined}
                     data-active={hairline && exportActive ? "true" : undefined}
                     data-export-active={hairline && exportActive ? "true" : undefined}
                     aria-disabled={!exportEnabled}
                     style={{ ...(hairline ? outlineBtnStyle(exportEnabled, exportActive) : actionBtnStyle(exportEnabled)), padding: 0, gap: 0, overflow: "hidden" }}>
                  <button data-testid="admin-export" disabled={!exportEnabled}
                    onClick={exportEnabled ? onExport : undefined}
                    style={{ height: "100%", padding: "0 10px 0 14px", fontSize: 12, fontWeight: 600, letterSpacing: "0.02em",
                             background: "transparent", color: "inherit", border: "none",
                             cursor: exportEnabled ? "pointer" : "not-allowed", fontFamily: "inherit" }}>Export</button>
                  <button data-testid="admin-export-filter" onClick={onExportFilter}
                    title="Choose and order the columns included in the export"
                    aria-label="Export columns"
                    style={{ width: 24, height: 24, margin: "0 4px 0 0", borderRadius: 999, display: "inline-flex",
                             alignItems: "center", justifyContent: "center", padding: 0, flex: "0 0 auto",
                             background: hairline && exportActive ? "rgba(255,255,255,0.22)" : (exportEnabled ? "#FFF7ED" : "#EDEDEE"),
                             border: hairline && exportActive ? "1px solid rgba(255,255,255,0.7)" : (hairline && exportEnabled ? HAIR_BRAND : `1px solid ${exportEnabled ? T.brand : T.line}`),
                             color: "inherit", cursor: "pointer", fontFamily: "inherit" }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
                         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <polygon points="22 3 2 3 10 12.5 10 19 14 21 14 12.5 22 3" />
                    </svg>
                  </button>
                </div>
                {extra}
              </>
            ) : (
              <>
                <button data-testid="admin-export" data-hfa-btn onClick={onExport}
                  style={{ padding: "8px 14px", fontSize: 12, background: "white", color: T.slate,
                           border: `1px solid ${T.line}`, borderRadius: 8, cursor: "pointer", fontWeight: 600,
                           fontFamily: "inherit" }}>Export</button>
                {extra}
                {onClearFilters && (
                  <button data-testid="btn-clear-filters" data-hfa-btn onClick={onClearFilters}
                    title="Reset every filter and date range on this page. Records are never affected."
                    style={{ padding: "5px 12px", fontSize: 11, color: T.brand,
                             background: "white", border: `1px solid ${T.brand}`,
                             borderRadius: 999, cursor: "pointer", fontFamily: "inherit",
                             fontWeight: 600, letterSpacing: "0.02em" }}>
                    Clear all filters
                  </button>
                )}
              </>
            )}
          </div>
          {secondaryActions && (
            /* Row 3 (Members only) — frequently used actions, right-aligned under Import. */
            <div className="hfa-list-header-actions" data-testid="list-header-actions-secondary"
                 style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end", alignItems: "center" }}>
              {secondaryLead}
              <RefreshIconButton onClick={onRefresh} attention={refreshAttention} />
              {onClearFilters && (
                <button data-testid="btn-clear-filters" data-hfa-btn data-hfa-outline="1" onClick={onClearFilters}
                  title="Reset every filter and date range on this page. Records are never affected."
                  style={outlineBtnStyle(true)}>Clear all filters</button>
              )}
            </div>
          )}
        </div>
      </div>
      {/* Row 2 — filter toggle groups (via leftBlock), full width so
       *  Payment Status | Review Status | Membership Type sit on one
       *  line wherever the viewport allows. */}
      <div className="hfa-list-header-row2" style={{ minWidth: 0 }}>{leftBlock}</div>
    </div>
  );
}

/** Compact refresh icon button — consistent across Dashboard, Members
 *  and Payments so the admin never has to hunt for it. Uses an inline
 *  SVG (matches lucide's `RotateCw` geometry) so no additional icon
 *  dependency is pulled in, and the styling mirrors the existing
 *  outlined icon buttons already used elsewhere in the admin. `title`
 *  provides the accessible label since this is an icon-only button. */
function RefreshIconButton({ onClick, testid = "admin-refresh", attention = false }) {
  const [busy, setBusy] = useState(false);
  const handle = async () => {
    if (busy) return;
    setBusy(true);
    const started = Date.now();
    try { await onClick?.(); } catch (_) { /* surfaced by the caller */ }
    // Keep the spin visible for at least one rotation so the admin gets
    // clear feedback even when the refetch returns instantly.
    const wait = Math.max(0, 650 - (Date.now() - started));
    setTimeout(() => setBusy(false), wait);
  };
  return (
    <button
      data-testid={testid}
      data-hfa-btn
      data-hfa-refresh
      onClick={handle}
      title={attention ? "Newer data is available — click to refresh" : "Refresh"}
      aria-label="Refresh"
      aria-busy={busy}
      data-attention={attention ? "true" : undefined}
      style={{
        width: 32, height: 32, display: "inline-flex", flex: "0 0 auto",
        alignItems: "center", justifyContent: "center",
        padding: 0, border: attention ? HAIR_BRAND : `1px solid ${T.line}`, background: "white",
        borderRadius: 8, cursor: busy ? "progress" : "pointer", color: attention ? T.brand : T.slate,
        fontFamily: "inherit", transition: "border-color 150ms ease, color 150ms ease",
      }}
    >
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" strokeWidth="2.25" strokeLinecap="round"
           strokeLinejoin="round" aria-hidden="true"
           style={{ animation: busy ? "hfa-spin 0.65s linear infinite" : "none" }}>
        <path d="M21 12a9 9 0 1 1-3-6.7" />
        <polyline points="21 3 21 9 15 9" />
      </svg>
    </button>
  );
}

/** Return the string that should appear in an exported cell for a given
 *  column key + raw row. Keeps date/time formatting consistent with the
 *  Admin UI (DD/MM/YYYY, HH:MM:SS). */
function _exportCellValue(row, colKey) {
  if (colKey === "amount_paise") return row.amount_paise ?? 0;
  if (colKey === "paid_at")      return fmtDate(row.paid_at);
  if (colKey === "paid_at_time") return fmtTime(row.paid_at);
  if (colKey === "membership_start_date") return fmtDate(row.membership_start_date);
  if (colKey === "applied_time") return _fmtClockToAmPm(row.applied_time);
  if (colKey === "applied_date") return fmtDate(row.applied_date);
  if (colKey === "updated_at") {
    if (!row.updated_at) return "";
    return fmtDateTime(row.updated_at);
  }
  if (colKey === "created_at") {
    if (!row.created_at) return "";
    return fmtDateTime(row.created_at);
  }
  if (colKey === "submitted_at") {
    if (!row.submitted_at) return "";
    return fmtDateTime(row.submitted_at);
  }
  const v = row[colKey];
  if (Array.isArray(v)) return v.join(" | ");
  return v ?? "";
}

function buildCsv(rows, cols) {
  const escape = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    if (s.includes(",") || s.includes("\n") || s.includes('"')) return '"' + s.replace(/"/g, '""') + '"';
    return s;
  };
  const lines = [];
  lines.push(cols.map((c) => escape(c.label)).join(","));
  rows.forEach((r) => {
    lines.push(cols.map((c) => escape(_exportCellValue(r, c.key))).join(","));
  });
  return "\ufeff" + lines.join("\r\n");
}

function downloadCsv(name, content) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(a.href);
}

/** Build an .xlsx workbook from filtered rows and export it.
 *  Column order and visibility already respect user personalisation —
 *  this is enforced by the caller passing the *visible* cols in order. */
async function downloadXlsx(name, rows, cols) {
  const XLSX = await import("xlsx");
  const data = [
    cols.map((c) => c.label),
    ...rows.map((r) => cols.map((c) => _exportCellValue(r, c.key))),
  ];
  const ws = XLSX.utils.aoa_to_sheet(data);
  // Reasonable column widths for readability.
  ws["!cols"] = cols.map((c) => ({ wpx: Math.min(240, Math.max(80, c.w || 120)) }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Data");
  XLSX.writeFile(wb, name);
}

/** Modal that lets the operator pick .xlsx (recommended) or .csv. */
function ExportPickerModal({ onPickXlsx, onPickCsv, onClose, count }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 90,
                  display: "flex", alignItems: "center", justifyContent: "center" }}
      data-testid="export-picker" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: "white", borderRadius: 14, padding: 26, maxWidth: 480, width: "92%",
                 boxShadow: "0 32px 64px rgba(0,0,0,0.3)" }}>
        <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 4 }}>Export</div>
        <div style={{ fontSize: 12.5, color: T.muted, marginBottom: 18 }}>
          Exporting <b>{count}</b> filtered row{count === 1 ? "" : "s"} using your current column layout.
        </div>
        <button data-testid="export-xlsx" onClick={onPickXlsx}
          style={{ display: "block", width: "100%", textAlign: "left",
                   padding: "14px 16px", marginBottom: 10, background: "white",
                   border: `2px solid ${T.brand}`, borderRadius: 12, cursor: "pointer",
                   fontFamily: "inherit" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: T.ink }}>Excel (.xlsx)</div>
              <div style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>Ready for Excel, Google Sheets, Numbers.</div>
            </div>
            <span style={{ padding: "3px 8px", background: T.brand, color: "white",
                           borderRadius: 999, fontSize: 10, fontWeight: 700, letterSpacing: "0.06em" }}>RECOMMENDED</span>
          </div>
        </button>
        <button data-testid="export-csv" onClick={onPickCsv}
          style={{ display: "block", width: "100%", textAlign: "left",
                   padding: "14px 16px", marginBottom: 14, background: "white",
                   border: `1px solid ${T.line}`, borderRadius: 12, cursor: "pointer",
                   fontFamily: "inherit" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.ink }}>CSV (.csv)</div>
          <div style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>Universal plain-text — opens in any spreadsheet tool.</div>
        </button>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button data-testid="export-cancel" onClick={onClose}
            style={{ padding: "8px 14px", background: "white", color: T.slate,
                     border: `1px solid ${T.line}`, borderRadius: 8, cursor: "pointer",
                     fontFamily: "inherit", fontSize: 12 }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

/* =====================================================================
 * Members / Exceptions pages
 * ===================================================================== */
/* =====================================================================
 * Members — MASTER member database with intelligent Payment × Review
 * status filters. Merges the legacy Members endpoint (pending_review /
 * onboarded / rejected) with the Unpaid Exceptions endpoint into a
 * single dataset so the Exceptions page can be retired.
 *
 * Filter matrix (impossible combinations are visually disabled and
 * cannot be selected):
 *   Payment = all       → Review = all | pending_review | onboarded | rejected
 *   Payment = paid      → Review = all | pending_review | onboarded          (rejected disabled)
 *   Payment = unpaid    → Review = all only (rest disabled — never reviewed)
 *   Payment = refunded  → Review = all | rejected                             (pending / onboarded disabled)
 *
 * Reverse: choosing a Review Status auto-sets Payment Status.
 *   Review = pending_review → Payment = paid
 *   Review = onboarded      → Payment = paid
 *   Review = rejected       → Payment = refunded
 *   Review = all            → Payment = all
 * =================================================================== */

const P_OPTS = [
  // Admin Portal = paid memberships only — unpaid / abandoned / retry
  // applications are excluded from every Admin view, so no Unpaid option.
  { k: "all",            label: "All" },
  { k: "paid",           label: "Paid" },
  { k: "to_be_refunded", label: "To Be Refunded" },
  { k: "refunded",       label: "Refunded" },
  { k: "no_refund",      label: "No Refund" },
];
/** Payment statuses that mean money landed (counts toward Gross Revenue). */
const PAID_LIKE = new Set(["paid", "to_be_refunded", "refunded", "no_refund"]);
const P_OPTS_MEMBERS = P_OPTS; // Members mirrors Payments — same options, same behaviour.
const R_OPTS = [
  { k: "all",             label: "All" },
  { k: "pending_review",  label: "Review Pending" },
  { k: "onboarded",       label: "Onboarded" },
  { k: "rejected",        label: "Rejected" },
  { k: "removed",         label: "Removed" },
];

/** Which R options are enabled for a given P (used to grey the buttons
 *  in the UI). Members-scoped rules per the redesign:
 *   • P = unpaid       → only R = All is selectable
 *   • P = paid | all   → every R is selectable
 *  Refunded is no longer a Members-page concept, so the old
 *  "refunded implies rejected" rule has been removed. */
function _allowedReviewsForP(p) {
  if (p === "unpaid") return new Set(["all"]);
  return new Set(["all", "pending_review", "onboarded", "rejected", "removed"]);
}
/** Members quick-filter linking — compatible statuses in the other filter. */
const _R_TO_P = {
  pending_review: ["paid"],
  onboarded:      ["paid"],
  rejected:       ["refunded", "to_be_refunded"],
  removed:        ["refunded", "to_be_refunded", "no_refund"],
};
const _P_TO_R = {
  paid:           ["pending_review", "onboarded"],
  refunded:       ["rejected", "removed"],
  to_be_refunded: ["rejected", "removed"],
  no_refund:      ["removed"],
};
function _linkedUnion(set, map) {
  if (set.has("all") || set.size === 0) return new Set(["all"]);
  const out = new Set();
  set.forEach((k) => (map[k] || []).forEach((v) => out.add(v)));
  return out.size ? out : new Set(["all"]);
}
const _linkedPForR = (rSet) => _linkedUnion(rSet, _R_TO_P);
const _linkedRForP = (pSet) => _linkedUnion(pSet, _P_TO_R);
/** Reverse — Members: ANY R change (including R=All) implies P=Paid.
 *  Review Status only makes sense against paid applications. */
function _pForR(_r) { return "paid"; }

/** Multi-selectable segmented toggle.
 *
 *  Interaction model:
 *   • Normal click / single tap → REPLACE the selection with the clicked key.
 *   • Ctrl/⌘-click OR double-tap → TOGGLE the clicked key in/out of the
 *     current selection without wiping the others.
 *   • `disabledSet` marks options that are currently invalid under the
 *     other filter (visually greyed, non-clickable).
 *
 *  Under the hood the selection is a `Set` of keys. A selection of
 *  `{"all"}` (or empty) is treated as "match everything".
 */
const MT_OPTS = [
  { k: "all", label: "All" },
  { k: "single", label: "Single" },
  { k: "multiple", label: "Multiple" },
];
function _readMtPref() {
  const v = _prefGet(PREF_KEYS.defaultMembershipType, "all");
  return ["all", "single", "multiple"].includes(v) ? v : "all";
}
function _mtMatches(mt, type) { return mt === "all" || (type || "single") === mt; }

function MembershipTypeFilter({ value, onChange, testid }) {
  return (
    <FilterGroupCard heading="City Chapters">
      <SegToggle options={MT_OPTS} valueSet={new Set([value])} onSelect={(k) => onChange(k)} testid={testid} />
    </FilterGroupCard>
  );
}

function SegToggle({ options, valueSet, onSelect, disabledSet, testid }) {
  const lastTapRef = useRef({ key: null, at: 0 });
  const preTapRef  = useRef(null);   // snapshot of valueSet before the first tap of a potential double-tap
  const isSelected = (k) => valueSet.has(k) || (valueSet.has("all") && k === "all");
  return (
    <div data-testid={testid} data-hfa-seg-toggle
      style={{ display: "inline-flex", flexWrap: "wrap", gap: 3, background: T.card,
               border: `1px solid ${T.line}`, borderRadius: 999, padding: 3,
               boxShadow: "0 1px 2px rgba(0,0,0,0.02)", maxWidth: "100%" }}>
      {options.map((o) => {
        const disabled = disabledSet && disabledSet.has(o.k);
        const active = isSelected(o.k);
        const bg = active ? T.brand : "transparent";
        const color = disabled ? "rgba(0,0,0,0.28)" : active ? "white" : T.slate;
        // Handler distinguishes "replace" (normal click) from "toggle"
        // (Ctrl / ⌘) and rebuilds from the pre-tap snapshot for double-taps.
        const handleClick = (e) => {
          if (disabled) return;
          const now = Date.now();
          const doubleTap = lastTapRef.current.key === o.k
            && (now - lastTapRef.current.at) < 320;
          lastTapRef.current = { key: o.k, at: now };
          const modifier = e.ctrlKey || e.metaKey;
          if (doubleTap && preTapRef.current) {
            // Rebuild against the pre-first-tap set, then toggle o.k.
            onSelect(o.k, "toggle-from", preTapRef.current);
            preTapRef.current = null;
          } else if (modifier) {
            onSelect(o.k, "toggle");
          } else {
            // Snapshot the state BEFORE we replace it, so a follow-up tap
            // within the double-tap window can restore & toggle.
            preTapRef.current = new Set(valueSet);
            onSelect(o.k, "replace");
          }
        };
        return (
          <button key={o.k} data-testid={`${testid}-${o.k}`}
            onClick={handleClick}
            disabled={disabled}
            title={active
              ? "Click to select only this · Ctrl/⌘-click (or double-tap) to remove from selection"
              : "Click to select · Ctrl/⌘-click (or double-tap) to add to selection"}
            style={{
              padding: "6px 14px", fontSize: 12, fontWeight: active ? 600 : 500,
              borderRadius: 999, border: "none", fontFamily: "inherit",
              background: bg, color,
              cursor: disabled ? "not-allowed" : "pointer",
              opacity: disabled ? 0.55 : 1,
              transition: "background 0.15s ease, color 0.15s ease",
              boxShadow: active ? "0 2px 8px rgba(234,88,12,0.28)" : "none",
              userSelect: "none", touchAction: "manipulation",
            }}
            onMouseEnter={(e) => { if (!disabled && !active) e.currentTarget.style.background = T.brandTint; }}
            onMouseLeave={(e) => { if (!disabled && !active) e.currentTarget.style.background = "transparent"; }}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** Preferences keys used by Members (kept next to component for locality). */
const PREF_ROWS_PER_VIEW = "hfa-admin-pref-page-size";     // 25|50|100|All
const PREF_HIDDEN_COLS   = "hfa-admin-pref-hidden-cols";   // JSON array of colKeys
const PREF_COL_ORDER     = "hfa-admin-pref-col-order";     // JSON array of colKeys, in preferred order
const PREF_SAVED_VIEWS   = "hfa-admin-pref-saved-views";   // JSON array of view objects

function _readHiddenCols() {
  try {
    const raw = localStorage.getItem(PREF_HIDDEN_COLS);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch (_) { return new Set(); }
}
function _writeHiddenCols(setOfKeys) {
  try { localStorage.setItem(PREF_HIDDEN_COLS, JSON.stringify([...setOfKeys])); } catch (_) {}
}
function _readColOrder() {
  try {
    const raw = localStorage.getItem(PREF_COL_ORDER);
    return raw ? JSON.parse(raw) : null;
  } catch (_) { return null; }
}
function _writeColOrder(arr) {
  try { localStorage.setItem(PREF_COL_ORDER, JSON.stringify(arr)); } catch (_) {}
}

/* ---------- Excel-style column layout (widths / freeze) --------------
 * Widths are stored per-table so Members and Payments have independent
 * layouts. Frozen-count is a single global preference shared across
 * every table (default 3 — matches the Selection Checkbox + Member ID +
 * Full Name identifiers). All state lives in localStorage; no server
 * dependency. */
const PREF_COL_WIDTHS      = "hfa-admin-pref-col-widths";     // { [tableKey]: { [colKey]: number } }
const PREFS_HYDRATED_EVENT = "hfa-admin-prefs-hydrated";
const PREF_FROZEN_COUNT    = "hfa-admin-pref-frozen-count";   // integer 0..3
const PREF_TBL_COL_ORDER   = "hfa-admin-pref-tbl-col-order";  // { [tableKey]: [colKeys] } (used only for tables that don't have their own legacy key)
const DEFAULT_FROZEN_COUNT = 3;
const FROZEN_CHANGE_EVENT  = "hfa-frozen-count-change";
/** Global "something changed on a member or a payment" signal. Any
 *  mutation that could affect Members / Payments / Dashboard must
 *  dispatch this event so every mounted page re-fetches immediately.
 *  Fires whenever the Members Drawer flips Review Status or Payment
 *  Status, whenever a remark is added, whenever contact name changes,
 *  and whenever the bulk toolbar performs an action.
 *
 *  Cross-tab delivery: broadcasts also flow over a BroadcastChannel
 *  (with a localStorage fallback for browsers that don't support it)
 *  so a change made in one admin tab reaches every other open admin
 *  tab within milliseconds — no polling required. */
const DATA_CHANGED_EVENT = "hfa-admin-data-changed";
const _bc = (() => {
  try { return new BroadcastChannel("hfa-admin-sync"); } catch (_) { return null; }
})();
if (_bc) {
  _bc.onmessage = (m) => {
    // Cross-tab mutation → invalidate this tab's SWR cache before
    // dispatching the event so subscribers re-fetch fresh data.
    invalidateAdminCache();
    try { window.dispatchEvent(new CustomEvent(DATA_CHANGED_EVENT, { detail: m.data || {} })); }
    catch (_) { /* noop */ }
  };
}
// Fallback for older browsers (no BroadcastChannel API): use a
// localStorage "ping" key — writes fire a `storage` event in every
// OTHER tab of the same origin.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key !== "hfa_admin_sync_ping" || !e.newValue) return;
    try {
      invalidateAdminCache();
      const parsed = JSON.parse(e.newValue);
      window.dispatchEvent(new CustomEvent(DATA_CHANGED_EVENT, { detail: parsed || {} }));
    } catch (_) { /* noop */ }
  });
}
const broadcastDataChanged = (detail) => {
  const payload = detail || {};
  // Single-record mutations patch just that row in the shared Members
  // feed (server-authoritative) and only mark the Payments feed stale so it
  // revalidates silently. Everything else (bulk, import, manual refresh,
  // safety poll) marks both feeds stale — pages keep showing current data
  // and refresh in the background.
  if (payload.app_uuid && _sharedApi) {
    const pe = _ADMIN_CACHE.get(PAY_KEY); if (pe) pe.t = 0;
    refreshMemberRow(_sharedApi, payload.app_uuid).catch(() => invalidateAdminCache());
  } else {
    invalidateAdminCache();
  }
  // 1) Same-tab: dispatch synchronously so listeners refresh in the
  //    very next tick.
  try {
    window.dispatchEvent(new CustomEvent(DATA_CHANGED_EVENT, { detail: { ...payload, _sameTab: true } }));
  } catch (_) { /* noop */ }
  // 2) Cross-tab: BroadcastChannel if available, otherwise a
  //    localStorage ping (see the `storage` event listener above).
  try {
    if (_bc) _bc.postMessage(payload);
    else localStorage.setItem("hfa_admin_sync_ping", JSON.stringify({ ...payload, _t: Date.now() }));
  } catch (_) { /* noop */ }
};

function _readColWidths(tableKey) {
  try {
    const raw = localStorage.getItem(PREF_COL_WIDTHS);
    if (!raw) return {};
    const all = JSON.parse(raw);
    return (all && typeof all === "object" && all[tableKey]) || {};
  } catch (_) { return {}; }
}
function _writeColWidths(tableKey, widths) {
  try {
    const raw = localStorage.getItem(PREF_COL_WIDTHS);
    const all = (raw && JSON.parse(raw)) || {};
    // Merge per column so a stale in-memory map can never drop widths
    // saved for other columns (e.g. hydrated from the server later).
    all[tableKey] = { ...(all[tableKey] || {}), ...widths };
    localStorage.setItem(PREF_COL_WIDTHS, JSON.stringify(all));
  } catch (_) {}
}
function _readFrozenCount(tableKey) {
  try {
    const raw = localStorage.getItem(PREF_FROZEN_COUNT);
    if (!raw) return DEFAULT_FROZEN_COUNT;
    // v2 envelope: per-table integer.
    if (raw.startsWith("{")) {
      const all = JSON.parse(raw);
      if (all && typeof all === "object" && Number.isInteger(all[tableKey])) {
        const n = all[tableKey];
        return n >= 0 && n <= 3 ? n : DEFAULT_FROZEN_COUNT;
      }
      return DEFAULT_FROZEN_COUNT;
    }
    // v1 legacy: bare integer applied to every table. Kept for BC.
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n >= 0 && n <= 3 ? n : DEFAULT_FROZEN_COUNT;
  } catch (_) { return DEFAULT_FROZEN_COUNT; }
}
function _writeFrozenCount(tableKey, n) {
  try {
    const raw = localStorage.getItem(PREF_FROZEN_COUNT);
    let all = {};
    if (raw && raw.startsWith("{")) {
      try { all = JSON.parse(raw) || {}; } catch (_) { all = {}; }
    }
    all[tableKey] = Math.max(0, Math.min(3, parseInt(n, 10) || 0));
    localStorage.setItem(PREF_FROZEN_COUNT, JSON.stringify(all));
    // Broadcast so already-mounted TableViews pick up the change without
    // waiting for a page reload.
    window.dispatchEvent(new CustomEvent(FROZEN_CHANGE_EVENT, { detail: { tableKey } }));
  } catch (_) {}
}
function _readTableColOrder(tableKey) {
  try {
    const raw = localStorage.getItem(PREF_TBL_COL_ORDER);
    if (!raw) return null;
    const all = JSON.parse(raw);
    return (all && typeof all === "object" && Array.isArray(all[tableKey])) ? all[tableKey] : null;
  } catch (_) { return null; }
}
function _writeTableColOrder(tableKey, arr) {
  try {
    const raw = localStorage.getItem(PREF_TBL_COL_ORDER);
    const all = (raw && JSON.parse(raw)) || {};
    all[tableKey] = arr;
    localStorage.setItem(PREF_TBL_COL_ORDER, JSON.stringify(all));
  } catch (_) {}
}
/** One-shot "reset layout" — clears order + widths + frozen count for a
 *  table AND any hidden-columns preference (legacy shared key).
 *  Filters/searches are NEVER touched. */
function _resetTableLayout(tableKey) {
  try {
    // Widths envelope
    const wraw = localStorage.getItem(PREF_COL_WIDTHS);
    if (wraw) {
      const all = JSON.parse(wraw) || {};
      delete all[tableKey];
      localStorage.setItem(PREF_COL_WIDTHS, JSON.stringify(all));
    }
    // Per-table order envelope
    const oraw = localStorage.getItem(PREF_TBL_COL_ORDER);
    if (oraw) {
      const all = JSON.parse(oraw) || {};
      delete all[tableKey];
      localStorage.setItem(PREF_TBL_COL_ORDER, JSON.stringify(all));
    }
    // Per-table frozen count envelope
    const fraw = localStorage.getItem(PREF_FROZEN_COUNT);
    if (fraw && fraw.startsWith("{")) {
      const all = JSON.parse(fraw) || {};
      delete all[tableKey];
      localStorage.setItem(PREF_FROZEN_COUNT, JSON.stringify(all));
    }
    // Legacy Members-only order + hidden keys (single shared table on
    // launch — safe to clear when resetting the Members layout).
    if (tableKey === "members") {
      localStorage.removeItem(PREF_COL_ORDER);
      localStorage.removeItem(PREF_HIDDEN_COLS);
    }
  } catch (_) {}
}
/** Reorder an array of column definitions according to a saved keys order.
 *  Any newly-introduced columns (not in the saved order) go at the end. */
function _applyColOrder(allCols, savedOrder) {
  if (!savedOrder || !savedOrder.length) return allCols;
  const byKey = Object.fromEntries(allCols.map((c) => [c.key, c]));
  const seen = new Set();
  const out = [];
  savedOrder.forEach((k) => {
    if (byKey[k] && !seen.has(k)) { out.push(byKey[k]); seen.add(k); }
  });
  allCols.forEach((c) => { if (!seen.has(c.key)) out.push(c); });
  return out;
}
function _readSavedViews() {
  try {
    const raw = localStorage.getItem(PREF_SAVED_VIEWS);
    return raw ? JSON.parse(raw) : [];
  } catch (_) { return []; }
}
function _writeSavedViews(arr) {
  try { localStorage.setItem(PREF_SAVED_VIEWS, JSON.stringify(arr)); } catch (_) {}
}
function _readRowsPerView() {
  try { return localStorage.getItem(PREF_ROWS_PER_VIEW) || "All"; } catch (_) { return "All"; }
}

function Members({ api, onOpenRow }) {
  const [members, setMembers] = useState([]);
  const [q, setQ] = useState("");
  const [err, setErr] = useState("");
  const [pFilter, setPFilter] = useState(() => {
    const v = _prefGet(PREF_KEYS.defaultPaymentMembers, "all");
    return new Set([["all","paid"].includes(v) ? v : "all"]);
  });
  const [rFilter, setRFilter] = useState(() => {
    const v = _prefGet(PREF_KEYS.defaultReviewMembers, "all");
    const allowedKeys = ["all","pending_review","onboarded","rejected","removed"];
    return new Set([allowedKeys.includes(v) ? v : "all"]);
  });
  const [mtFilter, setMtFilter] = useState(_readMtPref);
  const [selected, setSelected] = useState(() => new Set());
  const [hiddenCols, setHiddenCols] = useState(() => _readHiddenCols());
  const [colOrder, setColOrder] = useState(() => _readColOrder());
  const memberCols = useMemo(() => _applyColOrder(MEMBER_COLS, colOrder), [colOrder]);
  const [savedViews, setSavedViews] = useState(() => _readSavedViews());
  const [showColMgr, setShowColMgr] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [exportTargetRows, setExportTargetRows] = useState(null); // when bulk export
  const [busy, setBusy] = useState(false);
  // Quick Date Filter — always filters by Payment Date. Seeded from
  // the Members-specific default in Settings. Independent of Dashboard
  // and Payments.
  const [dateRange, setDateRange] = useGlobalDateRange(PREF_KEYS.defaultDateMembers);
  // Which application-timestamp the Quick Date Filter is applied
  // against. "payment_date" is the default and the only mode
  // reachable through the Members-page UI. Dashboard's "Refunded" /
  // "To Be Refunded" drill-downs set this to "rejected_at" via the
  // deeplink so the same date range window filters on Rejected Date
  // instead of Payment Date. Any user-driven change to the date range
  // in Members resets this back to "payment_date".
  const [dateBasis, setDateBasis] = useState("payment_date");
  // Wrap setDateRange so any UI-triggered date change silently
  // restores the default payment-date basis. This preserves the
  // "one-shot" nature of the deeplink override without adding any new
  // controls to the Members filter UI.
  const setDateRangeUI = useCallback((v) => {
    setDateBasis("payment_date");
    setDateRange(v);
  }, []);
  // Monotonic counter bumped by "Clear all filters" so TableView wipes
  // its internal column-filter/dropdown state.
  const [clearSig, setClearSig] = useState(0);
  // Bumped by the Refresh button so any manual column sort is dropped.
  const [sortResetSig, setSortResetSig] = useState(0);
  const [multiSort, setMultiSort] = useState(false);

  // Consume any deep-link handed off by the Dashboard's Eye-icon click.
  // Runs exactly once on mount — the deeplink payload is removed from
  // sessionStorage as it's read, so navigating back to Members later
  // (via the sidebar) keeps whatever filters the admin last chose.
  //
  // Contract: whenever a deeplink is present, we EXPLICITLY reset every
  // filter it addresses to a canonical value so the drill-down count
  // always matches the Dashboard card that produced it — regardless of
  // whatever Settings default the operator has persisted. Drills that
  // do NOT specify a review filter deliberately reset rFilter to "all"
  // (Revenue / Net Revenue span every review state).
  useEffect(() => {
    const link = _consumeMembersDeeplink();
    if (!link) return;
    const REVIEWS = ["pending_review", "onboarded", "rejected", "removed"];
    const reviewList = (Array.isArray(link.review) ? link.review : [link.review]).filter((k) => REVIEWS.includes(k));
    const hasReview = reviewList.length > 0;
    const hasPayFilter = Array.isArray(link.payment_filter) && link.payment_filter.length > 0;
    if (hasReview) {
      setRFilter(new Set(reviewList));
      // Workflow drills (Review Pending / Onboarded / Rejected / Removed)
      // are only meaningful on paid applications — align pFilter to
      // "paid" unless the drill-down explicitly overrides it below.
      setPFilter(new Set(["paid"]));
    } else if (hasPayFilter) {
      // Revenue-only drill (Gross Revenue / Net Revenue): span every
      // review state so the visible count matches the Dashboard's
      // Payment-Date aggregate exactly. Never inherit a stale Settings
      // default like "rejected" or "onboarded".
      setRFilter(new Set(["all"]));
    }
    if (hasPayFilter) {
      const valid = link.payment_filter.filter((k) =>
        ["all", ...ADMIN_PAY_STATUSES].includes(k)
      );
      if (valid.length) setPFilter(new Set(valid));
    }
    // City Chapters mirrors the Dashboard's own selection (defaults to All).
    setMtFilter(["all", "single", "multiple"].includes(link.mt) ? link.mt : "all");
    if (link.date_preset) {
      const preset = link.date_preset;
      const from = link.date_from;
      const to = link.date_to;
      if (from && to) setDateRange({ preset, from, to });
      else            setDateRange({ preset, ...computeRange(preset) });
    }
    // Optional date-basis override — used by the Dashboard Refunded /
    // To Be Refunded drills to reinterpret the date range against
    // rejected_at instead of paid_at. Any other value falls back to
    // the default payment_date basis.
    if (link.date_basis === "rejected_at") {
      setDateBasis("rejected_at");
    } else {
      setDateBasis("payment_date");
    }
    // Clear active column filters so the drill-down starts clean.
    setClearSig((n) => n + 1);
  }, []);

  const applyPair = useCallback((mRows, pRows) => {
    // Build a "refunded application ids" set from the payments feed so
    // Members can display Refunded as a live-mirror filter option too
    // (matches Payments exactly — no independent calculation).
    const refunded = new Set();
    (pRows || []).forEach((p) => {
      if ((p.status || "").toLowerCase() === "refunded" && p.application_id) {
        refunded.add(p.application_id);
      }
    });
    const enrich = (r) => ({
      ...r,
      star_member: r.is_star_member ? "Yes" : "No",
      _display_payment_status: refunded.has(r.application_id)
        ? "refunded"
        : (r.payment_status || "unpaid"),
    });
    setMembers((mRows || []).map(enrich));
  }, []);
  // Stale indicator for the Refresh button: the displayed Members feed
  // (shared store) is older than its freshness window. Re-evaluated on a
  // cheap local timer and whenever the store updates — no network involved.
  const [loadedAt, setLoadedAt] = useState(() => _storeFetchedAt(MEM_KEY));
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => { const id = setInterval(() => setNowTick(Date.now()), 30_000); return () => clearInterval(id); }, []);
  const load = useCallback(async ({ force } = {}) => {
    setErr("");
    // Master Search is purely client-side — the shared store always serves.
    const c = cachedPair();
    if (c) applyPair(c.m.rows, c.p.rows);
    try {
      const { m, p } = await loadAdminData(api, { force });
      applyPair(m.rows, p.rows);
      setLoadedAt(_storeFetchedAt(MEM_KEY));
      setNowTick(Date.now());
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load.");
    }
  }, [api, applyPair]);
  useEffect(() => { load(); }, [load]);
  const isStale = loadedAt > 0 && nowTick - loadedAt > STALE_MS;
  // Row-level mutations update the shared store → repaint from it (no
  // network); bulk/import/refresh events force a silent background reload.
  useEffect(() => {
    const onStore = () => { const c = cachedPair(); if (c) { applyPair(c.m.rows, c.p.rows); setLoadedAt(_storeFetchedAt(MEM_KEY)); setNowTick(Date.now()); } };
    const onChange = (e) => { if (!e?.detail?.app_uuid) load({ force: true }); };
    window.addEventListener(STORE_EVENT, onStore);
    window.addEventListener(DATA_CHANGED_EVENT, onChange);
    return () => {
      window.removeEventListener(STORE_EVENT, onStore);
      window.removeEventListener(DATA_CHANGED_EVENT, onChange);
    };
  }, [load, applyPair]);
  useEffect(() => _writeHiddenCols(hiddenCols), [hiddenCols]);
  useEffect(() => { if (colOrder) _writeColOrder(colOrder); }, [colOrder]);
  useEffect(() => _writeSavedViews(savedViews), [savedViews]);

  /** Normalise every row so both the on-screen pill and the column
   *  dropdown filter agree on a canonical set of values:
   *   • `payment_status` ∈ { paid, refunded }
   *   • `status` ∈ { pending_review, onboarded, rejected, removed }
   *  The Admin Portal shows paid memberships only — unpaid / abandoned
   *  applications are excluded at the API. This does NOT touch the
   *  underlying documents in Atlas — purely a presentation projection. */
  const allRows = useMemo(() => members.map((r) => ({
    ...r, payment_status: r.payment_status || "unpaid",
  })), [members]);
  // Master Search bags: built once per dataset, not per keystroke.
  const masterBags = useMemo(() => new WeakMap(allRows.map((r) => [r, _memberMasterBag(r)])), [allRows]);

  /** Union of allowed review keys across every selected payment key. */
  const _allowedRFor = (pSet) => {
    if (pSet.has("all") || pSet.size === 0)
      return new Set(["all", "pending_review", "onboarded", "rejected", "removed"]);
    const out = new Set(["all"]);
    pSet.forEach((p) => {
      const a = _allowedReviewsForP(p);
      a.forEach((v) => out.add(v));
    });
    return out;
  };

  const handlePSelect = (k, mode, snapshot) => {
    setPFilter((prev) => {
      let next;
      if (mode === "replace") {
        next = new Set([k]);
      } else if (mode === "toggle-from") {
        // Rebuild against the pre-first-tap set, then toggle k.
        next = new Set(snapshot || []);
        if (k === "all") { next = new Set(["all"]); }
        else if (next.has(k)) { next.delete(k); if (next.size === 0 || (next.size === 1 && next.has("all"))) next = new Set(["all"]); }
        else { next.delete("all"); next.add(k); }
      } else {
        next = new Set(prev);
        if (k === "all") { next = new Set(["all"]); }
        else if (next.has(k)) { next.delete(k); if (next.size === 0 || next.size === 1 && next.has("all")) next = new Set(["all"]); }
        else { next.delete("all"); next.add(k); }
      }
      // Linked filters: Review Status becomes the union of statuses
      // compatible with every selected Payment Status (All ↔ All).
      setRFilter(_linkedRForP(next));
      return next;
    });
  };
  const handleRSelect = (k, mode, snapshot) => {
    setRFilter((prev) => {
      let next;
      if (mode === "replace") {
        next = new Set([k]);
      } else if (mode === "toggle-from") {
        next = new Set(snapshot || []);
        if (k === "all") { next = new Set(["all"]); }
        else if (next.has(k)) { next.delete(k); if (next.size === 0) next.add("all"); }
        else { next.delete("all"); next.add(k); }
      } else {
        next = new Set(prev);
        if (k === "all") { next = new Set(["all"]); }
        else if (next.has(k)) { next.delete(k); if (next.size === 0) next.add("all"); }
        else { next.delete("all"); next.add(k); }
      }
      // Linked filters: Payment Status becomes the union of statuses
      // compatible with every selected Review Status (All ↔ All).
      setPFilter(_linkedPForR(next));
      return next;
    });
  };

  const disabledR = new Set(
    R_OPTS.map((o) => o.k).filter((k) => !_allowedRFor(pFilter).has(k))
  );

  const rowsPerView = _readRowsPerView();
  const cap = rowsPerView === "All" ? Infinity : Number(rowsPerView) || Infinity;

  const filteredAll = useMemo(() => {
    const pAll = pFilter.has("all") || pFilter.size === 0;
    const rAll = rFilter.has("all") || rFilter.size === 0;
    // Pick the calendar-key accessor for whichever basis the current
    // deeplink (or UI, defaulting to payment_date) asks for. Rows
    // whose accessor returns "" never match any date range — this is
    // deliberate: an unpaid row has no payment date, and a
    // never-rejected row has no rejected date.
    // Historical Excel imports: when no Payment Date was recorded, their
    // Applied Date positions them in time (never the import date).
    const dateAccessor = dateBasis === "rejected_at" ? _rejectedDateOf : _memberDateOf;
    const terms = _masterTerms(q);
    return allRows.filter((r) => {
      // Belt-and-suspenders archived guard. `/members` already filters
      // archived server-side, but this second gate ensures the Members
      // page can NEVER show an archived row even if a future endpoint
      // change leaks one through. Keeps the Dashboard drill-down
      // counts and the Members visible count exactly aligned.
      if (r.is_archived) return false;
      if (!pAll && !pFilter.has(r._display_payment_status || r.payment_status)) return false;
      if (!rAll && !rFilter.has(r.status)) return false;
      if (!_mtMatches(mtFilter, r.membership_type)) return false;
      // Quick Date Filter — either Payment Date (default) or Rejected
      // Date (Dashboard drill-down mode). "All Time" also keeps undated
      // historical (Excel-imported) members — it means every record.
      if (dateRange && dateRange.from && dateRange.to
          && !(dateRange.preset === "all_time" && dateBasis !== "rejected_at" && !dateAccessor(r))
          && !inRange(dateAccessor(r), dateRange)) return false;
      // Global search — case-insensitive substring across the
      // Members-side searchable columns AND phone-digit normalised
      // matching for whatsapp/contact fields. Applied AFTER the
      // structural filters so counts/badges reflect the currently
      // visible subset (spec §3.6, §3.8).
      if (terms.length && !_matchesMaster(masterBags.get(r), terms)) return false;
      return true;
    });
  }, [allRows, masterBags, pFilter, rFilter, mtFilter, dateRange, dateBasis, q]);
  const displayRows = filteredAll;   // TableView caps rows internally via rowsCap

  // ---- Bulk actions ------------------------------------------------
  const selectedRows = useMemo(
    () => filteredAll.filter((r) => selected.has(r.id)),
    [filteredAll, selected]
  );

  // Row count actually visible after every layer of filtering — top
  // toggles, global search, column text search and column dropdown
  // multi-selects. Seeded from filteredAll so the header shows a
  // sensible number before the first TableView render callback lands.
  // TableView owns the source of truth via `onFilteredCountChange`
  // (fires whenever the underlying rows OR the column filters change),
  // so a parent-side "sync to filteredAll.length" would only overwrite
  // the correct value the callback just delivered — hence intentionally
  // omitted.
  const [visibleCount, setVisibleCount] = useState(filteredAll.length);
  const [memberCount, setMemberCount] = useState(0);
  // Exact rows the table currently shows (quick filters + column filters).
  const [visibleRows, setVisibleRows] = useState(null);
  const [dupOpen, setDupOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const dupSource = selected.size === 1 ? filteredAll.find((r) => r.id === [...selected][0]) : null;

  // Selection auto-cleanup: drop ids that are no longer in the filtered
  // set (post top-toggle + global search). TableView still enforces its
  // own cap for rendering; selection tracks the full filtered dataset
  // so bulk actions on filtered rows work end-to-end.
  useEffect(() => {
    const visibleIds = new Set(filteredAll.map((r) => r.id));
    const next = new Set();
    selected.forEach((id) => { if (visibleIds.has(id)) next.add(id); });
    if (next.size !== selected.size) setSelected(next);
  }, [filteredAll, selected]);

  // Export-only column selection + order (never affects the table view).
  const [exportCfg, setExportCfg] = useState(() => _readExportCols(EXPORT_COLS_KEY_MEMBERS));
  const [showExportCols, setShowExportCols] = useState(false);
  const exportCols = useMemo(() => _resolveExportCols(MEMBER_COLS, exportCfg), [exportCfg]);
  const doExport = (fmt) => {
    const rows = exportTargetRows || visibleRows || filteredAll;
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:.]/g, "-");
    if (fmt === "xlsx") downloadXlsx(`hfa-members-${stamp}.xlsx`, rows, exportCols);
    else downloadCsv(`hfa-members-${stamp}.csv`, buildCsv(rows, exportCols));
    setShowExport(false);
    setExportTargetRows(null);
  };
  const openExportAll   = () => { setExportTargetRows(null); setShowExport(true); };
  const openExportBulk  = () => { setExportTargetRows(selectedRows); setShowExport(true); };

  // ---- Bulk actions ------------------------------------------------
  const bulkRun = async (verb) => {
    const targets = selectedRows;
    if (targets.length === 0) return;
    const label = { approve: "Accept", reject: "Reject", archive: "Archive" }[verb];
    if (!window.confirm(`${label} ${targets.length} selected member${targets.length === 1 ? "" : "s"}? This cannot be undone in bulk (individual actions can be undone by admin).`)) return;
    setBusy(true);
    let ok = 0, fail = 0;
    for (const r of targets) {
      try {
        if (verb === "approve") {
          if (r.status !== "pending_review") { fail++; continue; }
          await api.post(`/applications/${r.id}/approve`);
        } else if (verb === "reject") {
          if (r.status !== "pending_review") { fail++; continue; }
          await api.post(`/applications/${r.id}/reject`, {});
        } else if (verb === "archive") {
          await api.post(`/applications/${r.id}/archive`);
        }
        ok++;
      } catch (_) { fail++; }
    }
    setBusy(false);
    setSelected(new Set());
    load();
    // Broadcast so Payments + Dashboard + Archived views refresh too.
    if (ok > 0) broadcastDataChanged({ kind: `bulk_${verb}`, count: ok });
    alert(`${label} complete: ${ok} succeeded, ${fail} skipped/failed.`);
  };

  // ---- Saved filter views (v2: capture full workspace state) ---------
  const saveCurrentView = () => {
    const name = window.prompt("Name this saved view (e.g. 'Review Pending – Pune'):", "");
    if (!name || !name.trim()) return;
    setSavedViews((v) => [...v, {
      name: name.trim(),
      pFilter: [...pFilter], rFilter: [...rFilter], q,
      hiddenCols: [...hiddenCols],
      colOrder,
    }]);
  };
  const applySavedView = (v) => {
    setPFilter(Array.isArray(v.pFilter) ? new Set(v.pFilter) : new Set(["all"]));
    setRFilter(Array.isArray(v.rFilter) ? new Set(v.rFilter) : new Set(["all"]));
    setQ(v.q || "");
    if (Array.isArray(v.hiddenCols)) setHiddenCols(new Set(v.hiddenCols));
    if (Array.isArray(v.colOrder)) setColOrder(v.colOrder);
  };
  const deleteSavedView = (idx) => setSavedViews((v) => v.filter((_, i) => i !== idx));

  return (
    <div>
      <ListPageHeader title="Members" count={visibleCount} memberCount={memberCount}
        onExport={openExportAll} onExportFilter={() => setShowExportCols(true)} exportEnabled={visibleCount > 0} secondaryActions
        exportActive={showExport || showExportCols} refreshAttention={isStale}
        onRefresh={() => { setSortResetSig((n) => n + 1); setMultiSort(false); load({ force: true }); }}
        activeDateChip={activeDateChipFor(dateRange)}
        onShiftSingleDay={(delta) => setDateRangeUI(_shiftSingleDay(dateRange, delta))}
        topRight={
          <div className="hfa-date-slot"
               style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
            <DateRangePicker value={dateRange} onChange={setDateRangeUI} chipOutline="hairline-ink" />
            {dateBasis === "rejected_at" && (
              <div data-testid="members-date-basis-hint"
                   style={{ fontSize: 10.5, color: T.brand, fontWeight: 600,
                            letterSpacing: "0.04em", textTransform: "uppercase" }}>
                Date range applied to · Rejected Date
              </div>
            )}
          </div>
        }
        onClearFilters={() => {
          // Reset every filter layer WITHOUT touching stored records,
          // saved views, column layout or admin state. Pure view reset.
          // Quick Date returns to the Members-specific default configured
          // in Settings (never a hardcoded "This Month"); toggles fall
          // back to "all" because that is the operator's canonical
          // "unfiltered" starting point.
          setQ("");
          setPFilter(new Set(["all"]));
          setRFilter(new Set(["all"]));
          setMtFilter(_readMtPref());
          setDateRange(TODAY_RANGE());
          setDateBasis("payment_date");
          setMultiSort(false);
          setClearSig((n) => n + 1);
        }}
        extra={<>
          <button data-testid="btn-multi-sort" data-hfa-btn data-hfa-outline="1" data-active={multiSort ? "true" : undefined} type="button" aria-pressed={multiSort}
            onClick={() => setMultiSort((v) => !v)}
            title={multiSort ? "Multi-Sort is ON: each sort icon you click adds a priority level" : "Multi-Sort is OFF: one sort column at a time"}
            style={{ ...outlineBtnStyle(true, multiSort), gap: 6 }}>
            Multi-Sort
            <span data-testid="btn-multi-sort-state"
              style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: "0.08em", padding: "2px 6px", borderRadius: 999,
                       background: multiSort ? "rgba(255,255,255,0.22)" : "#FFF7ED",
                       color: multiSort ? "white" : T.brand }}>{multiSort ? "ON" : "OFF"}</span>
          </button>
          <button data-testid="btn-column-manager" data-hfa-btn data-hfa-outline="1" data-active={showColMgr ? "true" : undefined} onClick={() => setShowColMgr(true)}
            aria-expanded={showColMgr}
            style={outlineBtnStyle(true, showColMgr)}>Columns</button>
          <button data-testid="btn-duplicate-membership" data-hfa-btn data-hfa-outline="1" data-active={dupOpen ? "true" : undefined}
            disabled={!dupSource}
            onClick={() => dupSource && setDupOpen(true)}
            title={dupSource ? "Duplicate the selected membership for an additional City Chapter" : "Select exactly one membership row to duplicate"}
            style={outlineBtnStyle(!!dupSource, dupOpen)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
            </svg>
            Duplicate
          </button>
          <button data-testid="btn-import-members" data-hfa-btn data-hfa-outline="1" data-active={importOpen ? "true" : undefined} onClick={() => setImportOpen(true)}
            title="Import historical members from the official Excel template"
            aria-expanded={importOpen}
            style={outlineBtnStyle(true, importOpen)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            Import
          </button>
        </>}
        leftBlock={
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-start" }}>
            <FilterGroupCard heading="Review Status"
              rightAdornment={
                <button data-testid="btn-save-view" data-hfa-btn onClick={saveCurrentView}
                  style={{ padding: "3px 9px", fontSize: 10.5, background: "white",
                           border: `1px solid ${T.line}`, borderRadius: 6, cursor: "pointer",
                           fontFamily: "inherit", color: T.slate }}>Save view</button>
              }>
              <SegToggle options={R_OPTS} valueSet={rFilter} onSelect={handleRSelect}
                         disabledSet={disabledR} testid="mem-r-toggle" />
            </FilterGroupCard>
            <FilterGroupCard heading="Payment Status">
              <SegToggle options={P_OPTS_MEMBERS} valueSet={pFilter} onSelect={handlePSelect} testid="mem-p-toggle" />
            </FilterGroupCard>
            <MembershipTypeFilter value={mtFilter} onChange={setMtFilter} testid="mem-mt-toggle" />
          </div>
        }
        secondaryLead={
          <div className="hfa-master-search" style={{ position: "relative", width: 240, minWidth: 140 }}>
            <input data-testid="members-master-search" data-hfa-focus type="text"
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Master Search" aria-label="Master Search"
              className="hfa-ms-input"
              style={{ width: "100%", height: 32, boxSizing: "border-box", padding: "0 30px 0 14px",
                       background: "white", border: HAIR_BRAND, borderRadius: 999,
                       boxShadow: "0 1px 2px rgba(0,0,0,0.02)", fontSize: 12, fontWeight: 500,
                       fontFamily: "inherit", color: T.ink, outline: "none", caretColor: T.ink }} />
            {q && (
              <button data-testid="members-master-search-clear" type="button" title="Clear search"
                aria-label="Clear Master Search" onClick={() => setQ("")}
                style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)",
                         width: 24, height: 24, borderRadius: 999, border: "none", cursor: "pointer",
                         background: "transparent", color: T.muted, fontSize: 15, lineHeight: 1,
                         fontFamily: "inherit", display: "inline-flex", alignItems: "center",
                         justifyContent: "center" }}>×</button>
            )}
          </div>
        } />

      {/* Filter groups now live in the header's leftBlock (two-column layout). */}

      {savedViews.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
          <span style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.1em",
                         color: T.muted, fontWeight: 700, alignSelf: "center" }}>Saved views</span>
          {savedViews.map((v, i) => (
            <span key={i} data-testid={`saved-view-${i}`}
              style={{ display: "inline-flex", alignItems: "center", gap: 4,
                       padding: "5px 10px", background: T.card,
                       border: `1px solid ${T.line}`, borderRadius: 999, fontSize: 11 }}>
              <button onClick={() => applySavedView(v)}
                style={{ background: "transparent", border: "none", cursor: "pointer",
                         fontFamily: "inherit", fontSize: 11, color: T.ink, padding: 0 }}>{v.name}</button>
              <button onClick={() => deleteSavedView(i)} title="Delete saved view"
                style={{ background: "transparent", border: "none", cursor: "pointer",
                         color: T.muted, padding: "0 2px", fontFamily: "inherit" }}>×</button>
            </span>
          ))}
        </div>
      )}

      {/* Bulk toolbar (sticky). Accept & Reject are context-aware:
       *  enabled only when EVERY selected row is Paid + Review Pending.
       *  Any Onboarded/Rejected/Removed/Unpaid in the selection (or a
       *  mixed selection) disables both actions but keeps them visible
       *  so the admin sees what could apply. */}
      {selected.size > 0 && (() => {
        const eligible = selectedRows.length > 0 && selectedRows.every(
          (r) => r.payment_status === "paid" && r.status === "pending_review"
        );
        const disabledStyle = {
          background: "rgba(255,255,255,0.18)",
          color: "rgba(255,255,255,0.55)",
          cursor: "not-allowed",
          opacity: 0.65,
        };
        const disabledTitle = "Only enabled when every selected row is Paid + Review Pending.";
        return (
        <div data-testid="bulk-toolbar"
          style={{ position: "sticky", top: 8, zIndex: 5, marginBottom: 12,
                   padding: "12px 18px", background: T.ink, color: "white",
                   borderRadius: 12, boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
                   display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>{selected.size} selected</span>
          <div style={{ display: "flex", gap: 6, marginLeft: "auto" }}>
            <button data-testid="bulk-accept" disabled={busy || !eligible}
              title={eligible ? "" : disabledTitle}
              onClick={() => bulkRun("approve")}
              style={{ padding: "6px 12px", fontSize: 12,
                       ...(eligible
                         ? { background: T.brand, color: "white", cursor: busy ? "default" : "pointer" }
                         : disabledStyle),
                       border: "none", borderRadius: 6, fontFamily: "inherit", fontWeight: 600 }}>Accept</button>
            <button data-testid="bulk-reject" disabled={busy || !eligible}
              title={eligible ? "" : disabledTitle}
              onClick={() => bulkRun("reject")}
              style={{ padding: "6px 12px", fontSize: 12,
                       ...(eligible
                         ? { background: "white", color: T.danger, cursor: busy ? "default" : "pointer" }
                         : disabledStyle),
                       border: "none", borderRadius: 6, fontFamily: "inherit", fontWeight: 600 }}>Reject</button>
            <button data-testid="bulk-archive" disabled={busy} onClick={() => bulkRun("archive")}
              style={{ padding: "6px 12px", fontSize: 12, background: "transparent", color: "white",
                       border: "1px solid rgba(255,255,255,0.4)", borderRadius: 6, cursor: busy ? "default" : "pointer", fontFamily: "inherit" }}>Archive</button>
            <button data-testid="bulk-export" onClick={openExportBulk}
              style={{ padding: "6px 12px", fontSize: 12, background: "transparent", color: "white",
                       border: "1px solid rgba(255,255,255,0.4)", borderRadius: 6, cursor: "pointer", fontFamily: "inherit" }}>Export</button>
            <button data-testid="bulk-cancel" onClick={() => setSelected(new Set())}
              style={{ padding: "6px 12px", fontSize: 12, background: "transparent", color: "rgba(255,255,255,0.75)",
                       border: "1px solid rgba(255,255,255,0.2)", borderRadius: 6, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
          </div>
        </div>
        );
      })()}

      {err && <div style={{ color: T.danger, fontSize: 13, marginBottom: 10 }}>{err}</div>}
      <TableView rows={displayRows} cols={memberCols} onOpenRow={(r) => onOpenRow(r, q)} highlight={q} enableSort
        sortResetSignal={sortResetSig} multiSort={multiSort} defaultSort={{ key: "applied_instant", dir: "desc" }}
        testid="admin-members-table"
        selectable={true} selectedIds={selected} onSelectionChange={setSelected}
        hiddenCols={hiddenCols}
        tableKey="members"
        rowsCap={Number.isFinite(cap) ? cap : null}
        onFilteredCountChange={setVisibleCount}
        onFilteredMembersChange={setMemberCount}
        onFilteredRowsChange={setVisibleRows}
        clearSignal={clearSig}
        onReorderCols={(fromKey, toKey) => {
          // Rebuild the ordered key list and move `fromKey` next to `toKey`.
          const current = _applyColOrder(MEMBER_COLS, colOrder).map((c) => c.key);
          const from = current.indexOf(fromKey);
          const to   = current.indexOf(toKey);
          if (from === -1 || to === -1 || from === to) return;
          current.splice(from, 1);
          current.splice(to, 0, fromKey);
          setColOrder(current);
        }} />

      {dupOpen && dupSource && (
        <DuplicateMembershipModal api={api} source={dupSource}
          onClose={() => setDupOpen(false)}
          onCreated={() => { setDupOpen(false); setSelected(new Set()); broadcastDataChanged({ kind: "membership_duplicated", source_id: dupSource.id }); }} />
      )}
      {importOpen && <ImportMembersModal api={api} onClose={() => setImportOpen(false)}
        onImported={(res) => { broadcastDataChanged({ kind: "members_imported", batch_id: res.batch_id }); }} />}
      {rowsPerView !== "All" && visibleCount > cap && (
        <div style={{ marginTop: 10, color: T.muted, fontSize: 12, textAlign: "center" }}>
          Showing first {cap} of {visibleCount} rows — change <b>Rows Per View</b> in Settings to see more.
        </div>
      )}

      {showColMgr && (
        <ColumnManager
          allCols={MEMBER_COLS}
          hiddenCols={hiddenCols}
          colOrder={colOrder}
          tableKey="members"
          onChange={({ hidden, order }) => { if (hidden) setHiddenCols(hidden); setColOrder(order); }}
          onClose={() => setShowColMgr(false)} />
      )}
      {showExport && (
        <ExportPickerModal
          count={(exportTargetRows || visibleRows || filteredAll).length}
          onPickXlsx={() => doExport("xlsx")}
          onPickCsv={() => doExport("csv")}
          onClose={() => { setShowExport(false); setExportTargetRows(null); }} />
      )}
      {showExportCols && (
        <ExportColumnsPanel allCols={MEMBER_COLS} cfg={exportCfg}
          onApply={(next) => { setExportCfg(next); setShowExportCols(false); }}
          onSaveDefault={(next) => { _writeExportCols(EXPORT_COLS_KEY_MEMBERS, next); setExportCfg(next); }}
          onClose={() => setShowExportCols(false)} />
      )}
    </div>
  );
}

const EXPORT_COLS_KEY_MEMBERS = "hfa-admin-export-cols-members";

/** Members → Import. Step 1: download template. Step 2: upload → server-side
 *  validation + preview (READ-ONLY; nothing is written). Confirm step comes later. */
function ImportMembersModal({ api, onClose, onImported }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState("");
  const [preview, setPreview] = useState(null);
  const [lastFile, setLastFile] = useState(null);
  const [tab, setTab] = useState("all");
  const [confirming, setConfirming] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importErr, setImportErr] = useState("");
  const [result, setResult] = useState(null);
  const [sourceLabel, setSourceLabel] = useState("");
  const fileRef = useRef(null);
  const runImport = async () => {
    if (!lastFile || !preview || !sourceLabel.trim()) return;
    setImporting(true); setImportErr("");
    try {
      const fd = new FormData();
      fd.append("file", lastFile);
      fd.append("expected_ready", String(preview.summary.ready));
      fd.append("source_label", sourceLabel.trim());
      const res = await api.post("/members/import/commit", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(res.data); setConfirming(false);
      if (res.data.imported > 0 && onImported) onImported(res.data);
    } catch (e) {
      setImportErr(e?.response?.data?.detail || "Import failed. Nothing was saved.");
    } finally { setImporting(false); }
  };
  const download = async () => {
    setBusy(true); setErr("");
    try {
      const res = await api.get("/members/import-template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "hfa-members-import-template.xlsx";
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (_) { setErr("Could not download the template. Please try again."); }
    finally { setBusy(false); }
  };
  const upload = async (file) => {
    if (!file) return;
    setUploadErr(""); setPreview(null); setResult(null); setConfirming(false); setImportErr("");
    if (!/\.xlsx$/i.test(file.name)) { setUploadErr("Only .xlsx files from the official template are accepted."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/members/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(res.data); setLastFile(file); setTab("all");
    } catch (e) {
      setUploadErr(e?.response?.data?.detail || "Could not read the file. Please upload the official template.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  return (
    <div data-testid="import-members-modal" onClick={onClose}
         style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 90,
                  display: "flex", alignItems: "center", justifyContent: "center", padding: 12 }}>
      <div onClick={(e) => e.stopPropagation()}
           style={{ background: "white", borderRadius: 14, padding: 22, maxWidth: preview ? 1040 : 520, width: "96%",
                    maxHeight: "92vh", overflow: "auto", boxShadow: "0 32px 64px rgba(0,0,0,0.3)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>Import members</div>
            <div style={{ fontSize: 11.5, color: T.muted, marginTop: 2 }}>
              Historical members are imported from the official Excel template only.
            </div>
          </div>
          <button data-testid="import-members-close" onClick={onClose}
            style={{ border: "none", background: "transparent", fontSize: 20, cursor: "pointer", color: T.muted, fontFamily: "inherit" }}>×</button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: preview ? "repeat(auto-fit, minmax(300px, 1fr))" : "1fr", gap: 10 }}>
          <div style={{ border: `1px solid ${T.line}`, borderRadius: 10, padding: 14, background: T.bgSubtle }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: T.ink }}>Step 1 · Download the template</div>
            <div style={{ fontSize: 11.5, color: T.muted, marginTop: 4, lineHeight: 1.5 }}>
              Includes every supported Members field (with Membership Type and City Chapter dropdowns) plus an
              Allowed Values sheet. Member ID and Contact Name are generated by the system on import.
            </div>
            <button data-testid="btn-download-import-template" data-hfa-btn disabled={busy} onClick={download}
              style={{ ...actionBtnStyle(!busy), marginTop: 12, whiteSpace: "nowrap" }}>
              {busy ? "Preparing…" : "Download Import Template"}
            </button>
            {err && <div data-testid="import-template-error" style={{ color: T.danger, fontSize: 12, marginTop: 8 }}>{err}</div>}
          </div>
          <div data-testid="import-members-upload"
               style={{ border: `1px dashed ${T.line}`, borderRadius: 10, padding: 14, background: "white" }}>
            <div style={{ fontWeight: 600, color: T.ink, fontSize: 12.5 }}>Step 2 · Upload the filled template</div>
            <div style={{ fontSize: 11.5, color: T.muted, marginTop: 4, lineHeight: 1.5 }}>
              The file is validated and previewed only. Nothing is imported or changed until you confirm in the next step.
            </div>
            <input ref={fileRef} type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                   data-testid="import-file-input" style={{ display: "none" }}
                   onChange={(e) => upload(e.target.files?.[0])} />
            <button data-testid="btn-upload-import-file" data-hfa-btn disabled={uploading}
              onClick={() => fileRef.current?.click()} style={{ ...actionBtnStyle(!uploading), marginTop: 12 }}>
              {uploading ? "Validating…" : (preview ? "Upload another file" : "Choose .xlsx file")}
            </button>
            {uploadErr && <div data-testid="import-upload-error" style={{ color: T.danger, fontSize: 12, marginTop: 8 }}>{uploadErr}</div>}
          </div>
        </div>
        {preview && !result && <ImportPreview preview={preview} tab={tab} setTab={setTab} />}
        {preview && !result && (
          <ImportConfirm summary={preview.summary} confirming={confirming} setConfirming={setConfirming}
            importing={importing} error={importErr} onConfirm={runImport}
            sourceLabel={sourceLabel} setSourceLabel={setSourceLabel} />
        )}
        {result && <ImportResult result={result} />}
      </div>
    </div>
  );
}

function ImportConfirm({ summary, confirming, setConfirming, importing, error, onConfirm, sourceLabel, setSourceLabel }) {
  const canImport = summary.ready > 0 && sourceLabel.trim().length > 0;
  return (
    <div data-testid="import-confirm" style={{ marginTop: 14, border: `1px solid ${T.line}`, borderRadius: 10, padding: 14,
                                               background: canImport ? "#FFF7ED" : T.bgSubtle }}>
      <div style={{ fontSize: 13, fontWeight: 700 }}>Step 3 · Confirm import</div>
      <div style={{ fontSize: 12, color: T.ink, marginTop: 6, lineHeight: 1.6 }}>
        <b data-testid="confirm-ready-count">{summary.ready}</b> member{summary.ready === 1 ? "" : "s"} will be imported as new records.
        Not imported: <b data-testid="confirm-duplicate-count">{summary.duplicate}</b> potential duplicate{summary.duplicate === 1 ? "" : "s"},{" "}
        <b data-testid="confirm-error-count">{summary.error}</b> error{summary.error === 1 ? "" : "s"},{" "}
        <b data-testid="confirm-skipped-count">{summary.skipped}</b> skipped.
      </div>
      <div style={{ fontSize: 11.5, color: T.muted, marginTop: 4 }}>
        Only rows marked Ready to Import are created. Existing members are never updated, overwritten or deleted.
        All records get one Import Batch ID.
      </div>
      <label style={{ display: "block", marginTop: 12, fontSize: 11.5, fontWeight: 600, color: T.ink }}>
        Source for this import <span style={{ color: T.muted, fontWeight: 500 }}>(shown in the Members “Source” column — set explicitly, not derived from Membership Type)</span>
        <input data-testid="import-source-label" value={sourceLabel} onChange={(e) => setSourceLabel(e.target.value)}
          placeholder="e.g. Imported Annual Members" disabled={confirming || importing}
          style={{ display: "block", marginTop: 6, width: "100%", maxWidth: 360, padding: "8px 10px", fontSize: 12.5, borderRadius: 8,
                   border: `1px solid ${T.line}`, fontFamily: "inherit", background: "white" }} />
      </label>
      {!confirming ? (
        <button data-testid="btn-import-confirm-open" data-hfa-btn disabled={!canImport} onClick={() => setConfirming(true)}
          style={{ ...actionBtnStyle(canImport), marginTop: 12 }}>
          Import {summary.ready} member{summary.ready === 1 ? "" : "s"}…
        </button>
      ) : (
        <div data-testid="import-confirm-final" style={{ marginTop: 12, padding: 12, borderRadius: 10, background: "white", border: `1px solid ${T.brand}` }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: T.ink }}>
            This will write {summary.ready} new member record{summary.ready === 1 ? "" : "s"} to the database with Source “{sourceLabel.trim()}”. Continue?
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
            <button data-testid="btn-import-confirm-yes" data-hfa-btn disabled={importing} onClick={onConfirm}
              style={{ padding: "8px 16px", fontSize: 12.5, fontWeight: 600, borderRadius: 999, border: "none", cursor: importing ? "default" : "pointer",
                       background: importing ? "rgba(234,88,12,0.5)" : T.brand, color: "white", fontFamily: "inherit" }}>
              {importing ? "Importing…" : `Yes, import ${summary.ready} member${summary.ready === 1 ? "" : "s"}`}
            </button>
            <button data-testid="btn-import-confirm-cancel" data-hfa-btn disabled={importing} onClick={() => setConfirming(false)}
              style={actionBtnStyle(!importing)}>Cancel</button>
          </div>
        </div>
      )}
      {error && <div data-testid="import-commit-error" style={{ color: T.danger, fontSize: 12, marginTop: 8 }}>{error}</div>}
    </div>
  );
}

function ImportResult({ result }) {
  const notImported = [...(result.failed || []), ...(result.not_imported || [])];
  const meta = (s) => IMPORT_STATUS_META[s] || { label: "Failed", color: T.danger, bg: "#FEF2F2" };
  const ok = result.imported > 0;
  return (
    <div data-testid="import-result" style={{ marginTop: 14 }}>
      <div style={{ border: `1px solid ${ok ? "#86EFAC" : T.line}`, borderRadius: 10, padding: 14, background: ok ? "#ECFDF3" : T.bgSubtle }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: ok ? T.success : T.ink }}>
          {ok ? "Import complete" : "Nothing was imported"}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8, marginTop: 10 }}>
          {[["imported", "Successfully imported", result.imported, T.success],
            ["skipped", "Skipped", (result.not_imported || []).length, "#92400E"],
            ["failed", "Failed", (result.failed || []).length, T.danger]].map(([k, label, n, color]) => (
            <div key={k} style={{ padding: "8px 12px", borderRadius: 10, background: "white", border: `1px solid ${T.line}` }}>
              <div style={{ fontSize: 10.5, color: T.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.3 }}>{label}</div>
              <div data-testid={`import-result-${k}`} style={{ fontSize: 20, fontWeight: 700, color }}>{n}</div>
            </div>
          ))}
          <div style={{ padding: "8px 12px", borderRadius: 10, background: "white", border: `1px solid ${T.line}` }}>
            <div style={{ fontSize: 10.5, color: T.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.3 }}>Import Batch ID</div>
            <div data-testid="import-result-batch-id" style={{ fontSize: 12.5, fontWeight: 700, fontFamily: "monospace", wordBreak: "break-all" }}>{result.batch_id}</div>
          </div>
        </div>
      </div>
      {notImported.length > 0 && (
        <div style={{ marginTop: 10, border: `1px solid ${T.line}`, borderRadius: 10, overflow: "auto", maxHeight: 300 }}>
          <table data-testid="import-result-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: T.bgSubtle, position: "sticky", top: 0 }}>
                {["Excel Row", "Name", "Status", "Reason"].map((h) => (
                  <th key={h} style={{ textAlign: "left", padding: "8px 10px", fontSize: 10.5, color: T.muted, textTransform: "uppercase",
                                       letterSpacing: 0.4, borderBottom: `1px solid ${T.line}`, whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {notImported.map((r) => {
                const m = meta(r.status);
                return (
                  <tr key={`${r.status}-${r.row}`} data-testid={`import-result-row-${r.row}`} style={{ borderBottom: `1px solid ${T.lineSoft}` }}>
                    <td style={{ padding: "7px 10px" }}>{r.row}</td>
                    <td style={{ padding: "7px 10px", fontWeight: 600 }}>{r.name || <span style={{ color: T.muted }}>—</span>}</td>
                    <td style={{ padding: "7px 10px" }}>
                      <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 600,
                                     color: m.color, background: m.bg, whiteSpace: "nowrap" }}>{m.label}</span>
                    </td>
                    <td style={{ padding: "7px 10px", minWidth: 220 }}>
                      <ul style={{ margin: 0, paddingLeft: 16 }}>{r.reasons.map((x, i) => <li key={i}>{x}</li>)}</ul>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const IMPORT_STATUS_META = {
  ready:     { label: "Ready to Import",      color: T.success, bg: "#ECFDF3" },
  duplicate: { label: "Potential Duplicate",  color: "#92400E", bg: "#FFF7ED" },
  error:     { label: "Error",                color: T.danger,  bg: "#FEF2F2" },
  skipped:   { label: "Skipped",              color: T.muted,   bg: "#F3F4F6" },
};

function ImportPreview({ preview, tab, setTab }) {
  const { summary, rows, filename, age_column } = preview;
  const shown = tab === "all" ? rows : rows.filter((r) => r.status === tab);
  const headers = ["Excel Row", "Name", "WhatsApp", "Email", ...(age_column ? ["Age"] : []), "City Chapter", "Membership Type", "Applied Date", "Payment Date", "Start Date", "Status", "Reason"];
  const cards = [
    ["all", "Total rows", summary.total, T.ink, T.bgSubtle],
    ["ready", "Ready to Import", summary.ready, IMPORT_STATUS_META.ready.color, IMPORT_STATUS_META.ready.bg],
    ["duplicate", "Potential Duplicates", summary.duplicate, IMPORT_STATUS_META.duplicate.color, IMPORT_STATUS_META.duplicate.bg],
    ["error", "Errors", summary.error, IMPORT_STATUS_META.error.color, IMPORT_STATUS_META.error.bg],
    ["skipped", "Skipped", summary.skipped, IMPORT_STATUS_META.skipped.color, IMPORT_STATUS_META.skipped.bg],
  ];
  return (
    <div data-testid="import-preview" style={{ marginTop: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 6 }}>
        <div style={{ fontSize: 13, fontWeight: 700 }}>Import preview <span style={{ color: T.muted, fontWeight: 500, fontSize: 11.5 }}>· {filename}</span></div>
        <div data-testid="import-preview-note" style={{ fontSize: 11.5, color: T.muted }}>Preview only — no records have been created or changed.</div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8, marginTop: 10 }}>
        {cards.map(([k, label, n, color, bg]) => (
          <button key={k} data-testid={`import-summary-${k}`} onClick={() => setTab(k)}
            style={{ textAlign: "left", padding: "10px 12px", borderRadius: 10, cursor: "pointer", fontFamily: "inherit",
                     background: bg, border: `2px solid ${tab === k ? color : "transparent"}` }}>
            <div style={{ fontSize: 11, color: T.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.3 }}>{label}</div>
            <div data-testid={`import-count-${k}`} style={{ fontSize: 22, fontWeight: 700, color }}>{n}</div>
          </button>
        ))}
      </div>
      <div style={{ marginTop: 10, border: `1px solid ${T.line}`, borderRadius: 10, overflow: "auto", maxHeight: 360 }}>
        <table data-testid="import-preview-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ background: T.bgSubtle, position: "sticky", top: 0 }}>
              {headers.map((h) => (
                <th key={h} style={{ textAlign: "left", padding: "8px 10px", fontSize: 10.5, color: T.muted, textTransform: "uppercase",
                                     letterSpacing: 0.4, borderBottom: `1px solid ${T.line}`, whiteSpace: "nowrap" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 && (
              <tr><td colSpan={headers.length} style={{ padding: 16, color: T.muted, textAlign: "center" }}>No rows in this category.</td></tr>
            )}
            {shown.map((r) => {
              const m = IMPORT_STATUS_META[r.status];
              return (
                <tr key={r.row} data-testid={`import-row-${r.row}`} style={{ borderBottom: `1px solid ${T.lineSoft}` }}>
                  <td style={{ padding: "7px 10px", fontVariantNumeric: "tabular-nums" }}>{r.row}</td>
                  <td style={{ padding: "7px 10px", fontWeight: 600 }}>{r.name || <span style={{ color: T.muted }}>—</span>}</td>
                  <td style={{ padding: "7px 10px" }}>{r.whatsapp || "—"}</td>
                  <td style={{ padding: "7px 10px", overflowWrap: "anywhere" }}>{r.email || "—"}</td>
                  {age_column && <td style={{ padding: "7px 10px" }}>{r.age ?? "—"}</td>}
                  <td style={{ padding: "7px 10px" }}>{r.city_chapter || "—"}</td>
                  <td style={{ padding: "7px 10px" }}>{r.membership_type || "—"}</td>
                  <td data-testid={`import-row-applied-${r.row}`} style={{ padding: "7px 10px", whiteSpace: "nowrap" }}>{r.applied_date ? fmtDate(r.applied_date) : "—"}</td>
                  <td data-testid={`import-row-paid-${r.row}`} style={{ padding: "7px 10px", whiteSpace: "nowrap" }}>{r.payment_date ? fmtDate(r.payment_date) : "—"}</td>
                  <td data-testid={`import-row-start-${r.row}`} style={{ padding: "7px 10px", whiteSpace: "nowrap" }}>{r.membership_start_date ? fmtDate(r.membership_start_date) : "—"}</td>
                  <td style={{ padding: "7px 10px" }}>
                    <span data-testid={`import-row-status-${r.row}`}
                          style={{ display: "inline-block", padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 600,
                                   color: m.color, background: m.bg, whiteSpace: "nowrap" }}>{m.label}</span>
                  </td>
                  <td style={{ padding: "7px 10px", color: r.status === "ready" ? T.muted : T.ink, minWidth: 220 }}>
                    {r.reasons.length === 0 ? "All checks passed" : (
                      <ul style={{ margin: 0, paddingLeft: 16 }}>
                        {r.reasons.map((x, i) => <li key={i}>{x}</li>)}
                      </ul>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
/** { order: [key…], hidden: [key…] } — export-only; defaults to every column, default order. */
function _readExportCols(key) {
  try {
    const raw = JSON.parse(localStorage.getItem(key) || "null");
    if (raw && Array.isArray(raw.order) && Array.isArray(raw.hidden)) return raw;
  } catch (_) { /* noop */ }
  return { order: [], hidden: [] };
}
function _writeExportCols(key, cfg) {
  try { localStorage.setItem(key, JSON.stringify(cfg)); } catch (_) { /* noop */ }
}
function _resolveExportCols(allCols, cfg) {
  const byKey = Object.fromEntries(allCols.map((c) => [c.key, c]));
  const order = [...cfg.order.filter((k) => byKey[k]), ...allCols.map((c) => c.key).filter((k) => !cfg.order.includes(k))];
  const hidden = new Set(cfg.hidden);
  return order.map((k) => byKey[k]).filter((c) => !hidden.has(c.key));
}

/** Export column picker — tick/untick + drag (or ↑/↓) to set the exact
 *  column order of the exported file. Export-only: the table is untouched. */
function ExportColumnsPanel({ allCols, cfg, onApply, onSaveDefault, onClose }) {
  const [order, setOrder] = useState(() => _resolveExportCols(allCols, { order: cfg.order, hidden: [] }).map((c) => c.key));
  const [hidden, setHidden] = useState(() => new Set(cfg.hidden));
  const [dragKey, setDragKey] = useState(null);
  const [savedFlash, setSavedFlash] = useState(false);
  const flashTimer = useRef(null);
  useEffect(() => () => { if (flashTimer.current) clearTimeout(flashTimer.current); }, []);
  const byKey = useMemo(() => Object.fromEntries(allCols.map((c) => [c.key, c])), [allCols]);
  const selectedCount = order.filter((k) => !hidden.has(k)).length;
  const move = (from, to) => {
    if (from === to || from < 0 || to < 0 || to >= order.length) return;
    const next = [...order]; const [k] = next.splice(from, 1); next.splice(to, 0, k); setOrder(next);
  };
  const toggle = (k) => setHidden((h) => { const n = new Set(h); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const btn = { padding: "8px 14px", background: "white", color: T.slate, border: `1px solid ${T.line}`,
                borderRadius: 8, cursor: "pointer", fontFamily: "inherit", fontSize: 12 };
  return (
    <div data-testid="export-columns-panel" onClick={onClose}
         style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 90,
                  display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div onClick={(e) => e.stopPropagation()}
           style={{ background: "white", borderRadius: 14, padding: 22, maxWidth: 520, width: "92%",
                    maxHeight: "84vh", overflow: "hidden", display: "flex", flexDirection: "column",
                    boxShadow: "0 32px 64px rgba(0,0,0,0.3)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>Export columns</div>
            <div style={{ fontSize: 11.5, color: T.muted, marginTop: 2 }}>
              Tick the columns to include and drag to set their order. Affects the exported file only — the table is unchanged.
            </div>
          </div>
          <button data-testid="export-cols-close" onClick={onClose}
            style={{ background: "transparent", border: "none", cursor: "pointer", fontSize: 22, color: T.muted, lineHeight: 1 }}>×</button>
        </div>
        <div style={{ flex: 1, minHeight: 0, overflow: "auto", border: `1px solid ${T.line}`, borderRadius: 8 }}>
          {order.map((k, idx) => {
            const c = byKey[k]; if (!c) return null;
            const on = !hidden.has(k);
            return (
              <div key={k} data-testid={`export-col-row-${k}`} draggable
                onDragStart={(e) => { setDragKey(k); e.dataTransfer.setData("text/plain", k); e.dataTransfer.effectAllowed = "move"; }}
                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
                onDrop={(e) => { e.preventDefault(); const from = order.indexOf(e.dataTransfer.getData("text/plain") || dragKey); move(from, idx); setDragKey(null); }}
                onDragEnd={() => setDragKey(null)}
                style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", fontSize: 12.5,
                         borderBottom: `1px solid ${T.lineSoft}`, cursor: "grab", opacity: on ? 1 : 0.55,
                         background: dragKey === k ? "#FFF7ED" : "white" }}>
                <span title="Drag to reorder" style={{ color: T.muted, fontSize: 14, opacity: 0.6 }}>⋮⋮</span>
                <input type="checkbox" checked={on} onChange={() => toggle(k)} data-testid={`export-col-toggle-${k}`} />
                <span style={{ flex: 1 }}>{c.label}</span>
                <span style={{ color: T.muted, fontSize: 10.5, minWidth: 18, textAlign: "right" }}>{on ? order.filter((x) => !hidden.has(x)).indexOf(k) + 1 : ""}</span>
                <button onClick={() => move(idx, idx - 1)} disabled={idx === 0} data-testid={`export-col-up-${k}`} title="Move up"
                  style={{ padding: "2px 8px", fontSize: 12, border: `1px solid ${T.line}`, background: "white", borderRadius: 5,
                           cursor: idx === 0 ? "not-allowed" : "pointer", opacity: idx === 0 ? 0.35 : 1, fontFamily: "inherit" }}>↑</button>
                <button onClick={() => move(idx, idx + 1)} disabled={idx === order.length - 1} data-testid={`export-col-down-${k}`} title="Move down"
                  style={{ padding: "2px 8px", fontSize: 12, border: `1px solid ${T.line}`, background: "white", borderRadius: 5,
                           cursor: idx === order.length - 1 ? "not-allowed" : "pointer", opacity: idx === order.length - 1 ? 0.35 : 1, fontFamily: "inherit" }}>↓</button>
              </div>
            );
          })}
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap", alignItems: "center" }}>
          <button data-testid="export-cols-select-all" onClick={() => setHidden(new Set())} style={btn}>Select all</button>
          <button data-testid="export-cols-reset" onClick={() => { setOrder(allCols.map((c) => c.key)); setHidden(new Set()); }} style={btn}>Reset to default</button>
          <span data-testid="export-cols-count" style={{ fontSize: 11.5, color: T.muted }}>{selectedCount} of {allCols.length} columns</span>
          <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
            {savedFlash && (
              <span data-testid="export-cols-saved-flash"
                style={{ fontSize: 11.5, color: "#166534", background: "#DCFCE7", padding: "6px 10px",
                         borderRadius: 999, fontWeight: 600, border: "1px solid #86EFAC" }}>✓ Saved</span>
            )}
            <button data-testid="export-cols-cancel" onClick={onClose} style={btn}>Cancel</button>
            <button data-testid="export-cols-save-default" disabled={selectedCount === 0}
              title="Save this column selection and order as your default export layout. Loads automatically whenever this panel is opened."
              onClick={() => {
                onSaveDefault({ order, hidden: [...hidden] });
                setSavedFlash(true);
                if (flashTimer.current) clearTimeout(flashTimer.current);
                flashTimer.current = setTimeout(() => setSavedFlash(false), 1500);
              }}
              style={{ ...btn, color: T.brand, border: `1px solid ${T.brand}`, fontWeight: 600,
                       cursor: selectedCount === 0 ? "not-allowed" : "pointer", opacity: selectedCount === 0 ? 0.5 : 1 }}>Save as Default</button>
            <button data-testid="export-cols-apply" disabled={selectedCount === 0}
              onClick={() => onApply({ order, hidden: [...hidden] })}
              style={{ padding: "8px 14px", background: T.ink, color: "white", border: "none", borderRadius: 8,
                       cursor: selectedCount === 0 ? "not-allowed" : "pointer", opacity: selectedCount === 0 ? 0.5 : 1,
                       fontFamily: "inherit", fontSize: 12, fontWeight: 600 }}>Apply</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Column show / hide + reorder manager.
 *  Features:
 *   • Live search box (filters visible rows in the modal — helpful when
 *     the column list is long — 30+ columns).
 *   • Per-row checkbox for show/hide.
 *   • Up / Down arrow buttons to reorder the columns (keyboard-friendly
 *     alternative to native drag-and-drop that works on trackpads and
 *     touch devices out of the box).
 *   • Select All · Reset to Default · Cancel · Apply.
 *  Preferences persist to localStorage on Apply. */
function ColumnManager({ allCols, hiddenCols, colOrder, onChange, onClose, tableKey = null }) {
  // Local, uncommitted state — Apply commits, Cancel discards.
  const [localHidden, setLocalHidden] = useState(new Set(hiddenCols));
  const [localOrder, setLocalOrder] = useState(() => {
    // Start from saved order (if any), then append any new columns.
    const ordered = _applyColOrder(allCols, colOrder);
    return ordered.map((c) => c.key);
  });
  const [localFrozen, setLocalFrozen] = useState(() => tableKey ? _readFrozenCount(tableKey) : 0);
  const [q, setQ] = useState("");
  // Short-lived acknowledgement after "Save as Default Sequence" so the
  // admin gets clear feedback that their preferences have been persisted.
  const [savedFlash, setSavedFlash] = useState(false);
  const savedFlashTimer = useRef(null);
  useEffect(() => () => { if (savedFlashTimer.current) clearTimeout(savedFlashTimer.current); }, []);

  const toggle = (k) => {
    const n = new Set(localHidden);
    if (n.has(k)) n.delete(k); else n.add(k);
    setLocalHidden(n);
  };
  const moveKey = (idx, direction) => {
    const next = [...localOrder];
    const swap = idx + direction;
    if (swap < 0 || swap >= next.length) return;
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setLocalOrder(next);
  };
  const selectAll  = () => setLocalHidden(new Set());
  /** Save as Default Sequence — commits the CURRENT selections (visible
   *  columns, order, frozen count) as the admin's personal saved layout.
   *  Column widths are already tracked live on the mounted TableView and
   *  are auto-persisted to the same tableKey envelope; this button
   *  simply provides an explicit "save" affordance and confirmation
   *  toast so admins know their arrangement will re-appear on future
   *  sessions. */
  const saveAsDefault = () => {
    if (tableKey) _writeFrozenCount(tableKey, localFrozen);
    // Sequence only — which columns are shown/hidden is NOT saved here
    // (that still happens via Apply).
    onChange({ hidden: null, order: localOrder });
    setSavedFlash(true);
    if (savedFlashTimer.current) clearTimeout(savedFlashTimer.current);
    savedFlashTimer.current = setTimeout(() => setSavedFlash(false), 1500);
  };
  const apply = () => {
    // Frozen count is committed here (deferred until Apply — Section D).
    if (tableKey) _writeFrozenCount(tableKey, localFrozen);
    onChange({ hidden: localHidden, order: localOrder });
    onClose();
  };

  const byKey = useMemo(() => Object.fromEntries(allCols.map((c) => [c.key, c])), [allCols]);
  const orderedCols = localOrder.map((k) => byKey[k]).filter(Boolean);
  const filteredList = q
    ? orderedCols.filter((c) => (c.label + " " + c.key).toLowerCase().includes(q.toLowerCase()))
    : orderedCols;

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 90,
                  display: "flex", alignItems: "center", justifyContent: "center" }}
         onClick={onClose} data-testid="column-manager">
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: "white", borderRadius: 14, padding: 22, maxWidth: 560,
                 width: "92%", maxHeight: "84vh", overflow: "hidden",
                 display: "flex", flexDirection: "column",
                 boxShadow: "0 32px 64px rgba(0,0,0,0.3)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>Columns</div>
            <div style={{ fontSize: 11.5, color: T.muted, marginTop: 2 }}>
              Show, hide and reorder columns. Changes are personal to this browser.
            </div>
          </div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", cursor: "pointer", fontSize: 22, color: T.muted, lineHeight: 1 }}>×</button>
        </div>
        {/* Section A — Frozen Columns (per-table, deferred until Apply) */}
        {tableKey && (
          <div data-testid="col-mgr-frozen-section"
            style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                     gap: 12, padding: "10px 12px", marginBottom: 12,
                     background: "#FAF7F2", border: `1px solid ${T.line}`, borderRadius: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: T.ink }}>Frozen Columns</div>
              <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>
                Pin the left-most visible columns so they stay in place while you scroll horizontally. Applies only to this table.
              </div>
            </div>
            <select data-testid="col-mgr-frozen-count"
              value={String(localFrozen)}
              onChange={(e) => setLocalFrozen(parseInt(e.target.value, 10) || 0)}
              style={{ padding: "6px 10px", fontSize: 12, border: `1px solid ${T.line}`,
                       borderRadius: 6, background: "white", fontFamily: "inherit",
                       minWidth: 150 }}>
              <option value="0">None</option>
              <option value="1">First 1 column</option>
              <option value="2">First 2 columns</option>
              <option value="3">First 3 columns</option>
            </select>
          </div>
        )}
        <input data-testid="col-mgr-search"
          placeholder="Search columns…" value={q} onChange={(e) => setQ(e.target.value)}
          style={{ width: "100%", padding: "9px 12px", fontSize: 13,
                   border: `1px solid ${T.line}`, borderRadius: 8, fontFamily: "inherit",
                   marginBottom: 12 }} />
        <div style={{ flex: 1, minHeight: 0, overflow: "auto", border: `1px solid ${T.line}`, borderRadius: 8 }}>
          {filteredList.map((c) => {
            const idx = localOrder.indexOf(c.key);
            return (
              <div key={c.key}
                draggable={!q}
                onDragStart={(e) => { e.dataTransfer.setData("text/plain", c.key); e.dataTransfer.effectAllowed = "move"; }}
                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
                onDrop={(e) => {
                  e.preventDefault();
                  const draggedKey = e.dataTransfer.getData("text/plain");
                  if (!draggedKey || draggedKey === c.key) return;
                  const next = [...localOrder];
                  const from = next.indexOf(draggedKey);
                  const to = next.indexOf(c.key);
                  if (from === -1 || to === -1) return;
                  next.splice(from, 1);
                  next.splice(to, 0, draggedKey);
                  setLocalOrder(next);
                }}
                style={{ display: "flex", alignItems: "center", gap: 8,
                         padding: "8px 12px", fontSize: 12.5,
                         borderBottom: `1px solid ${T.lineSoft}`,
                         cursor: q ? "default" : "grab" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#FAFAFA")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "white")}>
                <span title={q ? "Clear search to drag" : "Drag to reorder"}
                  style={{ color: T.muted, fontSize: 14, opacity: q ? 0.3 : 0.6, cursor: q ? "default" : "grab" }}>⋮⋮</span>
                <input type="checkbox" checked={!localHidden.has(c.key)} onChange={() => toggle(c.key)}
                  data-testid={`col-toggle-${c.key}`} />
                <span style={{ flex: 1 }}>{c.label}</span>
                <span style={{ color: T.muted, fontSize: 10.5 }}>{c.key}</span>
                <button onClick={() => moveKey(idx, -1)} disabled={idx === 0}
                  data-testid={`col-up-${c.key}`}
                  style={{ padding: "2px 8px", fontSize: 12, border: `1px solid ${T.line}`,
                           background: "white", borderRadius: 5,
                           cursor: idx === 0 ? "not-allowed" : "pointer",
                           opacity: idx === 0 ? 0.35 : 1, fontFamily: "inherit" }}
                  title="Move up">↑</button>
                <button onClick={() => moveKey(idx, 1)} disabled={idx === localOrder.length - 1}
                  data-testid={`col-down-${c.key}`}
                  style={{ padding: "2px 8px", fontSize: 12, border: `1px solid ${T.line}`,
                           background: "white", borderRadius: 5,
                           cursor: idx === localOrder.length - 1 ? "not-allowed" : "pointer",
                           opacity: idx === localOrder.length - 1 ? 0.35 : 1, fontFamily: "inherit" }}
                  title="Move down">↓</button>
              </div>
            );
          })}
          {filteredList.length === 0 && (
            <div style={{ padding: 16, color: T.muted, fontSize: 12, textAlign: "center" }}>
              No columns match &quot;{q}&quot;.
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap", alignItems: "center" }}>
          <button data-testid="col-mgr-select-all" onClick={selectAll}
            style={{ padding: "8px 12px", background: "white", color: T.slate,
                     border: `1px solid ${T.line}`, borderRadius: 8, cursor: "pointer", fontFamily: "inherit", fontSize: 12 }}>Select all columns</button>
          <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
            {savedFlash && (
              <span data-testid="col-mgr-saved-flash"
                style={{ fontSize: 11.5, color: "#166534", background: "#DCFCE7",
                         padding: "6px 10px", borderRadius: 999, fontWeight: 600,
                         border: "1px solid #86EFAC" }}>
                ✓ Saved
              </span>
            )}
            <button data-testid="col-mgr-cancel" onClick={onClose}
              style={{ padding: "8px 14px", background: "white", color: T.slate,
                       border: `1px solid ${T.line}`, borderRadius: 8, cursor: "pointer", fontFamily: "inherit", fontSize: 12 }}>Cancel</button>
            <button data-testid="col-mgr-save-default" onClick={saveAsDefault}
              title="Save the current column order as your default sequence. Loads automatically on every future visit."
              style={{ padding: "8px 14px", background: "white", color: T.brand,
                       border: `1px solid ${T.brand}`, borderRadius: 8, cursor: "pointer",
                       fontFamily: "inherit", fontSize: 12, fontWeight: 600 }}>Save as Default Sequence</button>
            <button data-testid="col-mgr-apply" onClick={apply}
              style={{ padding: "8px 14px", background: T.ink, color: "white",
                       border: "none", borderRadius: 8, cursor: "pointer", fontFamily: "inherit", fontSize: 12, fontWeight: 600 }}>Apply</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Exceptions() { return null; }  // Deprecated: functionality merged into Members via Payment/Review Status toggles.

/* =====================================================================
 * Payments page
 * ===================================================================== */
/* =====================================================================
 * Payments page — Payment Status and Review Status are separate columns.
 * Payment Status only ever shows Paid / Unpaid / Refunded.
 * Review Status is derived by joining the application document (which
 * carries the membership review lifecycle).
 * ===================================================================== */

/** Normalise Razorpay's raw payment status into the 3 business values. */
function _paymentDisplayStatus(row) {
  if (row.refund_status === "refunded" || row.status === "refunded") return "refunded";
  if (row.status === "paid" || row.status === "captured") return "paid";
  return "unpaid";
}
const PAYMENT_STATUS_META = {
  paid:            { label: "Paid",           bg: "#DCFCE7", fg: "#166534" },
  unpaid:          { label: "Unpaid",         bg: "#FEF3C7", fg: "#92400E" },
  to_be_refunded:  { label: "To Be Refunded", bg: "#FFEDD5", fg: "#9A3412" },
  refunded:        { label: "Refunded",       bg: "#EDE9FE", fg: "#6B21A8" },
  no_refund:       { label: "No Refund",      bg: "#F1F5F9", fg: "#334155" },
};
const ADMIN_PAY_STATUSES = ["paid", "to_be_refunded", "refunded", "no_refund"];
function PaymentStatusPill({ status }) {
  const m = PAYMENT_STATUS_META[status] || PAYMENT_STATUS_META.unpaid;
  const label = useHl(m.label);
  return (
    <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 999,
                   background: m.bg, color: m.fg, fontSize: 11, fontWeight: 600,
                   letterSpacing: "0.02em" }}>{label}</span>
  );
}

const REVIEW_STATUS_META = {
  pending_review: { label: "Review Pending", bg: "#DBEAFE", fg: "#1E40AF" },
  onboarded:      { label: "Onboarded",      bg: "#DCFCE7", fg: "#166534" },
  rejected:       { label: "Rejected",       bg: "#FEE2E2", fg: "#991B1B" },
  removed:        { label: "Removed",        bg: "#F5F5F4", fg: "#57534E" },
};
function ReviewStatusPill({ status }) {
  const m = REVIEW_STATUS_META[status];
  if (!m) return <span style={{ color: T.muted }}>—</span>;
  return (
    <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 999,
                   background: m.bg, color: m.fg, fontSize: 11, fontWeight: 600,
                   letterSpacing: "0.02em" }}>{m.label}</span>
  );
}

const PAYMENT_COLS = [
  { key: "razorpay_payment_id", label: "Payment ID", w: 200, render: (r) => <CopyCell value={r.razorpay_payment_id} /> },
  // Applicant Name — pulled straight from the enriched Application
  // record (see Payments.load enrichment). Exposed as top-level `name`
  // so the Excel-style column search/filter/sort/export all work
  // without any special-cased accessor.
  { key: "name", label: "Name", w: 200, render: (r) => (
      r.name ? <CopyCell value={r.name} /> : <span style={{ color: T.muted }}>—</span>
    )
  },
  // Chapter — the applicant's assigned High Five Access city chapter
  // (e.g. Raipur, Bangalore, Pune). Sourced from the same enriched
  // Application record so it stays in step with Members without an
  // extra fetch. Exposed as top-level `chapter` so the Excel-style
  // TableView drives search / distinct / sort / export via the
  // standard `r[c.key]` accessor.
  { key: "chapter", label: "Chapter", w: 160, render: (r) => (
      r.chapter ? <CopyCell value={r.chapter} /> : <span style={{ color: T.muted }}>—</span>
    )
  },
  { key: "amount_paise",        label: "Amount",     w: 110, render: (r) => {
      const v = rupees(adminDisplayMembershipAmount(r.amount_paise));
      return v === "—" ? <span style={{ color: T.muted }}>—</span> : <span style={{ display: "inline-flex", alignItems: "center" }}><span>{v}</span><CopyIcon value={v} /></span>;
    }
  },
  { key: "method",              label: "Method", w: 100 },
  { key: "payment_status",      label: "Payment Status", w: 130, render: (r) => {
      // Read from the pre-computed mirror of Members' payment_status
      // (see Payments.load). Never recompute from the raw payment row
      // so the two tables cannot drift apart.
      const s = r._display_payment_status || "unpaid";
      return (
        <span style={{ display: "inline-flex", alignItems: "center" }}>
          <PaymentStatusPill status={s} />
          <CopyIcon value={(PAYMENT_STATUS_META[s] || PAYMENT_STATUS_META.unpaid).label} />
        </span>
      );
    }
  },
  { key: "review_status",       label: "Review Status", w: 150, render: (r) => (
      <span style={{ display: "inline-flex", alignItems: "center" }}>
        <ReviewStatusPill status={r._review_status} />
        {r._review_status && <CopyIcon value={REVIEW_STATUS_META[r._review_status].label} />}
      </span>
    )
  },
  { key: "paid_at",             label: "Payment Date", w: 120, render: (r) => {
      const v = fmtDate(r.paid_at);
      return v === "—" ? <span style={{ color: T.muted }}>—</span> : <span style={{ display: "inline-flex", alignItems: "center" }}><span>{v}</span><CopyIcon value={v} /></span>;
    }
  },
  { key: "paid_at_time",        label: "Payment Time", w: 110, render: (r) => {
      const v = fmtTime(r.paid_at);
      return !v ? <span style={{ color: T.muted }}>—</span> : <span style={{ display: "inline-flex", alignItems: "center" }}><span>{v}</span><CopyIcon value={v} /></span>;
    }
  },
  { key: "application_id",      label: "Application ID", w: 180, render: (r) => <CopyCell value={r.application_id} /> },
  { key: "email",               label: "Email", w: 200, render: (r) => <CopyCell value={r.email} href={_mailtoOf(r.email)} /> },
  { key: "created_at",          label: "Created", w: 170, render: (r) => {
      // Combined "DD/MM/YYYY HH:MM:SS" in IST — matches Payment Date/Time.
      if (!r.created_at) return <span style={{ color: T.muted }}>—</span>;
      const combined = fmtDateTime(r.created_at);
      return <span>{combined}</span>;
    }
  },
  { key: "error_description",   label: "Error", w: 220, wrap: true, render: (r) => {
      // Only surface the checkout error text if this specific payment
      // attempt itself failed. A stale `error_description` on a row
      // whose `status` later became paid/captured/refunded is
      // historical metadata and must not read as a live error.
      const st = (r.status || "").toLowerCase();
      const isSuccess = st === "paid" || st === "captured" || st === "refunded";
      if (!r.error_description || isSuccess) return <span style={{ color: T.muted }}>—</span>;
      return <span style={{ color: T.danger, fontSize: 12 }}>{r.error_description}</span>;
    } },
];

function Payments({ api }) {
  const [rows, setRows] = useState([]); const [q, setQ] = useState(""); const [err, setErr] = useState("");
  const [pFilter, setPFilter] = useState(() => {
    const v = _prefGet(PREF_KEYS.defaultPaymentPayments, "all");
    return new Set([["all","paid","refunded"].includes(v) ? v : "all"]);
  });
  // Review Status filter — Payments now mirrors the Members tab exactly
  // (same options, same two-card layout, same bidirectional linking).
  // Refunded is the only extra financial state that lives on Payments.
  const [rFilter, setRFilter] = useState(() => new Set(["all"]));
  const [mtFilter, setMtFilter] = useState(_readMtPref);
  // ---- Payments-scoped P↔R interlock rules -------------------------
  // P → allowed R:  determines which Review Status buttons stay enabled.
  //   all      → every R
  //   paid     → every R
  //   unpaid   → only { all }  (no review has happened yet)
  //   refunded → { all, rejected }  (only rejected apps can be refunded)
  const _allowedRForP = (p) => {
    if (p === "unpaid") return new Set(["all"]);
    if (p === "refunded") return new Set(["all", "rejected"]);
    return new Set(["all", "pending_review", "onboarded", "rejected", "removed"]);
  };
  // Union across every selected P key.
  const _allowedRFor = (pSet) => {
    if (!pSet || pSet.has("all") || pSet.size === 0)
      return new Set(["all", "pending_review", "onboarded", "rejected", "removed"]);
    const out = new Set();
    pSet.forEach((p) => _allowedRForP(p).forEach((v) => out.add(v)));
    return out;
  };
  // R → allowed P:  determines which Payment Status buttons stay enabled.
  //   all             → every P
  //   pending_review  → { all, paid }
  //   onboarded       → { all, paid }
  //   removed         → { all, paid }
  //   rejected        → { all, paid, refunded }  (differentiates
  //                     pending-refund vs already-refunded rejections)
  const _allowedPForR = (r) => {
    if (r === "rejected") return new Set(["all", "paid", "refunded"]);
    if (r === "pending_review" || r === "onboarded" || r === "removed")
      return new Set(["all", "paid"]);
    return new Set(["all", "paid", "unpaid", "refunded"]);
  };
  const _allowedPFor = (rSet) => {
    if (!rSet || rSet.has("all") || rSet.size === 0)
      return new Set(["all", "paid", "unpaid", "refunded"]);
    const out = new Set();
    rSet.forEach((r) => _allowedPForR(r).forEach((v) => out.add(v)));
    return out;
  };
  const handlePSelect = (k, mode, snapshot) => {
    setPFilter((prev) => {
      let next;
      if (mode === "replace") next = new Set([k]);
      else {
        next = new Set(mode === "toggle-from" ? (snapshot || []) : prev);
        if (k === "all") { next = new Set(["all"]); }
        else if (next.has(k)) { next.delete(k); if (next.size === 0 || (next.size === 1 && next.has("all"))) next = new Set(["all"]); }
        else { next.delete("all"); next.add(k); }
      }
      // Trim rFilter to still-allowed keys given the new P selection.
      const allowedR = _allowedRFor(next);
      setRFilter((r) => {
        const cleaned = new Set([...r].filter((k2) => allowedR.has(k2)));
        if (cleaned.size === 0) cleaned.add("all");
        return cleaned;
      });
      return next;
    });
  };
  const handleRSelect = (k, mode, snapshot) => {
    setRFilter((prev) => {
      let next;
      if (mode === "replace") next = new Set([k]);
      else {
        next = new Set(mode === "toggle-from" ? (snapshot || []) : prev);
        if (k === "all") { next = new Set(["all"]); }
        else if (next.has(k)) { next.delete(k); if (next.size === 0) next.add("all"); }
        else { next.delete("all"); next.add(k); }
      }
      // Rejected / Removed may be paid or refunded → widen Payment Status
      // to All. Otherwise trim pFilter to still-allowed keys for the new R.
      if (next.has("rejected") || next.has("removed")) {
        setPFilter(new Set(["all"]));
        return next;
      }
      const allowedP = _allowedPFor(next);
      setPFilter((p) => {
        const cleaned = new Set([...p].filter((k2) => allowedP.has(k2)));
        if (cleaned.size === 0) cleaned.add("all");
        return cleaned;
      });
      return next;
    });
  };
  const disabledR = new Set(
    R_OPTS.map((o) => o.k).filter((k) => !_allowedRFor(pFilter).has(k))
  );
  const disabledP = new Set(
    P_OPTS.map((o) => o.k).filter((k) => !_allowedPFor(rFilter).has(k))
  );
  const [hiddenCols, setHiddenCols] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("hfa-admin-pref-payments-hidden") || "[]")); }
    catch (_) { return new Set(); }
  });
  const [colOrder, setColOrder] = useState(() => _readTableColOrder("payments"));
  const paymentCols = useMemo(() => _applyColOrder(PAYMENT_COLS, colOrder), [colOrder]);
  useEffect(() => { if (colOrder) _writeTableColOrder("payments", colOrder); }, [colOrder]);
  const [showColMgr, setShowColMgr] = useState(false);
  const [showExport, setShowExport] = useState(false);
  // Quick Date Filter — always filters by Payment Date. Seeded from
  // the Payments-specific default in Settings. Independent of Dashboard
  // and Members.
  const [dateRange, setDateRange] = useGlobalDateRange(PREF_KEYS.defaultDatePayments);
  const [clearSig, setClearSig] = useState(0);
  useEffect(() => {
    try { localStorage.setItem("hfa-admin-pref-payments-hidden", JSON.stringify([...hiddenCols])); }
    catch (_) {}
  }, [hiddenCols]);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const load = useCallback(async ({ force } = {}) => {
    // Shared-store SWR: paint cached rows first, revalidate only when
    // stale/forced. Search is applied client-side (spec §3.9).

    const buildEnriched = (payRows, memRows) => {
      const appMap = {};
      (memRows || []).forEach((r) => { if (r.application_id) appMap[r.application_id] = r; });
      const refundedAppIds = new Set();
      (payRows || []).forEach((p) => {
        if ((p.status || "").toLowerCase() === "refunded" && p.application_id) {
          refundedAppIds.add(p.application_id);
        }
      });
      return (payRows || []).map((p) => {
        const app = appMap[p.application_id] || {};
        const membersDisplay = refundedAppIds.has(p.application_id)
          ? "refunded"
          : ((app.payment_status || "").toLowerCase() || "unpaid");
        const applicantName = app.full_name || app.contact_name || "";
        const applicantChapter = app.city_chapter || "";
        return {
          ...p,
          _review_status: app.status || "",
          _membership_type: app.membership_type || "single",
          _display_payment_status: membersDisplay,
          name: applicantName,
          chapter: applicantChapter,
          _app_full_name: app.full_name || "",
          _app_email: app.email || p.email || "",
          _app_whatsapp: app.whatsapp_number || p.contact || "",
          _app_current_city: app.current_city || "",
          _app_city_chapter: applicantChapter,
          _app_primary_circle: app.primary_circle || "",
          _app_secondary_circle: app.secondary_circle || "",
          _app_membership_id: app.membership_id || "",
          _app_contact_name: app.contact_name || "",
        };
      });
    };

    const applyEnriched = (payRows, memRows) => {
      const enriched = buildEnriched(payRows, memRows);
      setRows(enriched);
      // Keep any open drawer in sync with fresh row data.
      setSelectedPayment((sel) => {
        if (!sel) return sel;
        const fresh = enriched.find((r) => r.razorpay_payment_id && r.razorpay_payment_id === sel.razorpay_payment_id);
        return fresh || sel;
      });
    };
    const c = cachedPair();
    if (c) applyEnriched(c.p.rows, c.m.rows);
    try {
      const { m, p } = await loadAdminData(api, { force });
      applyEnriched(p.rows, m.rows);
    }
    catch (e) { setErr(e?.response?.data?.detail || "Failed to load."); }
  }, [api]);
  useEffect(() => { load(); }, [load]);
  // Row-level mutations: the Members feed is patched in the store and the
  // Payments feed is marked stale → repaint + silent revalidate. Bulk /
  // import / refresh events force a silent reload. Data stays on screen.
  useEffect(() => {
    const onStore = () => load();
    const onChange = (e) => { if (!e?.detail?.app_uuid) load({ force: true }); };
    window.addEventListener(STORE_EVENT, onStore);
    window.addEventListener(DATA_CHANGED_EVENT, onChange);
    return () => {
      window.removeEventListener(STORE_EVENT, onStore);
      window.removeEventListener(DATA_CHANGED_EVENT, onChange);
    };
  }, [load]);

  const filtered = useMemo(() => {
    const dateActive = dateRange && dateRange.from && dateRange.to;
    const pAll = pFilter.has("all") || pFilter.size === 0;
    const rAll = rFilter.has("all") || rFilter.size === 0;
    const qTrim = String(q || "").trim();
    return rows.filter((r) => {
      if (!pAll && !pFilter.has(r._display_payment_status)) return false;
      if (!rAll && !rFilter.has(r._review_status || "")) return false;
      if (!_mtMatches(mtFilter, r._membership_type)) return false;
      // Quick Date Filter — matches by Payment Date (see req §2). "All
      // Time" also keeps undated imported payment records (amount only).
      if (dateActive && !(dateRange.preset === "all_time" && !_paymentDateOf(r))
          && !inRange(_paymentDateOf(r), dateRange)) return false;
      // Global search — same matcher + phone-digit normalisation
      // used by Members, applied against the enriched payment row so
      // "rahul", "9876543210", and "rahul 9876" all resolve reliably
      // to the correct payment record (spec §3.7).
      if (qTrim && !_matchesRowQuery(r, qTrim, _PAYMENT_SEARCH_FIELDS)) return false;
      return true;
    });
  }, [rows, pFilter, rFilter, mtFilter, dateRange, q]);

  // Live count reflecting every layer of filtering (top toggle + global
  // search + column filters). Seeded from `filtered.length`; TableView
  // owns the source of truth via `onFilteredCountChange` (fires on any
  // change to underlying rows OR to internal column filters), so we
  // deliberately do NOT sync it back to `filtered.length` here —
  // doing so would clobber a valid column-filter-driven count.
  const [visibleCount, setVisibleCount] = useState(filtered.length);
  // Exact rows the table currently shows (quick filters + column filters).
  const [visibleRows, setVisibleRows] = useState(null);

  // Export-only column selection + order (never affects the table view).
  const [exportCfg, setExportCfg] = useState(() => _readExportCols(EXPORT_COLS_KEY_PAYMENTS));
  const [showExportCols, setShowExportCols] = useState(false);
  const exportCols = useMemo(() => _resolveExportCols(PAYMENT_COLS, exportCfg), [exportCfg]);
  const doExport = (fmt) => {
    const rows = visibleRows || filtered;
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:.]/g, "-");
    if (fmt === "xlsx") downloadXlsx(`hfa-payments-${stamp}.xlsx`, rows, exportCols);
    else downloadCsv(`hfa-payments-${stamp}.csv`, buildCsv(rows, exportCols));
    setShowExport(false);
  };
  return (
    <div>
      <ListPageHeader title="Payments" count={visibleCount}
        onExport={() => setShowExport(true)} onExportFilter={() => setShowExportCols(true)}
        exportEnabled={visibleCount > 0} onRefresh={() => load({ force: true })}
        hairline exportActive={showExport || showExportCols}
        activeDateChip={activeDateChipFor(dateRange)}
        onShiftSingleDay={(delta) => setDateRange(_shiftSingleDay(dateRange, delta))}
        topRight={<DateRangePicker value={dateRange} onChange={setDateRange} chipOutline="hairline-ink" />}
        onClearFilters={() => {
          setQ("");
          setPFilter(new Set(["all"]));
          setRFilter(new Set(["all"]));
          setMtFilter(_readMtPref());
          setDateRange(TODAY_RANGE());
          setClearSig((n) => n + 1);
        }}
        extra={
          <button data-testid="btn-column-manager-payments" data-hfa-btn data-hfa-outline="1" data-active={showColMgr ? "true" : undefined}
            aria-expanded={showColMgr} onClick={() => setShowColMgr(true)}
            style={outlineBtnStyle(true, showColMgr)}>Columns</button>
        }
        leftBlock={
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-start" }}>
            <FilterGroupCard heading="Payment Status">
              <SegToggle options={P_OPTS} valueSet={pFilter} onSelect={handlePSelect}
                         disabledSet={disabledP} testid="pay-p-toggle" />
            </FilterGroupCard>
            <FilterGroupCard heading="Review Status">
              <SegToggle options={R_OPTS} valueSet={rFilter} onSelect={handleRSelect}
                         disabledSet={disabledR} testid="pay-r-toggle" />
            </FilterGroupCard>
            <MembershipTypeFilter value={mtFilter} onChange={setMtFilter} testid="pay-mt-toggle" />
          </div>
        } />
      {/* Filter group renders inside the header's leftBlock. */}
      {err && <div style={{ color: T.danger, fontSize: 13, marginBottom: 10 }}>{err}</div>}
      <TableView rows={filtered} cols={paymentCols}
        testid="admin-payments-table" hiddenCols={hiddenCols}
        tableKey="payments"
        onFilteredCountChange={setVisibleCount}
        onFilteredRowsChange={setVisibleRows}
        clearSignal={clearSig}
        onOpenRow={(r) => setSelectedPayment(r)}
        onReorderCols={(fromKey, toKey) => {
          const current = _applyColOrder(PAYMENT_COLS, colOrder).map((c) => c.key);
          const from = current.indexOf(fromKey);
          const to   = current.indexOf(toKey);
          if (from === -1 || to === -1 || from === to) return;
          current.splice(from, 1);
          current.splice(to, 0, fromKey);
          setColOrder(current);
        }} />
      {selectedPayment && (
        <PaymentDrawer
          row={selectedPayment}
          onClose={() => setSelectedPayment(null)} />
      )}
      {showColMgr && (
        <ColumnManager allCols={PAYMENT_COLS} hiddenCols={hiddenCols} colOrder={colOrder}
          tableKey="payments"
          onChange={({ hidden, order }) => { setHiddenCols(hidden); if (order) setColOrder(order); }}
          onClose={() => setShowColMgr(false)} />
      )}
      {showExport && (
        <ExportPickerModal count={(visibleRows || filtered).length}
          onPickXlsx={() => doExport("xlsx")} onPickCsv={() => doExport("csv")}
          onClose={() => setShowExport(false)} />
      )}
      {showExportCols && (
        <ExportColumnsPanel allCols={PAYMENT_COLS} cfg={exportCfg}
          onApply={(next) => { setExportCfg(next); setShowExportCols(false); }}
          onSaveDefault={(next) => { _writeExportCols(EXPORT_COLS_KEY_PAYMENTS, next); setExportCfg(next); }}
          onClose={() => setShowExportCols(false)} />
      )}
    </div>
  );
}

const EXPORT_COLS_KEY_PAYMENTS = "hfa-admin-export-cols-payments";

/* =====================================================================
 * Payment Detail Drawer  (Payments tab — opens on row click).
 *
 * READ-ONLY financial mirror. The Members tab is now the sole
 * operational surface for both Review Status AND Payment Status
 * edits. This drawer simply presents payment + applicant metadata
 * and the audit trail for admins to reference; every editing
 * control has been removed.
 * ===================================================================== */
function PaymentDrawer({ row, onClose }) {
  const isRefunded = (row._display_payment_status || row.status) === "refunded"
                     || !!row.refunded_at;

  const paymentStatusPill = () => {
    const key = (row._display_payment_status || "").toLowerCase();
    const meta = PAYMENT_STATUS_META[key] || PAYMENT_STATUS_META.unpaid;
    return (
      <span data-testid="pay-drawer-payment-status"
        style={{ padding: "4px 10px", fontSize: 11.5, fontWeight: 700,
                 background: meta.bg, color: meta.fg, borderRadius: 999 }}>
        {meta.label}
      </span>
    );
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 80, display: "flex",
                  background: "rgba(0,0,0,0.35)" }}
         onClick={onClose} data-testid="payment-drawer">
      <div style={{ flex: 1 }} />
      <div onClick={(e) => e.stopPropagation()} data-hfa-drawer
        style={{ width: "min(560px, 100%)", background: "white",
                 height: "100vh", overflow: "auto",
                 boxShadow: "-24px 0 60px rgba(11,11,15,0.20)",
                 padding: "20px 24px" }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: "-0.01em" }}>
              Payment
            </div>
            <div style={{ marginTop: 4, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontFamily: "monospace", fontSize: 12, color: T.slate }}>
                {row.razorpay_payment_id || "—"}
              </span>
              {paymentStatusPill()}
            </div>
            <div style={{ marginTop: 6, fontSize: 11, color: T.muted, fontWeight: 500 }}>
              Read-only · Manage status from the Members tab.
            </div>
          </div>
          <button onClick={onClose} data-testid="pay-drawer-close"
            style={{ background: "transparent", border: "none", cursor: "pointer",
                     fontSize: 22, color: T.muted, lineHeight: 1 }}>×</button>
        </div>

        {/* Payment Information */}
        <Section title="Payment Information">
          <KV k="Payment Status" v={(PAYMENT_STATUS_META[(row._display_payment_status || "unpaid")] || PAYMENT_STATUS_META.unpaid).label} />
          <KV k="Amount" v={rupees(adminDisplayMembershipAmount(row.amount_paise))} />
          <KV k="Payment Date" v={row.paid_at ? fmtDate(row.paid_at) : ""} />
          <KV k="Payment Time" v={row.paid_at ? fmtTime(row.paid_at) : ""} />
          <KV k="Razorpay Payment ID" v={row.razorpay_payment_id} />
          <KV k="Order ID" v={row.razorpay_order_id} />
          <KV k="Transaction Reference" v={row.razorpay_payment_id} />
          <KV k="Payment Method" v={row.method} />
          <KV k="Created" v={row.created_at ? fmtDateTime(row.created_at) : ""} />
        </Section>

        {/* Applicant Information */}
        <Section title="Applicant">
          <KV k="Member ID" v={row._app_membership_id} />
          <KV k="Name" v={row._app_full_name || row._app_contact_name} />
          <KV k="Email" v={row._app_email} link={_mailtoOf(row._app_email)} />
          <KV k="WhatsApp Number" v={row._app_whatsapp}
              link={row._app_whatsapp ? `https://wa.me/91${String(row._app_whatsapp).replace(/\D/g, "")}` : ""} />
          <KV k="Current City" v={row._app_current_city} />
          <KV k="City Chapter" v={row._app_city_chapter} />
          <KV k="Primary Circle" v={row._app_primary_circle} />
          <KV k="Application ID" v={row.application_id} />
        </Section>

        {/* Review Status — read-only, decisions live on the Members page */}
        <Section title="Review Status">
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0" }}>
            <ReviewStatusPill status={row._review_status} />
            <span style={{ fontSize: 11.5, color: T.muted }}>
              Read-only · Change from the Members tab.
            </span>
          </div>
        </Section>

        {/* Refund Information — visible permanently once refunded */}
        {isRefunded && (
          <Section title="Refund Information">
            <KV k="Refund Status" v="Refunded" />
            <KV k="Refunded On"
                v={row.refunded_at ? `${fmtDateTime(row.refunded_at)} IST` : ""} />
            <KV k="Refunded By" v={row.refunded_by || "Admin"} />
            {row.refund_notes && <KV k="Notes" v={row.refund_notes} multiline />}
          </Section>
        )}
      </div>
    </div>
  );
}


/* =====================================================================
 * Row detail drawer  (opens on row click)
 * ===================================================================== */
function DetailDrawer({ appId, api, onClose, onChanged }) {
  const hlRe = useContext(HighlightCtx);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [remark, setRemark] = useState("");
  const [contactName, setContactName] = useState("");
  const [editingName, setEditingName] = useState(false);
  // Review-status change UI state (dropdown value + confirmation modal).
  const [pendingStatus, setPendingStatus] = useState("");
  const paySelectRef = useRef(null);
  const [pendingReason, setPendingReason] = useState("");
  // Payment-status change UI state — mirrors the review-status flow but
  // targets the /payment-status endpoint (paid <-> refunded only).
  const [pendingPayStatus, setPendingPayStatus] = useState("");
  const [pendingPayReason, setPendingPayReason] = useState("");
  // Resend confirmation email UI state.
  const [confirmResend, setConfirmResend] = useState(false);
  const [starOverride, setStarOverride] = useState(null);
  // Circle suitability: which circle is pending confirmation ("primary"|"secondary").
  const [pendingCircle, setPendingCircle] = useState("");
  const [resendBusy, setResendBusy] = useState(false);
  const [resendMsg, setResendMsg] = useState(null); // {ok, text}

  const load = useCallback(async () => {
    if (!appId) return;
    setErr("");
    try {
      const data = await fetchShared(api, `/applications/${appId}`, {}, { force: true });
      setData(data);
      setContactName(data.application.contact_name || "");
    } catch (e) { setErr(e?.response?.data?.detail || "Failed to load."); }
  }, [appId, api]);
  useEffect(() => { load(); }, [load]);
  // Global "data changed" broadcast — refetch immediately whenever any
  // other surface (Member Drawer, bulk toolbar, refund flow) mutates
  // an application or payment. Keeps Members table + filters in sync
  // without any manual reload.
  useEffect(() => {
    // Own mutations already re-fetched this drawer (`await load()`), so
    // skip the echo of our own broadcast; everything else refreshes.
    const onChange = (e) => { if (e?.detail?._sameTab && e.detail.app_uuid === appId) return; load(); };
    window.addEventListener(DATA_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(DATA_CHANGED_EVENT, onChange);
  }, [load, appId]);

  if (!appId) return null;

  const app = data?.application || {};
  const payments = data?.payments || [];
  const uuid = app.id;

  /** Unified Review-Status change — used by the dropdown at the top of
   *  the drawer. Every transition among the four review states goes
   *  through the single `/review-status` endpoint, which enforces the
   *  "paid only" business rule and writes an audit-log entry. */
  const commitReviewStatus = async () => {
    if (!pendingStatus || pendingStatus === app.status) {
      setPendingStatus("");
      return;
    }
    setBusy(true); setErr("");
    try {
      await api.post(`/applications/${uuid}/review-status`, {
        new_status: pendingStatus,
        reason: pendingReason.trim() || undefined,
        changed_by: "Admin",
      });
      setPendingStatus("");
      setPendingReason("");
      await load();
      // Broadcast so Payments, Dashboard, filter counts and any other
      // mounted admin surface refresh in the same tick.
      broadcastDataChanged({ kind: "review_status_changed", app_uuid: uuid });
      if (onChanged) onChanged();
      // Hand off to the Payment Status dropdown right below so the admin
      // immediately records the financial treatment (no extra popup).
      requestAnimationFrame(() => {
        const sel = paySelectRef.current;
        if (!sel || sel.disabled) return;
        sel.scrollIntoView({ block: "center", behavior: "smooth" });
        sel.focus({ preventScroll: true });
        try { if (typeof sel.showPicker === "function") sel.showPicker(); } catch (_) { /* needs user gesture on some browsers */ }
      });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not change review status.");
    }
    finally { setBusy(false); }
  };
  const cancelReviewChange = () => { setPendingStatus(""); setPendingReason(""); };

  /** Unified Payment-Status change — used by the second dropdown in
   *  the Members drawer. Only `paid ↔ refunded` transitions are
   *  admin-permitted; `unpaid` is Razorpay-only and never appears
   *  in the dropdown as an enabled option. */
  const commitPayStatus = async () => {
    if (!pendingPayStatus) return;
    setBusy(true); setErr("");
    try {
      await api.post(`/applications/${uuid}/payment-status`, {
        new_status: pendingPayStatus,
        reason: pendingPayReason.trim() || undefined,
        changed_by: "Admin",
      });
      setPendingPayStatus("");
      setPendingPayReason("");
      await load();
      broadcastDataChanged({ kind: "payment_status_changed", app_uuid: uuid });
      if (onChanged) onChanged();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not change payment status.");
    }
    finally { setBusy(false); }
  };
  const cancelPayChange = () => { setPendingPayStatus(""); setPendingPayReason(""); };

  const toggleStarMember = async () => {
    if (starOverride !== null) return;
    const next = !app.is_star_member;
    setStarOverride(next);
    try {
      await api.patch(`/applications/${uuid}/star-member`, { star: next });
      await load();
      broadcastDataChanged({ kind: "star_member_changed", app_uuid: uuid });
      if (onChanged) onChanged();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not update Star Member status.");
    } finally { setStarOverride(null); }
  };
  const starOn = starOverride !== null ? starOverride : !!app.is_star_member;

  const commitCircleSuitability = async () => {
    if (!pendingCircle) return;
    const circle = pendingCircle;
    const unsuitable = !app[`${circle}_circle_unsuitable`];
    setBusy(true); setErr("");
    try {
      await api.patch(`/applications/${uuid}/circle-suitability`, { circle, unsuitable });
      setPendingCircle("");
      await load();
      broadcastDataChanged({ kind: "circle_suitability_changed", app_uuid: uuid });
      if (onChanged) onChanged();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not update circle suitability.");
    } finally { setBusy(false); }
  };

  const resendConfirmationEmail = async () => {
    if (resendBusy) return;
    setResendBusy(true); setResendMsg(null);
    try {
      const r = await api.post(`/applications/${uuid}/resend-confirmation-email`);
      setConfirmResend(false);
      setResendMsg({ ok: true, text: `Confirmation email resent to ${r.data.to}.` });
      await load();
      broadcastDataChanged({ kind: "confirmation_email_resent", app_uuid: uuid });
    } catch (e) {
      setConfirmResend(false);
      setResendMsg({ ok: false, text: e?.response?.data?.detail || "Could not resend the confirmation email." });
    } finally { setResendBusy(false); }
  };

  const addRemark = async () => {
    if (!remark.trim()) return;
    setBusy(true); setErr("");
    try {
      await api.post(`/applications/${uuid}/remarks`, { text: remark.trim() });
      setRemark("");
      await load();
      broadcastDataChanged({ kind: "remark_added", app_uuid: uuid });
      if (onChanged) onChanged();
    } catch (e) { setErr(e?.response?.data?.detail || "Failed to add remark"); }
    finally { setBusy(false); }
  };

  const saveContactName = async () => {
    setBusy(true); setErr("");
    try {
      await api.patch(`/applications/${uuid}/contact-name`, { contact_name: contactName.trim() });
      setEditingName(false); await load();
      broadcastDataChanged({ kind: "contact_name_updated", app_uuid: uuid });
      if (onChanged) onChanged();
    } catch (e) { setErr(e?.response?.data?.detail || "Failed to save"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-detail-drawer"
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 100, display: "flex", justifyContent: "flex-end" }}
      onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-hfa-drawer
        style={{ width: "min(720px, 100%)", background: "white", height: "100vh", overflowY: "auto",
                 boxShadow: "-16px 0 40px rgba(0,0,0,0.15)", fontFamily: font }}>
        {!data && <div style={{ padding: 30, color: T.muted }}>Loading…</div>}
        {data && (
          <>
            <div style={{ padding: "20px 26px", borderBottom: `1px solid ${T.line}`,
                          display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <div style={{ fontSize: 20, fontWeight: 700, color: T.ink }}>{app.full_name ? _hlText(app.full_name, hlRe) : "—"}</div>
                <div style={{ fontSize: 12, color: T.muted, marginTop: 3 }}>
                  {_hlText(app.application_id, hlRe)} · <StatusPill status={app.status} isArchived={app.is_archived} />
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <button type="button" onClick={toggleStarMember}
                  data-testid="admin-star-member-toggle"
                  aria-pressed={starOn}
                  aria-label={starOn ? "Star Member: Yes (click to unmark)" : "Star Member: No (click to mark)"}
                  title={starOn ? "Star Member" : "Mark as Star Member"}
                  style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 36, height: 36,
                           border: "none", background: "transparent", cursor: "pointer", padding: 0, borderRadius: 8 }}>
                  <StarGlyph filled={starOn} size={26} />
                </button>
                <button data-testid="admin-drawer-close" onClick={onClose}
                  style={{ padding: "6px 12px", fontSize: 12, border: `1px solid ${T.line}`, background: "white", borderRadius: 8, cursor: "pointer", fontFamily: "inherit" }}>Close</button>
              </div>
            </div>

            <div style={{ padding: "18px 26px" }}>
              {/* Unified Review Status control — replaces the old
                  context-specific Accept / Reject / Remove buttons.
                  Only appears for PAID applications (the review pipeline
                  is not applicable to unpaid records). */}
              {ADMIN_PAY_STATUSES.includes((app.payment_status || "").toLowerCase()) && (
                <div data-testid="admin-review-status-card"
                  style={{ marginBottom: 20, padding: 14, borderRadius: 12,
                           border: `1px solid ${T.line}`, background: T.brandSoft }}>
                  <div style={{ fontSize: 11, textTransform: "uppercase",
                                letterSpacing: "0.08em", color: T.muted, marginBottom: 8 }}>
                    Review Status
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 11, color: T.slate }}>Current:</span>
                    <StatusPill status={app.status} isArchived={app.is_archived} />
                  </div>
                  <label htmlFor="admin-review-status-select"
                    style={{ display: "block", fontSize: 11.5, color: T.slate, marginBottom: 5 }}>
                    Change Review Status
                  </label>
                  <select id="admin-review-status-select"
                    data-testid="admin-review-status-select"
                    value={app.status || "pending_review"}
                    disabled={busy}
                    onChange={(e) => { setPendingReason(""); setPendingStatus(e.target.value); }}
                    style={{ width: "100%", maxWidth: 320,
                             padding: "9px 12px", fontSize: 13, fontFamily: "inherit",
                             border: `1px solid ${T.line}`, borderRadius: 8,
                             background: "white", cursor: busy ? "not-allowed" : "pointer" }}>
                    {/* Transition matrix (per business rules):
                          pending_review → onboarded, rejected           (removed disabled)
                          onboarded      → pending_review, removed       (rejected disabled)
                          rejected       → pending_review                (onboarded, removed disabled)
                          removed        → pending_review, onboarded     (rejected disabled)
                        Current status is always selectable (it's the
                        current value); disallowed options remain visible
                        but greyed out so the full workflow is clear. */}
                    {(() => {
                      const ALLOWED = {
                        pending_review: new Set(["pending_review", "onboarded", "rejected"]),
                        onboarded:      new Set(["pending_review", "onboarded", "removed"]),
                        rejected:       new Set(["pending_review", "rejected"]),
                        removed:        new Set(["pending_review", "onboarded", "removed"]),
                      };
                      const allow = ALLOWED[app.status] || new Set(["pending_review", "onboarded", "rejected", "removed"]);
                      return [
                        ["pending_review", "Review Pending"],
                        ["onboarded",      "Onboarded"],
                        ["rejected",       "Rejected"],
                        ["removed",        "Removed"],
                      ].map(([v, label]) => (
                        <option key={v} value={v} disabled={!allow.has(v)}>{label}</option>
                      ));
                    })()}
                  </select>
                </div>
              )}
              {/* Payment Status control — Members drawer is the sole
                  admin surface for Payment Status too. The dropdown is
                  always visible (so admins have a consistent mental
                  model), but its options are context-gated per the
                  business rules (paid↔refunded only, no manual unpaid). */}
              {(() => {
                const payDisplay = _displayPaymentStatusFor(app, payments[0]);
                const editable = payDisplay !== "unpaid";
                return (
                  <div data-testid="admin-payment-status-card"
                    style={{ marginBottom: 20, padding: 14, borderRadius: 12,
                             border: `1px solid ${T.line}`, background: T.brandSoft }}>
                    <div style={{ fontSize: 11, textTransform: "uppercase",
                                  letterSpacing: "0.08em", color: T.muted, marginBottom: 8 }}>
                      Payment Status
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 11, color: T.slate }}>Current:</span>
                      <span data-testid="admin-payment-status-current"
                        style={{ padding: "3px 10px", fontSize: 12, fontWeight: 700,
                                 background: (PAYMENT_STATUS_META[payDisplay] || PAYMENT_STATUS_META.unpaid).bg,
                                 color: (PAYMENT_STATUS_META[payDisplay] || PAYMENT_STATUS_META.unpaid).fg,
                                 borderRadius: 999 }}>
                        {(PAYMENT_STATUS_META[payDisplay] || PAYMENT_STATUS_META.unpaid).label}
                      </span>
                    </div>
                    <label htmlFor="admin-payment-status-select"
                      style={{ display: "block", fontSize: 11.5, color: T.slate, marginBottom: 5 }}>
                      Change Payment Status
                    </label>
                    <select id="admin-payment-status-select" ref={paySelectRef}
                      data-testid="admin-payment-status-select"
                      value={payDisplay}
                      disabled={busy || !editable}
                      onChange={(e) => {
                        // Ignore no-op selections; ignore disabled → disabled attempts.
                        const v = e.target.value;
                        if (v === payDisplay) return;
                        setPendingPayReason("");
                        setPendingPayStatus(v);
                      }}
                      style={{ width: "100%", maxWidth: 320,
                               padding: "9px 12px", fontSize: 13, fontFamily: "inherit",
                               border: `1px solid ${T.line}`, borderRadius: 8,
                               background: "white",
                               cursor: (busy || !editable) ? "not-allowed" : "pointer" }}>
                      {payDisplay === "unpaid" && <option value="unpaid" disabled>Unpaid</option>}
                      {ADMIN_PAY_STATUSES.map((v) => (
                        <option key={v} value={v}>{PAYMENT_STATUS_META[v].label}</option>
                      ))}
                    </select>
                    {payDisplay === "unpaid" && (
                      <div style={{ marginTop: 8, fontSize: 11.5, color: T.muted, lineHeight: 1.5 }}>
                        Read-only · Only Razorpay can move a record from Unpaid to Paid.
                      </div>
                    )}
                  </div>
                );
              })()}

              {err && <div style={{ color: T.danger, fontSize: 13, marginBottom: 12 }}>{err}</div>}

              {/* Contact Name (editable, autoregen on onboard) */}
              {app.status === "onboarded" && (
                <Section title="Contact Name">
                  {!editingName ? (
                    <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                      <div style={{ fontSize: 15, fontWeight: 600 }}>{app.contact_name ? _hlText(app.contact_name, hlRe) : "—"}</div>
                      <button data-testid="admin-edit-contact-name" onClick={() => setEditingName(true)}
                        style={{ padding: "4px 10px", fontSize: 11, border: `1px solid ${T.line}`, background: "white", borderRadius: 6, cursor: "pointer", fontFamily: "inherit" }}>Edit</button>
                    </div>
                  ) : (
                    <div style={{ display: "flex", gap: 8 }}>
                      <input data-testid="admin-contact-name-input" value={contactName} onChange={(e) => setContactName(e.target.value)}
                        style={{ flex: 1, padding: "8px 10px", border: `1px solid ${T.line}`, borderRadius: 8, fontSize: 13, fontFamily: "inherit" }} />
                      <button data-testid="admin-save-contact-name" disabled={busy} onClick={saveContactName}
                        style={{ padding: "8px 14px", background: T.brand, color: "white", border: "none", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Save</button>
                      <button onClick={() => { setEditingName(false); setContactName(app.contact_name || ""); }}
                        style={{ padding: "8px 14px", background: "white", border: `1px solid ${T.line}`, borderRadius: 8, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
                    </div>
                  )}
                </Section>
              )}

              {/* Personal */}
              <Section title="Personal">
                <EditableKV
                  k="Full Name"
                  v={app.full_name}
                  fieldTestid="kv-full-name"
                  placeholder="Full name"
                  validate={(val) => (String(val || "").trim().length < 2 ? "Full name must be at least 2 characters." : "")}
                  onSave={async (val) => {
                    await api.patch(`/applications/${uuid}/full-name`, { full_name: val });
                    await load();
                    broadcastDataChanged({ kind: "full_name_updated", app_uuid: uuid });
                    if (onChanged) onChanged();
                  }} />
                <KV k="Gender" v={app.gender} />
                <KV k="Date of Birth" v={app.date_of_birth} />
                {/* Age — mirrors the same value the Members list shows
                    for this row. Prefer the pre-computed `age` field
                    if the drawer's `app` payload carries it (future-
                    proofs against a backend change) and falls back to
                    the local DOB-based calculation so the drawer never
                    silently drops the field. Rendered as its own KV
                    row directly under DOB so the two personal
                    identifiers stay visually paired. */}
                <KV
                  k="Age"
                  v={(() => {
                    const a = app.age != null && app.age !== ""
                      ? app.age
                      : _computeAgeFromDob(app.date_of_birth);
                    return a == null ? "" : String(a);
                  })()}
                />
                {/* Admin-only inline edit for the applicant's primary
                    WhatsApp number. The persisted value becomes the
                    canonical contact number used by every future admin
                    surface + the `wa.me` deep-link — see
                    /applications/{uuid}/whatsapp-number PATCH endpoint. */}
                <EditableKV
                  k="WhatsApp"
                  v={app.whatsapp_number}
                  buildLink={(n) => (n ? `https://wa.me/91${String(n).replace(/\D/g, "")}` : "")}
                  inputType="tel"
                  inputMode="numeric"
                  maxLength={10}
                  placeholder="10-digit mobile"
                  fieldTestid="kv-whatsapp"
                  normalize={(raw) => String(raw || "").replace(/\D/g, "")}
                  validate={(digits) => {
                    if (!digits) return "WhatsApp number is required.";
                    if (!/^[6-9]\d{9}$/.test(digits)) {
                      return "Enter a valid 10-digit Indian mobile number (starting with 6-9).";
                    }
                    return "";
                  }}
                  onSave={async (digits) => {
                    await api.patch(
                      `/applications/${uuid}/whatsapp-number`,
                      { whatsapp_number: digits },
                    );
                    // Reload the drawer with the fresh applicant doc
                    // AND fan out to every other open admin surface so
                    // Members / Payments tables + drawers pick up the
                    // new number without a manual refresh.
                    await load();
                    broadcastDataChanged({ kind: "whatsapp_number_updated", app_uuid: uuid });
                    if (onChanged) onChanged();
                  }} />
                <EditableKV
                  k="Email"
                  v={app.email}
                  buildLink={(e) => _mailtoOf(e)}
                  inputType="email"
                  inputMode="email"
                  placeholder="name@example.com"
                  fieldTestid="kv-email"
                  normalize={(raw) => String(raw || "").trim().toLowerCase()}
                  validate={(val) => (/^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(val) ? "" : "Enter a valid email address.")}
                  onSave={async (val) => {
                    await api.patch(`/applications/${uuid}/email`, { email: val });
                    await load();
                    broadcastDataChanged({ kind: "email_updated", app_uuid: uuid });
                    if (onChanged) onChanged();
                  }} />
                <KV k="Current City" v={app.current_city} />
                <KV k="City Chapter" v={app.city_chapter} />
                <KV k="Membership Type" v={app.membership_plan} />
                <KV k="Source" v={app.source} />
                <KV k="LinkedIn" v={app.linkedin_url} link={_linkedinUrlOf(app.linkedin_url)} />
                <KV k="Instagram" v={app.instagram_username} link={_instagramUrlOf(app.instagram_username)} />
              </Section>

              {/* Professional */}
              <Section title="Professional">
                <KV k="Professional Type" v={app.professional_type} />
                {app.professional_type_other && <KV k="Professional Type (Other)" v={app.professional_type_other} />}
                <CircleKV k="Primary Circle" v={app.primary_circle} unsuitable={!!app.primary_circle_unsuitable}
                  testid="kv-primary-circle" disabled={busy} onToggle={() => { setErr(""); setPendingCircle("primary"); }}
                  options={PRIMARY_CIRCLE_OPTIONS}
                  optionLabel={(o) => (o === PRIMARY_CIRCLE_NONE ? "None of the above" : o)}
                  onSelect={async (val) => {
                    await api.patch(`/applications/${uuid}/circle`, { circle: "primary", value: val });
                    await load();
                    broadcastDataChanged({ kind: "circle_updated", app_uuid: uuid });
                    if (onChanged) onChanged();
                  }} />
                <CircleKV k="Secondary Circle" v={app.secondary_circle} unsuitable={!!app.secondary_circle_unsuitable}
                  testid="kv-secondary-circle" disabled={busy} onToggle={() => { setErr(""); setPendingCircle("secondary"); }}
                  options={SECONDARY_CIRCLE_OPTIONS}
                  optionLabel={(o) => (o === SECONDARY_CIRCLE_NA ? "None of the above" : o)}
                  onSelect={async (val) => {
                    await api.patch(`/applications/${uuid}/circle`, { circle: "secondary", value: val });
                    await load();
                    broadcastDataChanged({ kind: "circle_updated", app_uuid: uuid });
                    if (onChanged) onChanged();
                  }} />
                <KV k="Experience" v={app.experience_range} />
                <KV k="What do you do professionally?" v={app.professional_description} multiline />
                <KV k="Secondary Description" v={app.secondary_description} multiline />
                <KV k="What are you currently looking for?" v={(app.looking_for || []).join(", ")} />
                <KV k="Notes / Questions / Suggestions" v={app.notes} multiline />
              </Section>

              {/* Acknowledgements */}
              <Section title="Acknowledgements">
                {Object.entries(app.acknowledgements || {}).map(([k, v]) => (
                  <KV key={k} k={k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())} v={v ? "Yes" : "No"} />
                ))}
              </Section>

              {/* Payment */}
              <Section title="Payment Details">
                <KV k="Payment Status" v={app.payment_status} />
                <KV k="Amount" v={rupees(adminDisplayMembershipAmount(app.amount_paise))} />
                <KV k="Razorpay Order ID" v={app.razorpay_order_id} />
                <KV k="Razorpay Payment ID" v={app.razorpay_payment_id} />
                <KV k="Paid At" v={app.paid_at ? fmtDateTime(app.paid_at) : ""} />
                {payments.length > 0 && (
                  <div style={{ marginTop: 10 }}>
                    {payments.map((p, i) => {
                      // "Historical" = a payment ATTEMPT that itself did
                      // not succeed. Only these rows may carry a
                      // failure/timeout message — a successful attempt's
                      // `error_description` is stale metadata from a
                      // previous checkout and must never appear as the
                      // current payment state (see admin bug report).
                      const st = (p.status || "").toLowerCase();
                      const isSuccess = st === "paid" || st === "captured" || st === "refunded";
                      const isHistoricalFailure = !isSuccess && !!p.error_description;
                      return (
                        <div key={p.id || i} style={{ padding: 10, background: isHistoricalFailure ? "#FEF3F2" : "#FAFAFA", borderRadius: 8, marginBottom: 8, fontSize: 12,
                                                       border: isHistoricalFailure ? `1px dashed ${T.danger}` : "none" }}>
                          <div>
                            <b>Method:</b> {p.method || "—"} · <b>Status:</b> {p.status || "—"}
                            {isHistoricalFailure && (
                              <span style={{ marginLeft: 8, fontSize: 10.5, letterSpacing: "0.06em",
                                              textTransform: "uppercase", color: T.muted, fontWeight: 700 }}>
                                Historical attempt
                              </span>
                            )}
                          </div>
                          <div><b>Payment ID:</b> {p.razorpay_payment_id || "—"}</div>
                          {p.refunds && p.refunds.length > 0 && (<div style={{ color: "#6B21A8" }}><b>Refunds:</b> {p.refunds.length}</div>)}
                          {/* Only render the checkout error text when this
                              specific attempt itself failed. A success
                              row's stale `error_description` is
                              suppressed so it can't contradict the
                              authoritative Paid/Refunded state on the
                              KV row above. */}
                          {isHistoricalFailure && (
                            <div style={{ color: T.danger, marginTop: 4 }}>Error: {p.error_description}</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </Section>

              {/* Confirmation email */}
              <Section title="Confirmation Email">
                {(() => {
                  const st = app.confirmation_email_status
                    || (app.confirmation_email_claimed_at ? "failed" : "not_sent");
                  const label = { sent: "Sent", skipped: "Skipped", failed: "Failed", not_sent: "Not sent" }[st] || st;
                  const resends = app.confirmation_email_resends || [];
                  const last = resends[resends.length - 1];
                  const canResend = app.payment_status === "paid" && !!app.razorpay_payment_id && !!app.email;
                  return (
                    <>
                      <KV k="Status" v={label} />
                      {app.confirmation_email_sent_at && <KV k="Sent At" v={fmtDateTime(app.confirmation_email_sent_at)} />}
                      {app.confirmation_email_id && <KV k="Resend Email ID" v={app.confirmation_email_id} />}
                      {app.confirmation_email_error && st !== "sent" && <KV k="Error" v={app.confirmation_email_error} multiline />}
                      {last && (
                        <KV k={`Last Resend Attempt (${resends.length})`}
                          v={[
                            last.status === "sent" ? "Sent" : last.status === "skipped" ? "Skipped" : "Failed",
                            fmtDateTime(last.at),
                            last.email_id || "",
                            last.error && last.status !== "sent" ? last.error : "",
                          ].filter(Boolean).join(" • ")} />
                      )}
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
                        <button data-testid="admin-resend-email-btn"
                          disabled={!canResend || resendBusy}
                          onClick={() => { setResendMsg(null); setConfirmResend(true); }}
                          title={canResend ? "" : "Available only for verified paid applications"}
                          style={{ padding: "7px 14px", fontSize: 12, fontWeight: 600, borderRadius: 8, fontFamily: "inherit",
                                   border: `1px solid ${T.line}`, background: canResend ? "white" : "#F4F4F5",
                                   color: canResend ? T.ink : T.muted, cursor: canResend && !resendBusy ? "pointer" : "not-allowed" }}>
                          {resendBusy ? "Sending…" : "Resend email"}
                        </button>
                        {resendMsg && (
                          <span data-testid="admin-resend-email-msg"
                            style={{ fontSize: 12.5, color: resendMsg.ok ? T.success : T.danger }}>
                            {resendMsg.text}
                          </span>
                        )}
                      </div>
                    </>
                  );
                })()}
              </Section>

              {/* Technical */}
              <Section title="Technical IDs">
                <KV k="Application UUID" v={app.id} />
                <KV k="Application ID" v={app.application_id} />
                <KV k="Membership ID" v={app.membership_id} />
                <KV k="Internal Notes" v={app.internal_notes} multiline />
                <KV k="Submitter IP" v={app.submitter_ip} />
                <KV k="User Agent" v={app.user_agent} />
                <KV k="Current Stage" v={app.current_stage} />
                <KV k="Is Draft" v={app.is_draft ? "Yes" : "No"} />
              </Section>

              {/* Timeline — every timestamp rendered in IST via the
                  central fmtDateTime helper. applied_date/applied_time
                  are pre-IST-stringified on the backend so we combine
                  them with the "•" separator and reformat the time via
                  _fmtClockToAmPm; submitted_at / updated_at /
                  onboarding_date are UTC ISO and shift to IST inside
                  fmtDateTime. */}
              <Section title="Timeline">
                <KV k="Applied" v={app.applied_date
                    ? `${fmtDate(app.applied_date)}${app.applied_time ? ` • ${_fmtClockToAmPm(app.applied_time)}` : ""}`
                    : "—"} />
                <KV k="Submitted" v={app.submitted_at ? fmtDateTime(app.submitted_at) : ""} />
                <KV k="Updated" v={app.updated_at ? fmtDateTime(app.updated_at) : ""} />
                {app.onboarding_date && <KV k="Onboarded" v={fmtDate(app.onboarding_date)} />}
                {app.membership_start_date && (
                  <EditableKV
                    k="Membership Start Date"
                    v={app.membership_start_date}
                    display={(d) => fmtDate(d)}
                    inputType="date"
                    fieldTestid="kv-membership-start-date"
                    validate={(val) => (/^\d{4}-\d{2}-\d{2}$/.test(val) ? "" : "Select a valid date.")}
                    onSave={async (val) => {
                      await api.patch(`/applications/${uuid}/membership-start-date`, { membership_start_date: val });
                      await load();
                      broadcastDataChanged({ kind: "membership_start_date_updated", app_uuid: uuid });
                      if (onChanged) onChanged();
                    }} />
                )}
              </Section>

              {/* Internal remarks */}
              <Section title="Internal Remarks">
                <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                  <input data-testid="admin-remark-input" placeholder="Add an internal remark…" value={remark} onChange={(e) => setRemark(e.target.value)}
                    style={{ flex: 1, padding: "8px 10px", border: `1px solid ${T.line}`, borderRadius: 8, fontSize: 13, fontFamily: "inherit" }} />
                  <button data-testid="admin-remark-add" onClick={addRemark} disabled={busy}
                    style={{ padding: "8px 14px", background: T.brand, color: "white", border: "none", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Add</button>
                </div>
                {(app.remarks || []).length === 0 && (<div style={{ color: T.muted, fontSize: 12 }}>No remarks yet.</div>)}
                {[...(app.remarks || [])].reverse().map((r, i) => (
                  <div key={i} style={{ padding: 10, background: "#FAFAFA", borderRadius: 8, marginBottom: 6, fontSize: 12.5 }}>
                    <div>{r.text}</div>
                    <div style={{ color: T.muted, fontSize: 11, marginTop: 4 }}>{fmtDateTime(r.timestamp)}</div>
                  </div>
                ))}
              </Section>

              {/* Activity log */}
              <Section title="Activity Log">
                {(app.activity_log || []).length === 0 && (<div style={{ color: T.muted, fontSize: 12 }}>No activity yet.</div>)}
                {[...(app.activity_log || [])].reverse().map((r, i) => (
                  <div key={i} style={{ padding: 8, borderBottom: i < (app.activity_log || []).length - 1 ? `1px solid ${T.line}` : "none", fontSize: 12.5 }}>
                    <b>{r.action}</b> {r.details && (<span style={{ color: T.slate }}>· {JSON.stringify(r.details)}</span>)}
                    <span style={{ color: T.muted, fontSize: 11, marginLeft: 6 }}>· {fmtDateTime(r.timestamp)}</span>
                  </div>
                ))}
              </Section>
            </div>
          </>
        )}
      </div>
      {/* Confirmation dialog for review-status change (dropdown flow) */}
      {pendingStatus && pendingStatus !== app.status && (
        <div onClick={(e) => e.stopPropagation()}
          data-testid="admin-review-confirm"
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
                   display: "flex", alignItems: "center", justifyContent: "center",
                   zIndex: 200 }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: "white", borderRadius: 14, padding: 22,
                     width: "min(460px, 92%)",
                     boxShadow: "0 40px 80px rgba(0,0,0,0.4)" }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>
              Change review status from{" "}
              <span style={{ color: T.brand }}>{_labelForStatus(app.status)}</span>{" "}
              to <span style={{ color: T.brand }}>{_labelForStatus(pendingStatus)}</span>?
            </div>
            <div style={{ fontSize: 12.5, color: T.slate, marginBottom: 12, lineHeight: 1.55 }}>
              This will immediately update the Members table, Dashboard counts and
              the Payments page. Existing filter and refund rules still apply.
            </div>
            <textarea data-testid="admin-review-reason"
              rows={2}
              placeholder="Optional note (stored as an internal remark and in the audit log)"
              value={pendingReason}
              onChange={(e) => setPendingReason(e.target.value)}
              style={{ width: "100%", padding: "8px 10px", fontSize: 12.5,
                       border: `1px solid ${T.line}`, borderRadius: 8,
                       fontFamily: "inherit", marginBottom: 14,
                       resize: "vertical", outline: "none" }} />
            {err && <div style={{ color: T.danger, fontSize: 12.5, marginBottom: 10 }}>{err}</div>}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button data-testid="admin-review-cancel"
                onClick={cancelReviewChange} disabled={busy}
                style={{ padding: "8px 14px", background: "white", color: T.slate,
                         border: `1px solid ${T.line}`, borderRadius: 8,
                         cursor: busy ? "not-allowed" : "pointer",
                         fontFamily: "inherit", fontSize: 12.5 }}>Cancel</button>
              <button data-testid="admin-review-confirm-btn"
                onClick={commitReviewStatus} disabled={busy}
                style={{ padding: "8px 16px", background: T.ink, color: "white",
                         border: "none", borderRadius: 8, fontWeight: 600,
                         cursor: busy ? "not-allowed" : "pointer",
                         fontFamily: "inherit", fontSize: 12.5 }}>
                {busy ? "Updating…" : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Confirmation dialog for payment-status change (Members drawer) */}
      {pendingCircle && (() => {
        const circleName = app[`${pendingCircle}_circle`] || "";
        const restoring = !!app[`${pendingCircle}_circle_unsuitable`];
        const label = pendingCircle === "primary" ? "Primary Circle" : "Secondary Circle";
        return (
          <div onClick={(e) => e.stopPropagation()}
            data-testid="admin-circle-confirm"
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
                     display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200 }}>
            <div onClick={(e) => e.stopPropagation()}
              style={{ background: "white", borderRadius: 14, padding: 22, width: "min(460px, 92%)",
                       boxShadow: "0 40px 80px rgba(0,0,0,0.4)" }}>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>
                {restoring
                  ? `Restore "${circleName}" as a suitable ${label}?`
                  : `Mark "${circleName}" as not suitable for this member?`}
              </div>
              <div style={{ fontSize: 12.5, color: T.slate, marginBottom: 14, lineHeight: 1.55 }}>
                {restoring
                  ? <>The strike-through will be removed. No other member information changes.</>
                  : <>The {label} will stay on the profile with a strike-through. Nothing is deleted and no other member information changes.</>}
              </div>
              {err && <div style={{ color: T.danger, fontSize: 12.5, marginBottom: 10 }}>{err}</div>}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                <button data-testid="admin-circle-cancel"
                  onClick={() => setPendingCircle("")} disabled={busy}
                  style={{ padding: "8px 14px", background: "white", color: T.slate, border: `1px solid ${T.line}`,
                           borderRadius: 8, cursor: busy ? "not-allowed" : "pointer", fontFamily: "inherit", fontSize: 12.5 }}>Cancel</button>
                <button data-testid="admin-circle-confirm-btn"
                  onClick={commitCircleSuitability} disabled={busy}
                  style={{ padding: "8px 16px", background: restoring ? T.ink : T.danger, color: "white", border: "none", borderRadius: 8,
                           fontWeight: 600, cursor: busy ? "not-allowed" : "pointer", fontFamily: "inherit", fontSize: 12.5 }}>
                  {busy ? "Saving…" : restoring ? "Yes, restore" : "Yes, mark as unsuitable"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
      {confirmResend && (
        <div onClick={(e) => e.stopPropagation()}
          data-testid="admin-resend-email-confirm"
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
                   display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200 }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: "white", borderRadius: 14, padding: 22, width: "min(460px, 92%)",
                     boxShadow: "0 40px 80px rgba(0,0,0,0.4)" }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>
              Are you sure you want to resend the application confirmation email to {app.email}?
            </div>
            <div style={{ fontSize: 12.5, color: T.slate, marginBottom: 14, lineHeight: 1.55 }}>
              The exact "Application Submitted" email will be sent again. Payment and review status are not affected.
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button data-testid="admin-resend-email-cancel"
                onClick={() => setConfirmResend(false)} disabled={resendBusy}
                style={{ padding: "8px 14px", background: "white", color: T.slate, border: `1px solid ${T.line}`,
                         borderRadius: 8, cursor: resendBusy ? "not-allowed" : "pointer", fontFamily: "inherit", fontSize: 12.5 }}>Cancel</button>
              <button data-testid="admin-resend-email-confirm-btn"
                onClick={resendConfirmationEmail} disabled={resendBusy}
                style={{ padding: "8px 16px", background: T.ink, color: "white", border: "none", borderRadius: 8,
                         fontWeight: 600, cursor: resendBusy ? "not-allowed" : "pointer", fontFamily: "inherit", fontSize: 12.5 }}>
                {resendBusy ? "Sending…" : "Yes, resend"}
              </button>
            </div>
          </div>
        </div>
      )}
      {pendingPayStatus && (
        <div onClick={(e) => e.stopPropagation()}
          data-testid="admin-payment-confirm"
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
                   display: "flex", alignItems: "center", justifyContent: "center",
                   zIndex: 200 }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: "white", borderRadius: 14, padding: 22,
                     width: "min(460px, 92%)",
                     boxShadow: "0 40px 80px rgba(0,0,0,0.4)" }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>
              {pendingPayStatus === "refunded"
                ? "Have you already completed this refund in Razorpay?"
                : "Are you sure you want to change the payment status back to Paid?"}
            </div>
            <div style={{ fontSize: 12.5, color: T.slate, marginBottom: 12, lineHeight: 1.55 }}>
              {pendingPayStatus === "refunded"
                ? <>This action only updates the internal payment status. It does <b>not</b> initiate a refund with Razorpay.</>
                : <>This will revert the Refunded state on this payment. The historical refund entries are preserved in the audit log.</>}
            </div>
            <textarea data-testid="admin-payment-reason"
              rows={2}
              placeholder="Optional note (e.g. refund ID or reason for reverting)"
              value={pendingPayReason}
              onChange={(e) => setPendingPayReason(e.target.value)}
              style={{ width: "100%", padding: "8px 10px", fontSize: 12.5,
                       border: `1px solid ${T.line}`, borderRadius: 8,
                       fontFamily: "inherit", marginBottom: 14,
                       resize: "vertical", outline: "none" }} />
            {err && <div style={{ color: T.danger, fontSize: 12.5, marginBottom: 10 }}>{err}</div>}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button data-testid="admin-payment-cancel"
                onClick={cancelPayChange} disabled={busy}
                style={{ padding: "8px 14px", background: "white", color: T.slate,
                         border: `1px solid ${T.line}`, borderRadius: 8,
                         cursor: busy ? "not-allowed" : "pointer",
                         fontFamily: "inherit", fontSize: 12.5 }}>Cancel</button>
              <button data-testid="admin-payment-confirm-btn"
                onClick={commitPayStatus} disabled={busy}
                style={{ padding: "8px 16px", background: T.ink, color: "white",
                         border: "none", borderRadius: 8, fontWeight: 600,
                         cursor: busy ? "not-allowed" : "pointer",
                         fontFamily: "inherit", fontSize: 12.5 }}>
                {busy ? "Updating…" : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Human-readable label for the four review statuses. Used by the
 *  confirmation dialog copy and any future audit-log render. */
function _labelForStatus(s) {
  return ({
    pending_review: "Review Pending",
    onboarded: "Onboarded",
    rejected: "Rejected",
    removed: "Removed",
  })[s] || (s || "—");
}

/** Derive the *display* payment status ("paid" / "unpaid" / "refunded")
 *  from the application row and its linked payment doc. Kept in sync
 *  with backend derivation so the Members drawer, the Payments table
 *  and the /payment-status endpoint all agree on what the current
 *  state is. */
function _displayPaymentStatusFor(app, payment) {
  if (payment) {
    const s = (payment.status || "").toLowerCase();
    if (s === "refunded" || (payment.refunds && payment.refunds.length &&
        payment.refunds[payment.refunds.length - 1].kind !== "manual_revert")) return "refunded";
    if (s === "paid" || s === "captured") return "paid";
  }
  return (app && (app.payment_status || "").toLowerCase()) || "unpaid";
}

function Section({ title, children }) {
  return (
    <section style={{ marginBottom: 22 }}>
      <h3 style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.16em", color: T.brand, fontWeight: 700, margin: "0 0 10px" }}>{title}</h3>
      <div>{children}</div>
    </section>
  );
}
/** KV row for Primary/Secondary Circle with a strike-through state and a
 *  per-row action icon (red cross to flag, undo arrow to restore). */
function CircleKV({ k, v, unsuitable, onToggle, disabled, testid, options, optionLabel, onSelect }) {
  const hasValue = !(v === undefined || v === null || v === "");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(v ?? "");
  const [saving, setSaving] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const hlV = useHlValue(v);
  useEffect(() => { if (!editing) setDraft(v ?? ""); }, [v, editing]);
  const commit = async () => {
    if (!draft || draft === (v ?? "")) { setEditing(false); return; }
    setSaving(true); setErrMsg("");
    try { await onSelect(draft); setEditing(false); }
    catch (e) { setErrMsg(String(e?.response?.data?.detail || e?.message || "Failed to save.")); }
    finally { setSaving(false); }
  };
  if (editing) {
    return (
      <div style={{ display: "grid", gridTemplateColumns: "180px 1fr",
                    padding: "8px 0", borderBottom: `1px solid ${T.line}`, alignItems: "flex-start", gap: 6 }}>
        <div style={{ fontSize: 11.5, color: T.muted, fontWeight: 500 }}>{k}</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <select autoFocus value={draft} disabled={saving} data-testid={`${testid}-select`}
              onChange={(e) => setDraft(e.target.value)}
              style={{ flex: "1 1 220px", minWidth: 0, fontFamily: "inherit", fontSize: 13, padding: "6px 10px",
                       border: `1px solid ${errMsg ? "#DC2626" : T.line}`, borderRadius: 6, background: "white", color: T.ink }}>
              {!options.includes(draft) && <option value={draft}>{draft || "—"}</option>}
              {options.map((o) => <option key={o} value={o}>{optionLabel ? optionLabel(o) : o}</option>)}
            </select>
            <button type="button" data-testid={`${testid}-save`} disabled={saving} onClick={commit}
              style={{ fontSize: 12, padding: "6px 12px", background: T.brand, color: "white", border: "none",
                       borderRadius: 6, fontFamily: "inherit", cursor: saving ? "wait" : "pointer", fontWeight: 600 }}>
              {saving ? "Saving…" : "Save"}
            </button>
            <button type="button" data-testid={`${testid}-cancel`} disabled={saving}
              onClick={() => { setEditing(false); setErrMsg(""); setDraft(v ?? ""); }}
              style={{ fontSize: 12, padding: "6px 10px", background: "white", color: T.slate,
                       border: `1px solid ${T.line}`, borderRadius: 6, fontFamily: "inherit", cursor: "pointer" }}>
              Cancel
            </button>
          </div>
          {errMsg && <div data-testid={`${testid}-error`} style={{ fontSize: 11, color: "#DC2626" }}>{errMsg}</div>}
        </div>
      </div>
    );
  }
  return (
    <div style={{ display: "grid", gridTemplateColumns: "180px 1fr",
                  padding: "8px 0", borderBottom: `1px solid ${T.line}`, alignItems: "flex-start", gap: 6 }}>
      <div style={{ fontSize: 11.5, color: T.muted, fontWeight: 500 }}>{k}</div>
      <div data-testid={testid}
           style={{ fontSize: 13, color: T.ink, wordBreak: "break-word",
                    display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        <span data-testid={`${testid}-value`} data-strike={unsuitable ? "true" : undefined}
              style={{ flex: "1 1 auto", minWidth: 0,
                       textDecoration: unsuitable ? "line-through" : "none",
                       textDecorationColor: T.danger, textDecorationThickness: 2,
                       color: unsuitable ? T.muted : T.ink }}>
          {hasValue ? hlV : <span style={{ color: T.muted }}>—</span>}
        </span>
        {hasValue && <CopyIcon value={v} />}
        {options && onSelect && (
          <EditIcon onClick={() => { setDraft(v ?? ""); setErrMsg(""); setEditing(true); }}
                    title={`Edit ${k}`} testid={`${testid}-edit-icon`} />
        )}
        {hasValue && (
          <button type="button" data-testid={`${testid}-toggle`}
            onClick={onToggle} disabled={disabled}
            title={unsuitable ? "Restore this circle as suitable" : "Mark this circle as not suitable"}
            aria-label={unsuitable ? `Restore ${k}` : `Mark ${k} as not suitable`}
            style={{ width: 22, height: 22, borderRadius: 6, border: `1px solid ${unsuitable ? T.line : "#FECACA"}`,
                     background: unsuitable ? "white" : "#FEF2F2", color: unsuitable ? T.slate : T.danger,
                     display: "inline-flex", alignItems: "center", justifyContent: "center",
                     cursor: disabled ? "not-allowed" : "pointer", padding: 0, lineHeight: 1 }}>
            {unsuitable ? (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 7v6h6" /><path d="M21 17a9 9 0 0 0-15-6.7L3 13" />
              </svg>
            ) : (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round">
                <path d="M18 6 6 18" /><path d="m6 6 12 12" />
              </svg>
            )}
          </button>
        )}
        {unsuitable && (
          <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: T.danger }}>
            Not suitable
          </span>
        )}
      </div>
    </div>
  );
}

function KV({ k, v, link, multiline }) {
  // Every field with a real value gets a per-row Copy affordance. The
  // icon lives right after the value so it stays aligned across rows.
  const hasValue = !(v === undefined || v === null || v === "");
  const shown = useHl(typeof v === "string" || typeof v === "number" ? v : "");
  const text = (typeof v === "string" || typeof v === "number") ? shown : v;
  const rendered = !hasValue ? <span style={{ color: T.muted }}>—</span>
    : link ? <a href={link} target="_blank" rel="noreferrer noopener" style={{ color: T.brand }}>{text}</a>
    : <span>{text}</span>;
  return (
    <div style={{ display: "grid", gridTemplateColumns: multiline ? "1fr" : "180px 1fr",
                  padding: "8px 0", borderBottom: `1px solid ${T.line}`, alignItems: "flex-start", gap: 6 }}>
      <div style={{ fontSize: 11.5, color: T.muted, fontWeight: 500 }}>{k}</div>
      <div data-testid={`kv-${String(k).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
           style={{ fontSize: 13, color: T.ink,
                    whiteSpace: multiline ? "pre-wrap" : "normal",
                    wordBreak: "break-word",
                    display: "inline-flex", alignItems: "center", gap: 4,
                    flexWrap: "wrap" }}>
        <span style={{ flex: "1 1 auto", minWidth: 0 }}>{rendered}</span>
        {hasValue && <CopyIcon value={v} />}
      </div>
    </div>
  );
}

/** Small pencil-glyph edit trigger. Matches the visual weight and
 *  opacity behaviour of `CopyIcon` so the two sit side-by-side in a KV
 *  row without competing for hierarchy. */
function EditIcon({ onClick, title = "Edit", testid = "kv-edit-icon" }) {
  return (
    <button onClick={onClick} title={title} data-testid={testid}
      type="button"
      style={{ marginLeft: 2, border: "none", background: "transparent",
               cursor: "pointer", opacity: 0.5, fontSize: 12, fontFamily: "inherit",
               padding: "0 2px" }}
      onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
      onMouseLeave={(e) => (e.currentTarget.style.opacity = 0.5)}>
      ✎
    </button>
  );
}

/** Editable KV row — same visual template as `KV` (identical grid,
 *  padding, typography and border) but with an inline edit flow
 *  triggered by a pencil button sitting next to the Copy icon.
 *
 *  Props:
 *    • k, v, link          — same as KV
 *    • buildLink(next)     — optional; recomputes `link` for the newly
 *                            edited value while still in view mode
 *    • validate(raw) → ""  — synchronous form-level validation that
 *                            returns an error string ("" = pass)
 *    • normalize(raw) → val — pre-save cleanup (e.g. strip non-digits)
 *    • onSave(value)       — async; must persist. On resolve, the row
 *                            exits edit mode and adopts the new value.
 *    • fieldTestid         — testid prefix for the input/save/cancel
 *                            buttons (defaults to `kv-<k-slug>`)
 */
function EditableKV({
  k, v, link, buildLink, validate, normalize, onSave,
  inputType = "text", inputMode = "text", maxLength, placeholder,
  fieldTestid, display,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(v ?? "");
  const [busy, setBusy] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const slug = String(k).toLowerCase().replace(/[^a-z0-9]+/g, "-");
  const idBase = fieldTestid || `kv-${slug}`;

  useEffect(() => {
    // Sync back if the parent re-loads the record — but only while
    // NOT in edit mode, so an in-progress edit isn't clobbered by a
    // background refetch of the same field.
    if (!editing) setDraft(v ?? "");
  }, [v, editing]);

  const hasValue = !(v === undefined || v === null || v === "");
  const displayLink = buildLink ? buildLink(v) : link;
  const shownRaw = display ? display(v) : v;
  const shown = useHlValue(shownRaw);
  const rendered = !hasValue ? <span style={{ color: T.muted }}>—</span>
    : displayLink
      ? <a href={displayLink} target="_blank" rel="noreferrer noopener" style={{ color: T.brand }}>{shown}</a>
      : <span>{shown}</span>;

  const enterEdit = () => {
    setDraft(v ?? "");
    setErrMsg("");
    setEditing(true);
  };
  const cancelEdit = () => {
    setEditing(false);
    setErrMsg("");
    setDraft(v ?? "");
  };
  const commit = async () => {
    const raw = String(draft || "");
    const cleaned = normalize ? normalize(raw) : raw.trim();
    const localErr = validate ? validate(cleaned) : "";
    if (localErr) { setErrMsg(localErr); return; }
    if (cleaned === (v ?? "")) { setEditing(false); return; }
    setBusy(true); setErrMsg("");
    try {
      await onSave(cleaned);
      setEditing(false);
    } catch (e) {
      // Surface the server-side error inline so admins understand why
      // save failed without diving into the network tab.
      const msg = e?.response?.data?.detail || e?.message || "Failed to save.";
      setErrMsg(String(msg));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "180px 1fr",
                  padding: "8px 0", borderBottom: `1px solid ${T.line}`, alignItems: "flex-start", gap: 6 }}>
      <div style={{ fontSize: 11.5, color: T.muted, fontWeight: 500 }}>{k}</div>
      {!editing ? (
        <div data-testid={`kv-${slug}`}
             style={{ fontSize: 13, color: T.ink, wordBreak: "break-word",
                      display: "inline-flex", alignItems: "center", gap: 4,
                      flexWrap: "wrap" }}>
          <span style={{ flex: "1 1 auto", minWidth: 0 }}>{rendered}</span>
          {hasValue && <CopyIcon value={v} />}
          <EditIcon onClick={enterEdit}
                    title="Edit"
                    testid={`${idBase}-edit-icon`} />
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <input
              autoFocus
              type={inputType}
              inputMode={inputMode}
              maxLength={maxLength}
              placeholder={placeholder}
              value={draft}
              disabled={busy}
              onChange={(e) => setDraft(e.target.value)}
              onFocus={(e) => { if (inputType === "date") { try { e.target.showPicker?.(); } catch (_) {} } }}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); commit(); }
                else if (e.key === "Escape") { e.preventDefault(); cancelEdit(); }
              }}
              data-testid={`${idBase}-input`}
              style={{
                flex: "1 1 180px", minWidth: 0, fontFamily: "inherit",
                fontSize: 13, padding: "6px 10px",
                border: `1px solid ${errMsg ? "#DC2626" : T.line}`,
                borderRadius: 6, outline: "none", background: "white",
                color: T.ink,
              }} />
            <button data-testid={`${idBase}-save`} disabled={busy}
              onClick={commit} type="button"
              style={{ fontSize: 12, padding: "6px 12px", background: T.brand,
                       color: "white", border: "none", borderRadius: 6,
                       fontFamily: "inherit", cursor: busy ? "wait" : "pointer",
                       fontWeight: 600 }}>
              {busy ? "Saving…" : "Save"}
            </button>
            <button data-testid={`${idBase}-cancel`} disabled={busy}
              onClick={cancelEdit} type="button"
              style={{ fontSize: 12, padding: "6px 10px", background: "white",
                       color: T.slate, border: `1px solid ${T.line}`, borderRadius: 6,
                       fontFamily: "inherit", cursor: busy ? "wait" : "pointer" }}>
              Cancel
            </button>
          </div>
          {errMsg && (
            <div data-testid={`${idBase}-error`}
                 style={{ fontSize: 11, color: "#DC2626" }}>{errMsg}</div>
          )}
        </div>
      )}
    </div>
  );
}

/* =====================================================================
 * Settings — minimal Phase 1 (session + about)
 * ===================================================================== */
/* =====================================================================
 * Settings — Admin-portal-scoped preferences and system info.
 * All preference toggles persist to localStorage under the hfa-admin-*
 * namespace so they follow the browser session, not the Atlas user.
 * ===================================================================== */
const PREF_KEYS = {
  defaultDateFilter:      "hfa-admin-pref-default-date-filter",     // Dashboard's default date preset
  defaultDateMembers:     "hfa-admin-pref-default-date-members",    // Members quick date default
  defaultDatePayments:    "hfa-admin-pref-default-date-payments",   // Payments quick date default
  defaultMembersView:     "hfa-admin-pref-default-members",         // (legacy, kept for BC)
  defaultPaymentsView:    "hfa-admin-pref-default-payments",        // (legacy, kept for BC)
  defaultPaymentMembers:  "hfa-admin-pref-default-payment-members", // Members Payment Status default: all|paid|unpaid
  defaultReviewMembers:   "hfa-admin-pref-default-review-members",  // Members Review Status default
  defaultPaymentPayments: "hfa-admin-pref-default-payment-payments",// Payments Payment Status default: all|paid|unpaid|refunded
  defaultMembershipType:  "hfa-admin-pref-default-membership-type",  // Dashboard/Members/Payments Membership Type default: all|single|multiple
  defaultPageSize:        "hfa-admin-pref-page-size",
  exportScope:            "hfa-admin-pref-export-scope",
  csvDateFormat:          "hfa-admin-pref-csv-date-format",
};
function _prefGet(key, fallback) {
  try { return localStorage.getItem(key) || fallback; } catch (_) { return fallback; }
}
function _prefSet(key, val) {
  try { localStorage.setItem(key, val); } catch (_) {}
  // Any pref change → schedule a debounced push to the server so the
  // same admin sees the same defaults on every device.
  _schedulePrefSync();
}

/* =====================================================================
 * Cross-device preference sync
 * ---------------------------------------------------------------------
 * Every admin preference (Settings defaults, column order, column
 * widths, filter defaults) is mirrored to a single Atlas document via
 * `/admin/preferences`. localStorage remains the fast read path — the
 * server is the source of truth for cross-device consistency.
 *
 * Contract:
 *   • On first login the server MAY be empty. We PUT the local set up
 *     as the migration seed so the current device becomes the master.
 *   • Any pref write (`_prefSet` OR the column-config helpers) fires
 *     `_schedulePrefSync` — a 800 ms trailing-debounce that batches
 *     rapid successive writes into a single PUT.
 *   • On new-device login we GET first, then hydrate every returned
 *     key into localStorage BEFORE the app renders anywhere.
 *
 * The full localStorage key surface we care about — everything starts
 * with `hfa-admin-pref-` OR `hfa-admin-tbl-` (column layout state).
 * =================================================================== */
const ADMIN_PREF_PREFIXES = ["hfa-admin-pref-", "hfa-admin-tbl-"];
let _prefSyncApi = null;
let _prefSyncTimer = null;
let _prefSyncSuppressed = false;

function _isAdminPrefKey(k) {
  if (!k) return false;
  for (const p of ADMIN_PREF_PREFIXES) if (k.startsWith(p)) return true;
  return false;
}

function _collectLocalPrefs() {
  const out = {};
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!_isAdminPrefKey(k)) continue;
      const v = localStorage.getItem(k);
      if (v !== null) out[k] = v;
    }
  } catch (_) {}
  return out;
}

function _schedulePrefSync() {
  if (_prefSyncSuppressed || !_prefSyncApi) return;
  if (_prefSyncTimer) clearTimeout(_prefSyncTimer);
  _prefSyncTimer = setTimeout(() => {
    const prefs = _collectLocalPrefs();
    _prefSyncApi.put("/preferences", { prefs })
      .catch(() => { /* silent — retried on next change */ });
  }, 800);
}

/** Called from the app root once the api client is available. Wires
 *  the module-level uploader and, on first call, either seeds the
 *  server from the local device (empty server) or hydrates local from
 *  the server (existing account). */
async function _bootstrapPrefsFromServer(api) {
  _prefSyncApi = api;
  // Monkey-patch localStorage so EVERY admin-pref write (including the
  // ones done directly by column-config helpers with
  // `localStorage.setItem(PREF_COL_WIDTHS, ...)`) triggers a debounced
  // upload. Doing it here (post-login) keeps the patch scoped to the
  // authenticated portion of the app and avoids fighting other apps
  // that may share the origin during dev.
  try {
    const origSet = Storage.prototype.setItem;
    if (!Storage.prototype.__hfaAdminPatched) {
      Storage.prototype.setItem = function(k, v) {
        const r = origSet.apply(this, arguments);
        if (this === window.localStorage && _isAdminPrefKey(k)) _schedulePrefSync();
        return r;
      };
      const origRem = Storage.prototype.removeItem;
      Storage.prototype.removeItem = function(k) {
        const r = origRem.apply(this, arguments);
        if (this === window.localStorage && _isAdminPrefKey(k)) _schedulePrefSync();
        return r;
      };
      Storage.prototype.__hfaAdminPatched = true;
    }
  } catch (_) {}
  try {
    const res = await api.get("/preferences");
    const remote = (res && res.data && res.data.prefs) || {};
    const remoteKeys = Object.keys(remote);
    if (remoteKeys.length === 0) {
      // Server empty → this device becomes the seed (one-shot migration).
      const local = _collectLocalPrefs();
      if (Object.keys(local).length > 0) {
        await api.put("/preferences", { prefs: local }).catch(() => {});
      }
      return;
    }
    // Server has authoritative values → hydrate them into localStorage.
    //
    // IMPORTANT: never DELETE a local admin-pref key just because the
    // server doesn't have it. A key can legitimately be missing from
    // the server temporarily — e.g. the debounced PUT for a very
    // recent change (drag-reorder / resize / hide-column) hasn't
    // fired yet. Deleting the local key in that window would silently
    // revert a saved layout back to factory defaults, which is
    // exactly the Dashboard→Members drill-down bug reported by the
    // admin. Server overrides local for keys it DOES know about; the
    // debounced sync will push anything the server is missing on the
    // next write.
    _prefSyncSuppressed = true;
    try {
      remoteKeys.forEach((k) => localStorage.setItem(k, remote[k]));
    } catch (_) {}
    finally { _prefSyncSuppressed = false; }
    try { window.dispatchEvent(new Event(PREFS_HYDRATED_EVENT)); } catch (_) {}
    // Re-run the debounced sync so any local key the server was
    // missing (see rationale above) gets pushed up immediately.
    _schedulePrefSync();
  } catch (_) { /* silent — offline start still works from local cache */ }
}

function SettingRow({ label, help, children }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                  gap: 16, padding: "14px 0", borderBottom: `1px solid ${T.lineSoft}` }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: T.ink }}>{label}</div>
        {help && <div style={{ fontSize: 11.5, color: T.muted, marginTop: 3 }}>{help}</div>}
      </div>
      <div style={{ flexShrink: 0 }}>{children}</div>
    </div>
  );
}
function SettingsCard({ title, children }) {
  return (
    <div style={{ background: T.card, border: `1px solid ${T.line}`, borderRadius: 16,
                  padding: "22px 26px", marginBottom: 18,
                  boxShadow: "0 1px 2px rgba(0,0,0,0.02)" }}>
      <div style={{ fontSize: 11, color: T.slate, textTransform: "uppercase",
                    letterSpacing: "0.14em", fontWeight: 700, marginBottom: 6 }}>{title}</div>
      {children}
    </div>
  );
}
function PrefSelect({ testid, value, onChange, options }) {
  return (
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
      style={{ padding: "7px 12px", fontSize: 12.5, border: `1px solid ${T.line}`,
               borderRadius: 8, background: "white", fontFamily: "inherit", color: T.ink,
               minWidth: 170, cursor: "pointer" }}>
      {options.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
    </select>
  );
}
function PrefBtn({ testid, tone = "muted", onClick, children }) {
  const isDanger = tone === "danger";
  return (
    <button data-testid={testid} onClick={onClick}
      style={{ padding: "7px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer",
               border: `1px solid ${isDanger ? T.danger : T.line}`,
               background: "white", color: isDanger ? T.danger : T.slate,
               borderRadius: 8, fontFamily: "inherit", transition: "all 0.15s ease" }}>
      {children}
    </button>
  );
}


/** Archive view — full-screen modal accessed via Settings → Archive.
 *  Fetches `is_archived=true` rows via the existing exceptions endpoint
 *  and offers per-row Restore. Never appears in the sidebar. */
function ArchiveView({ api, onClose }) {
  const [rows, setRows] = useState([]); const [q, setQ] = useState(""); const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  // Live count of rows currently VISIBLE in the table body — reported
  // by TableView after every column filter / dropdown-multi-select
  // update. This is what the header shows so the "N Archived" count
  // can never drift away from the actual rows on screen (spec §5).
  const [visibleCount, setVisibleCount] = useState(0);
  useEffect(() => { setVisibleCount(rows.length); }, [rows.length]);
  const load = useCallback(async () => {
    setErr("");
    try {
      const r = await api.get("/exceptions/archived", { params: { q: q || undefined, limit: 2000 } });
      setRows(r.data.rows || []);
    } catch (e) { setErr(e?.response?.data?.detail || "Failed to load."); }
  }, [q, api]);
  useEffect(() => { load(); }, [load]);
  // Global "data changed" broadcast — refetch immediately whenever any
  // other surface (Member Drawer, bulk toolbar, refund flow) mutates
  // an application or payment. Keeps Members table + filters in sync
  // without any manual reload.
  useEffect(() => {
    const onChange = () => { load(); };
    window.addEventListener(DATA_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(DATA_CHANGED_EVENT, onChange);
  }, [load]);
  const restore = async (id) => {
    if (!window.confirm("Restore this archived record back to the Members view?")) return;
    setBusy(true);
    try { await api.post(`/applications/${id}/restore`); await load(); broadcastDataChanged({ kind: "restored", app_uuid: id }); }
    catch (e) { alert(e?.response?.data?.detail || "Failed to restore."); }
    setBusy(false);
  };
  const bulkRestore = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!window.confirm(`Restore ${ids.length} archived record${ids.length === 1 ? "" : "s"} back to Members?`)) return;
    setBusy(true);
    let ok = 0, fail = 0;
    for (const id of ids) {
      try { await api.post(`/applications/${id}/restore`); ok++; }
      catch (_) { fail++; }
    }
    setBusy(false);
    setSelected(new Set());
    if (ok > 0) broadcastDataChanged({ kind: "bulk_restored", count: ok });
    await load();
    alert(`Restore complete: ${ok} succeeded, ${fail} failed.`);
  };
  const exportCsv = () => {
    const csv = buildCsv(rows, MEMBER_COLS);
    downloadCsv(`hfa-archive-${new Date().toISOString().slice(0, 19).replace(/[:.]/g, "-")}.csv`, csv);
  };
  const restoreCol = {
    key: "_restore", label: "Restore", w: 100,
    render: (r) => (
      <button data-testid={`archive-restore-${r.id}`}
        onClick={(e) => { e.stopPropagation(); restore(r.id); }}
        disabled={busy}
        style={{ padding: "4px 10px", fontSize: 11, fontWeight: 600, cursor: busy ? "default" : "pointer",
                 background: "white", color: T.brand, border: `1px solid ${T.brand}`,
                 borderRadius: 6, fontFamily: "inherit" }}>Restore</button>
    ),
  };
  return (
    <div data-testid="archive-view"
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 88,
               display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 24 }}
      onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: T.bg, borderRadius: 18, width: "96%", maxWidth: 1400, height: "88vh",
                 overflow: "auto", padding: "22px 26px",
                 boxShadow: "0 32px 64px rgba(0,0,0,0.3)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14, gap: 12 }}>
          <div>
            <h2 style={{ fontSize: 26, margin: 0, letterSpacing: "-0.02em", fontWeight: 700 }}>Archive</h2>
            <div style={{ color: T.muted, fontSize: 12.5, marginTop: 2, maxWidth: 640 }}>
              Archived records live safely here. Restore returns them to Members with their original status intact. Nothing is ever permanently deleted.
            </div>
          </div>
          <button onClick={onClose} data-testid="archive-close"
            style={{ background: "white", border: `1px solid ${T.line}`,
                     padding: "6px 14px", borderRadius: 8, cursor: "pointer",
                     fontFamily: "inherit", fontSize: 12 }}>Close</button>
        </div>
        <ListPageHeader title="Archived" count={visibleCount} totalCount={rows.length} q={q} setQ={setQ}
          onExport={exportCsv} onRefresh={load} />
        {selected.size > 0 && (
          <div data-testid="archive-bulk-toolbar"
            style={{ position: "sticky", top: 8, zIndex: 5, marginBottom: 12,
                     padding: "12px 18px", background: T.ink, color: "white",
                     borderRadius: 12, boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
                     display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>{selected.size} selected</span>
            <div style={{ display: "flex", gap: 6, marginLeft: "auto" }}>
              <button data-testid="archive-bulk-restore" disabled={busy} onClick={bulkRestore}
                style={{ padding: "6px 12px", fontSize: 12, background: T.brand, color: "white",
                         border: "none", borderRadius: 6, cursor: busy ? "default" : "pointer",
                         fontFamily: "inherit", fontWeight: 600 }}>Restore selected</button>
              <button data-testid="archive-bulk-cancel" onClick={() => setSelected(new Set())}
                style={{ padding: "6px 12px", fontSize: 12, background: "transparent",
                         color: "rgba(255,255,255,0.75)",
                         border: "1px solid rgba(255,255,255,0.2)", borderRadius: 6,
                         cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
            </div>
          </div>
        )}
        {err && <div style={{ color: T.danger, fontSize: 13, marginBottom: 10 }}>{err}</div>}
        <TableView rows={rows} cols={[restoreCol, ...MEMBER_COLS]} testid="admin-archive-table"
          selectable={true} selectedIds={selected} onSelectionChange={setSelected}
          onFilteredCountChange={setVisibleCount} />
      </div>
    </div>
  );
}


function Settings({ api }) {
  const API_URL = process.env.REACT_APP_BACKEND_URL || "";
  const [showArchive, setShowArchive] = useState(false);
  // Preferences (localStorage-backed).
  const [defDate,  setDefDate]  = useState(_prefGet(PREF_KEYS.defaultDateFilter,    "this_month"));
  const [defDateM, setDefDateM] = useState(_prefGet(PREF_KEYS.defaultDateMembers,   "this_month"));
  const [defDateP, setDefDateP] = useState(_prefGet(PREF_KEYS.defaultDatePayments,  "this_month"));
  const [defPMem,  setDefPMem]  = useState(() => { const v = _prefGet(PREF_KEYS.defaultPaymentMembers,  "all"); return v === "unpaid" ? "all" : v; });
  const [defRMem,  setDefRMem]  = useState(_prefGet(PREF_KEYS.defaultReviewMembers,   "all"));
  const [defPPay,  setDefPPay]  = useState(() => { const v = _prefGet(PREF_KEYS.defaultPaymentPayments, "all"); return v === "unpaid" ? "all" : v; });
  const [defMType, setDefMType] = useState(_readMtPref());
  const [defSize,  setDefSize]  = useState(_prefGet(PREF_KEYS.defaultPageSize,     "50"));
  const [expScope, setExpScope] = useState(_prefGet(PREF_KEYS.exportScope,         "filtered"));
  const [dateFmt,  setDateFmt]  = useState(_prefGet(PREF_KEYS.csvDateFormat,       "iso"));
  useEffect(() => _prefSet(PREF_KEYS.defaultDateFilter,      defDate),  [defDate]);
  useEffect(() => _prefSet(PREF_KEYS.defaultDateMembers,     defDateM), [defDateM]);
  useEffect(() => _prefSet(PREF_KEYS.defaultDatePayments,    defDateP), [defDateP]);
  useEffect(() => _prefSet(PREF_KEYS.defaultPaymentMembers,  defPMem),  [defPMem]);
  useEffect(() => _prefSet(PREF_KEYS.defaultReviewMembers,   defRMem),  [defRMem]);
  useEffect(() => _prefSet(PREF_KEYS.defaultPaymentPayments, defPPay),  [defPPay]);
  useEffect(() => _prefSet(PREF_KEYS.defaultMembershipType,  defMType), [defMType]);
  useEffect(() => _prefSet(PREF_KEYS.defaultPageSize,        defSize),  [defSize]);
  useEffect(() => _prefSet(PREF_KEYS.exportScope,            expScope), [expScope]);
  useEffect(() => _prefSet(PREF_KEYS.csvDateFormat,          dateFmt),  [dateFmt]);

  // System info — pull backend health status.
  const [ping, setPing] = useState({ backend: "checking…", db: "checking…" });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await api.get("/auth/me");
        if (!cancelled) setPing((p) => ({ ...p, backend: "healthy", db: "connected (Atlas)" }));
      } catch (_) {
        if (!cancelled) setPing({ backend: "unreachable", db: "unknown" });
      }
    })();
    return () => { cancelled = true; };
  }, [api]);

  const resetAllFilters = () => {
    ["dash-range-preset"].forEach((k) => { try { localStorage.removeItem(k); } catch (_) {} });
    Object.values(PREF_KEYS).forEach((k) => { try { localStorage.removeItem(k); } catch (_) {} });
    window.location.reload();
  };
  /** Reset the physical column layout (order, widths, hidden cols and
   *  freeze) across every table without touching filters, search or any
   *  business preference. Excel-parity behaviour. */
  const resetColumnLayout = () => {
    if (!window.confirm("Restore default column order, widths, visibility and freeze across every table? Filters and search are not affected.")) return;
    _resetTableLayout("members");
    _resetTableLayout("payments");
    try { localStorage.removeItem(PREF_FROZEN_COUNT); } catch (_) {}
    window.location.reload();
  };
  const logoutAll = async () => {
    if (!window.confirm("Log out of every admin device? This will invalidate every active session token.")) return;
    try { await api.post("/auth/logout"); } catch (_) {}
    clearToken();
    window.location.reload();
  };

  // ---- Google Sheets sync health ------------------------------------
  //  Set to `false` when re-enabling the Settings card + polling. The
  //  companion JSX for the "Sheets Sync" card is commented-out below.
  const SHEETS_UI_HIDDEN = true;

  // Polled every 30 s so the pill stays fresh while the drain worker
  // pushes rows. Silent failures surface as "Errors" instead of the
  // healthy green state.
  const [sheets, setSheets] = useState(null);
  useEffect(() => {
    if (SHEETS_UI_HIDDEN) return; // Skip network chatter while the card is hidden.
    let alive = true;
    const load = () => {
      api.get("/sheets_health").then((r) => { if (alive) setSheets(r.data); }).catch(() => {});
    };
    load();
    const timer = setInterval(load, 30_000);
    return () => { alive = false; clearInterval(timer); };
  }, [api, SHEETS_UI_HIDDEN]);
  const sheetsPill = (() => {
    if (!sheets) return { text: "Sheets sync · checking…", tone: "neutral" };
    if (!sheets.sync_enabled) return { text: "Sheets sync · disabled", tone: "neutral" };
    // Recent error with unresolved pending items → degraded.
    if (sheets.pending > 0 && sheets.last_error) {
      const attempts = sheets.last_error_attempts || 0;
      return {
        text: `Sheets sync · errors (${sheets.pending} pending · ${attempts} attempts · ${sheets.last_error})`,
        tone: "danger",
        subline: sheets.last_error_at ? `First error at ${fmtDateTime(sheets.last_error_at)}` : null,
      };
    }
    if (sheets.pending > 0) {
      return { text: `Sheets sync · pushing (${sheets.pending} pending)`, tone: "warn" };
    }
    // Healthy — surface "X min ago" from last_synced_at.
    if (sheets.last_synced_at) {
      const ago = (() => {
        try {
          const then = new Date(sheets.last_synced_at).getTime();
          const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
          if (mins < 1)   return "just now";
          if (mins === 1) return "1 min ago";
          if (mins < 60)  return `${mins} min ago`;
          const hrs = Math.round(mins / 60);
          if (hrs === 1)  return "1 hr ago";
          if (hrs < 48)   return `${hrs} hrs ago`;
          return `${Math.round(hrs / 24)} days ago`;
        } catch (_) { return "recently"; }
      })();
      return {
        text: `Sheets sync · healthy · last pushed ${ago}`,
        tone: "ok",
        subline: `${sheets.synced} rows synced total`,
      };
    }
    return { text: "Sheets sync · healthy · no rows pushed yet", tone: "ok" };
  })();
  const sheetsPillStyle = {
    ok:      { bg: "#ECFDF5", fg: "#047857", border: "#A7F3D0" },
    warn:    { bg: "#FEF3C7", fg: "#B45309", border: "#FCD34D" },
    danger:  { bg: "#FEF2F2", fg: "#B91C1C", border: "#FECACA" },
    neutral: { bg: "#F3F4F6", fg: "#4B5563", border: T.line },
  }[sheetsPill.tone];

  return (
    <div style={{ maxWidth: 780 }}>
      <h1 style={{ fontSize: 34, margin: 0, letterSpacing: "-0.035em",
                   fontWeight: 800, color: T.ink }}>Settings</h1>
      <div style={{ color: T.muted, fontSize: 13, marginTop: 6, marginBottom: 22 }}>
        Preferences that apply only to the Admin Portal.
      </div>

      <SettingsCard title="Authentication">
        <SettingRow label="Change Admin Password"
          help="Password is stored as a bcrypt hash in the ADMIN_PASSWORD_BCRYPT environment variable. Ask your platform admin to update it — every active session expires at next login.">
          <span style={{ fontSize: 11, color: T.muted, fontStyle: "italic" }}>Managed via env</span>
        </SettingRow>
        <SettingRow label="Session Timeout"
          help="Sessions expire after ADMIN_SESSION_TTL_HOURS from login. Change this env variable to adjust.">
          <span style={{ fontSize: 12, color: T.slate, fontWeight: 600 }}>Managed via env</span>
        </SettingRow>
        <SettingRow label="Log Out of All Devices"
          help="Ends every admin session immediately, including this one.">
          <PrefBtn testid="settings-logout-all" tone="danger" onClick={logoutAll}>Log out all</PrefBtn>
        </SettingRow>
      </SettingsCard>

      {/* Sheets Sync card hidden — Aug 2026. MongoDB Atlas is now the
       *  single source of truth; sync disabled via SHEETS_SYNC_ENABLED
       *  env flag. Card kept in code (below) for easy re-enable.
      <SettingsCard title="Sheets Sync">
        <SettingRow label="Live Status"
          help="Google Sheets sync pushes every new onboarded member into the shared ops sheet. This card refreshes every 30 seconds.">
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
            <span data-testid="settings-sheets-pill"
              style={{ display: "inline-flex", alignItems: "center", gap: 6,
                       padding: "6px 12px", borderRadius: 999, fontSize: 11.5, fontWeight: 700,
                       background: sheetsPillStyle.bg, color: sheetsPillStyle.fg,
                       border: `1px solid ${sheetsPillStyle.border}`,
                       fontFamily: "inherit" }}>
              <span style={{ width: 7, height: 7, borderRadius: "50%",
                             background: sheetsPillStyle.fg,
                             boxShadow: sheetsPill.tone === "ok" ? `0 0 0 3px ${sheetsPillStyle.border}` : undefined }} />
              {sheetsPill.text}
            </span>
            {sheetsPill.subline && (
              <span style={{ fontSize: 11, color: T.muted }}>{sheetsPill.subline}</span>
            )}
          </div>
        </SettingRow>
      </SettingsCard>
      */}

      <SettingsCard title="Default Quick Date Filters">
        <SettingRow label="Dashboard Default"
          help="Applied when you first open the Dashboard. Uses Payment Date.">
          <PrefSelect testid="settings-default-date" value={defDate} onChange={setDefDate}
            options={QUICK_RANGES.filter((r) => r.key !== "custom").map((r) => ({ v: r.key, label: r.label }))} />
        </SettingRow>
        <SettingRow label="Members Default"
          help="Applied when you first open Members. Uses Payment Date — rows without a payment do not match.">
          <PrefSelect testid="settings-default-date-members" value={defDateM} onChange={setDefDateM}
            options={QUICK_RANGES.filter((r) => r.key !== "custom").map((r) => ({ v: r.key, label: r.label }))} />
        </SettingRow>
        <SettingRow label="Payments Default"
          help="Applied when you first open Payments. Uses Payment Date.">
          <PrefSelect testid="settings-default-date-payments" value={defDateP} onChange={setDefDateP}
            options={QUICK_RANGES.filter((r) => r.key !== "custom").map((r) => ({ v: r.key, label: r.label }))} />
        </SettingRow>
      </SettingsCard>

      <SettingsCard title="Default Filter Views">
        <SettingRow label="Members — Default Payment Status"
          help="Applied every time the Members page opens. Refunds are Payments-page-only.">
          <PrefSelect testid="settings-default-payment-members" value={defPMem} onChange={setDefPMem}
            options={[
              { v: "all",    label: "All" },
              { v: "paid",   label: "Paid" },
            ]} />
        </SettingRow>
        <SettingRow label="Members — Default Review Status"
          help="Applied every time the Members page opens. Any specific choice implies Payment = Paid.">
          <PrefSelect testid="settings-default-review-members" value={defRMem} onChange={setDefRMem}
            options={[
              { v: "all",            label: "All" },
              { v: "pending_review", label: "Review Pending" },
              { v: "onboarded",      label: "Onboarded" },
              { v: "rejected",       label: "Rejected" },
              { v: "removed",        label: "Removed" },
            ]} />
        </SettingRow>
        <SettingRow label="Payments — Default Payment Status"
          help="Applied every time the Payments page opens.">
          <PrefSelect testid="settings-default-payment-payments" value={defPPay} onChange={setDefPPay}
            options={[
              { v: "all",      label: "All" },
              { v: "paid",     label: "Paid" },
              { v: "refunded", label: "Refunded" },
            ]} />
        </SettingRow>
        <SettingRow label="City Chapters — Default"
          help="Applied to Dashboard, Members and Payments on open. Single = member has one paid membership record; Multiple = more than one (by member identity). Unpaid applications never count.">
          <PrefSelect testid="settings-default-membership-type" value={defMType} onChange={setDefMType}
            options={[
              { v: "all",      label: "All" },
              { v: "single",   label: "Single" },
              { v: "multiple", label: "Multiple" },
            ]} />
        </SettingRow>
      </SettingsCard>

      <SettingsCard title="Table Preferences">
        <SettingRow label="Rows Per View"
          help="Applies across every table in the Admin Portal. Choose “All” for an Excel-like continuous view.">
          <PrefSelect testid="settings-page-size" value={defSize} onChange={setDefSize}
            options={[{v:"25",label:"25 rows"},{v:"50",label:"50 rows"},{v:"100",label:"100 rows"},{v:"All",label:"All rows"}]} />
        </SettingRow>
        <SettingRow label="Reset All Filters"
          help="Clears every text-search and dropdown filter across every table.">
          <PrefBtn testid="settings-reset-filters" onClick={resetAllFilters}>Reset filters</PrefBtn>
        </SettingRow>
        <SettingRow label="Reset Column Layout"
          help="Restores default column order, widths, visibility and frozen columns across every table. Filters and searches are NOT affected.">
          <PrefBtn testid="settings-reset-columns" onClick={resetColumnLayout}>Reset columns</PrefBtn>
        </SettingRow>
        <SettingRow label="Reset Admin View"
          help="Wipes every admin-portal preference and reloads with fresh defaults.">
          <PrefBtn testid="settings-reset-view" tone="danger" onClick={resetAllFilters}>Reset all</PrefBtn>
        </SettingRow>
      </SettingsCard>

      <SettingsCard title="Export Preferences">
        <SettingRow label="Default Export Scope"
          help="Whether the CSV export includes only currently filtered rows or the full dataset.">
          <PrefSelect testid="settings-export-scope" value={expScope} onChange={setExpScope}
            options={[{v:"filtered",label:"Filtered rows only"},{v:"all",label:"All rows"}]} />
        </SettingRow>
        <SettingRow label="CSV Date Format"
          help="How date columns are serialised in exported CSVs.">
          <PrefSelect testid="settings-csv-date" value={dateFmt} onChange={setDateFmt}
            options={[
              { v: "iso",   label: "ISO — 2026-08-05" },
              { v: "dmy",   label: "DD/MM/YYYY" },
              { v: "mdy",   label: "MM/DD/YYYY" },
            ]} />
        </SettingRow>
      </SettingsCard>

      <SettingsCard title="Archive">
        <SettingRow label="Archived Records"
          help="Nothing is ever permanently deleted from the Admin Portal. Archived rows land here — search, filter, export, and restore back to their original location.">
          <PrefBtn testid="settings-open-archive" onClick={() => setShowArchive(true)}>Open Archive</PrefBtn>
        </SettingRow>
      </SettingsCard>

      <SettingsCard title="Developer Tools">
        <SettingRow label="Generate Sample Data"
          help="Inserts ~30 realistic demo applications tagged as _sample. Use this to test layout, filters, scrolling and the Row Detail Drawer without touching production data.">
          <PrefBtn testid="settings-gen-sample"
            onClick={async () => {
              if (!window.confirm("Generate 30 sample applications? These will be tagged _sample:true and can be cleared with one click.")) return;
              try {
                const r = await api.post("/sample-data/generate", { count: 30 });
                broadcastDataChanged({ kind: "sample_generated", inserted: r.data.inserted });
                alert(`Inserted ${r.data.inserted} sample applications and ${r.data.payments_inserted} sample payments.`);
              } catch (e) { alert("Failed: " + (e?.response?.data?.detail || e.message)); }
            }}>Generate 30 rows</PrefBtn>
        </SettingRow>
        <SettingRow label="Clear Sample Data"
          help="Deletes ONLY records tagged _sample:true. Real production applications are never touched.">
          <PrefBtn testid="settings-clear-sample" tone="danger"
            onClick={async () => {
              if (!window.confirm("Delete every _sample-tagged application and payment? Real production data is safe.")) return;
              try {
                const r = await api.post("/sample-data/clear");
                broadcastDataChanged({ kind: "sample_cleared", deleted: r.data.deleted_applications });
                alert(`Deleted ${r.data.deleted_applications} sample applications and ${r.data.deleted_payments} sample payments.`);
              } catch (e) { alert("Failed: " + (e?.response?.data?.detail || e.message)); }
            }}>Clear samples</PrefBtn>
        </SettingRow>
      </SettingsCard>

      <SettingsCard title="System Information">
        <SettingRow label="Connected Database">
          <span data-testid="sys-db" style={{ fontSize: 12, color: T.slate, fontWeight: 600 }}>{ping.db}</span>
        </SettingRow>
        <SettingRow label="Backend Status">
          <span data-testid="sys-backend" style={{ fontSize: 12,
                color: ping.backend === "healthy" ? T.success : ping.backend === "unreachable" ? T.danger : T.muted,
                fontWeight: 600 }}>{ping.backend}</span>
        </SettingRow>
        <SettingRow label="Razorpay Status"
          help="Payment gateway readiness is verified per checkout inside the Apply page.">
          <span data-testid="sys-razorpay" style={{ fontSize: 12, color: T.slate, fontWeight: 600 }}>configured</span>
        </SettingRow>
        <SettingRow label="Current Version">
          <span data-testid="sys-version" style={{ fontSize: 12, color: T.slate, fontWeight: 600 }}>Admin v1.2 · Phase 1</span>
        </SettingRow>
        <SettingRow label="Last Deployment"
          help="Preview environment auto-deploys on every commit. Production is redeployed manually from the Emergent console.">
          <span data-testid="sys-deploy" style={{ fontSize: 12, color: T.slate, fontWeight: 600 }}>
            {API_URL.includes("preview") ? "preview" : "production"}
          </span>
        </SettingRow>
      </SettingsCard>
      {showArchive && <ArchiveView api={api} onClose={() => setShowArchive(false)} />}
    </div>
  );
}

/* =====================================================================
 * Main
 * ===================================================================== */
/** Read the current admin page from the URL hash. Supported values are
 *  "dashboard" (default), "members", "payments", "settings". Anything
 *  else falls back to the default so a stale/broken link never leaves
 *  the admin in an unmounted state. */
const VALID_PAGES = ["dashboard", "members", "payments", "settings"];
function _readPageFromHash() {
  try {
    const h = String(window.location.hash || "").replace(/^#\/?/, "").split("?")[0];
    return VALID_PAGES.includes(h) ? h : "dashboard";
  } catch (_) { return "dashboard"; }
}
function _writePageToHash(page) {
  try {
    if (window.location.hash.replace(/^#\/?/, "") !== page) {
      // Use pushState so the browser back-button still navigates back
      // through prior admin pages, but replaceState-style semantics
      // for the initial load (no double history entry).
      const url = `#${page}`;
      history.pushState({ hfaAdminPage: page }, "", url);
    }
  } catch (_) {}
}

export default function Admin() {
  const [authed, setAuthed] = useState(!!getToken());
  // Boot the active page from the URL hash so browser refresh, deep
  // links and back-button navigation all keep the user on the same
  // screen. The default "dashboard" only applies when the hash is
  // empty or malformed.
  const [active, setActive] = useState(() => _readPageFromHash());
  const [detailId, setDetailId] = useState(null);
  const [detailHl, setDetailHl] = useState("");
  const detailHlRe = useMemo(() => _hlTokens(detailHl), [detailHl]);
  const [refreshTick, setRefreshTick] = useState(0);

  const onAuthFail = useCallback(() => setAuthed(false), []);
  const api = useApi(onAuthFail);
  _sharedApi = api;

  // Persist the active page to the URL hash every time it changes.
  useEffect(() => { _writePageToHash(active); }, [active]);
  // React to browser back/forward navigation.
  useEffect(() => {
    const h = () => setActive(_readPageFromHash());
    window.addEventListener("popstate", h);
    window.addEventListener("hashchange", h);
    return () => {
      window.removeEventListener("popstate", h);
      window.removeEventListener("hashchange", h);
    };
  }, []);

  // Boot: validate the cached token against /auth/me, then hydrate
  // admin preferences from the server so this device inherits the
  // account's authoritative defaults (or seeds them if the account is
  // new). Awaited sequentially — /auth/me must succeed before we
  // trust the /preferences call, and we only PUT during migration
  // when the server side is confirmed empty.
  useEffect(() => {
    if (!authed) return;
    (async () => {
      try { await api.get("/auth/me"); } catch (_) { return; }
      try { await _bootstrapPrefsFromServer(api); } catch (_) {}
    })();
  }, [authed, api]);

  // Safety-net polling — event-driven sync is now the primary mechanism
  // (every mutation broadcasts DATA_CHANGED_EVENT same-tab AND across
  // tabs via BroadcastChannel/storage). This 5-minute interval only
  // exists to recover from missed events (dropped websocket-style
  // signals, brief offline windows, or edits that happened in another
  // browser without BroadcastChannel support). Admins should almost
  // never notice this fires.
  useEffect(() => {
    if (!authed) return;
    const id = setInterval(() => broadcastDataChanged({ kind: "safety_poll" }), 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [authed]);

  if (!authed) return <LoginGate onLoggedIn={() => setAuthed(true)} />;

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_) {}
    clearToken();
    setAuthed(false);
  };

  const openRow = (r, hl = "") => { setDetailHl(hl || ""); setDetailId(r.application_id || r.id); };
  const closeRow = () => { setDetailId(null); setDetailHl(""); };
  // NOTE: The drawer's onChanged used to call bump() → increment
  // refreshTick → remount Dashboard/Members/Payments via the `key`
  // prop. Every remount wiped the currently-active filters
  // (dateRange, pFilter, rFilter, colOrder, hiddenCols, dateBasis…)
  // back to the configured defaults — the exact "changing a status
  // resets my filters" bug reported by the admin. Data refresh is
  // already handled by broadcastDataChanged → DATA_CHANGED_EVENT →
  // each page's own load() listener, so the remount was redundant.
  // Keeping `refreshTick` on the shell for backward compat but no
  // longer bumping it from the drawer.

  return (
    <>
      <Shell active={active} setActive={(k) => { setActive(k); closeRow(); }} onLogout={logout}>
        {active === "dashboard" && <Dashboard api={api} key={`d-${refreshTick}`} />}
        {active === "members"    && <Members    api={api} key={`m-${refreshTick}`} onOpenRow={openRow} />}
        {active === "payments"   && <Payments   api={api} key={`p-${refreshTick}`} />}
        {active === "settings"   && <Settings api={api} />}
      </Shell>
      {detailId && (
        <HighlightCtx.Provider value={detailHlRe}>
        <DetailDrawer appId={detailId} api={api} onClose={closeRow}
          onChanged={() => { /* Data refresh flows through
            broadcastDataChanged → DATA_CHANGED_EVENT already fired
            inside commit*Status, which triggers each mounted page's
            own load() without remounting the component tree. */ }} />
        </HighlightCtx.Provider>
      )}
    </>
  );
}
